/**
 * Project store — dual-mode.
 *
 * Mode is determined at runtime:
 *   - 'api'           — user is authenticated + Supabase configured. All
 *                       reads/writes go to the database. Local state mirrors
 *                       the API for fast renders. Mutations are optimistic.
 *   - 'localStorage'  — user is not authenticated (or Supabase isn't
 *                       configured). Behaves exactly as Phase 10: seed data
 *                       loaded into Zustand, persisted via safeStorage.
 *
 * Components consume the store via the same hooks they always have. They
 * gain `useStoreStatus()` for loading/error UI when appropriate, but
 * existing CRUD calls don't change shape.
 *
 * IMPORTANT: this store does NOT subscribe to auth itself. The `bootstrap()`
 * action is invoked by the AuthProvider whenever auth state changes, telling
 * the store which mode to enter and (for 'api') triggering a data load.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import type {
  Project,
  Task,
  Risk,
  Issue,
  ChangeRequest,
  Decision,
  ActionItem,
  Baseline,
  Portfolio,
  Program,
  BudgetLine,
  BudgetVersion,
  BudgetLinePeriod,
  WorkspacePhase,
  ProjectSizeTier,
  TimeEntry,
  Board,
  BoardColumn,
  WorkItemType,
  WorkItem,
  Sprint,
  SprintBurndownSnapshot,
  WorkingCalendar,
  WorkingCalendarException,
  InsightSnooze,
  InsightTargetKind,
  Benefit,
  BenefitCategory,
  BenefitStatus,
  Demand,
  DemandStage,
  Lesson,
  LessonCategory,
  ResourceTimeOff,
  TimeOffKind,
  Resource,
  ResourceScenario,
  ScenarioOverride,
  UtilizationSnapshot,
  Requirement,
  RequirementCategory,
  RequirementPriority,
  RequirementStatus,
  RequirementTestCase,
  TestCaseStatus,
  ChangeRequestHistoryEntry,
  ChangeRequestStatus,
  GateTemplate,
  GateTemplateItem,
  ProjectGate,
  ProjectGateItem,
  ProjectGateStatus,
  AppNotification,
  NotificationKind,
  NotificationEntityType,
  ResourceRole,
  ResourceRoleAssignment,
  RateCard,
  ResourceSkillLevel,
  SkillLevel,
  Department,
  PipelineProject,
  PipelineProjectStatus,
  PipelineProjectPriority,
  PipelineResourceNeed,
} from '@/types';
import { seedProjects, seedPortfolios, seedPrograms } from '@/data/seedProjects';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import * as api from '@/lib/api';
import type { CurrentOrg } from '@/lib/api';
import { DEFAULT_HEALTH_THRESHOLDS, type HealthThresholds } from '@/lib/health/thresholds';
import {
  DEFAULT_RESOURCE_MODE,
  RESOURCE_MODE_STORAGE_KEY,
  normalizeResourceMode,
  type ResourceMode,
} from '@/lib/resourceMode';
import { DEFAULT_DEMAND_WEIGHTS, computeDemandScore, type DemandWeights } from '@/lib/demands';
import { aggregateResources, normalizeResourceName } from '@/lib/resources';
import { scheduleTaskUpsert } from '@/lib/taskWriteQueue';
import { rollUpCompletion } from '@/lib/evm';
import { applyCrRebaseline } from '@/lib/baseline';
import { projectIdPrefix } from '@/lib/projectScopedIds';
import type { ImportedBudgetLine } from '@/lib/import/budgetImport';
import type { ImportedResource } from '@/lib/import/resourceImport';
import {
  indentTask as indentTaskHierarchy,
  outdentTask as outdentTaskHierarchy,
  nextChildWbs,
} from '@/lib/wbsHierarchy';
import { toast } from '@/store/useToastStore';
import { useAuthStore } from '@/store/useAuthStore';
import { convertRiskToIssue as buildConversion } from '@/lib/riskConversion';
import {
  subscribeToWorkspace,
  unsubscribeAll,
  type RealtimeHandlers,
} from '@/lib/realtime';
import { getWorkspaceSubscription, type Subscription } from '@/lib/billing/api';

// =============================================================================
// safeStorage (preserved from Phase 10 — used in localStorage mode for the
// rare case of cross-tab sync; not central to dual-mode)
// =============================================================================
const STORAGE_KEY = 'projexhq-projects-v4';
const ACTIVE_ORG_STORAGE_KEY = 'projexhq-active-org-v1';
const RESOURCE_CAPACITY_STORAGE_KEY = 'projexhq-resource-capacities-v1';
const SCENARIOS_STORAGE_KEY = 'projexhq-resource-scenarios-v1';
const UTIL_SNAPSHOTS_STORAGE_KEY = 'projexhq-util-snapshots-v1';

/**
 * Monday (ISO yyyy-mm-dd) of the week containing `d`, computed in UTC so the
 * week key matches the server-side cron (capture-utilization-snapshot) and the
 * two never create duplicate rows for "the same week".
 */
function weekMondayIso(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = x.getUTCDay();
  x.setUTCDate(x.getUTCDate() + (day === 0 ? -6 : 1 - day));
  return x.toISOString().slice(0, 10);
}

let _cached: { get: () => Project[]; set: (v: Project[]) => void } | null = null;

/**
 * Phase 30.15: parse 'YYYY-MM-DD' into a UTC Date. Avoids local-timezone
 * surprises that would shift the date by ±1 day.
 */
function parseISODate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, (m ?? 1) - 1, d ?? 1));
}

/**
 * Phase 30.15: format a Date as 'YYYY-MM-DD' in UTC. Pair with parseISODate.
 */
function formatISODate(d: Date): string {
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function localStore() {
  if (_cached) return _cached;
  const memory = { value: null as Project[] | null };
  const usable = (() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;
      const k = '__projexhq_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch {
      return false;
    }
  })();

  _cached = {
    get: () => {
      if (!usable) return memory.value ?? [];
      try {
        const s = window.localStorage.getItem(STORAGE_KEY);
        if (!s) return [];
        return JSON.parse(s) as Project[];
      } catch {
        return memory.value ?? [];
      }
    },
    set: (v) => {
      memory.value = v;
      if (!usable) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
      } catch (e) {
        console.warn('[ProjexHQ] localStorage write failed:', e);
      }
    },
  };
  return _cached;
}

/**
 * Read/write the user's currently active org id. Persisted across reloads
 * and tab refreshes so users don't lose their context.
 */
function getStoredActiveOrgId(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function setStoredActiveOrgId(orgId: string | null) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (orgId) {
      window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    } else {
      window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/**
 * Phase 30.20.2: persist resource capacities in localStorage mode so they
 * survive page refresh. In API mode the source of truth is the DB; this
 * key is unused. Stored as JSON: { [nameNormalized]: { hoursPerWeek, displayName, notes } }.
 */
type CapacityMap = Record<string, { hoursPerWeek: number; displayName: string; notes: string | null; hourlyRate: number | null; isArchived: boolean }>;

function loadStoredCapacities(): CapacityMap {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    const raw = window.localStorage.getItem(RESOURCE_CAPACITY_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as CapacityMap;
  } catch {
    return {};
  }
}

function saveStoredCapacities(map: CapacityMap) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(RESOURCE_CAPACITY_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

/**
 * Phase 30.23: persist workspace phase definitions in localStorage mode so
 * they survive page refresh. In API mode the source of truth is the DB.
 */
const WORKSPACE_PHASES_STORAGE_KEY = 'projexhq-workspace-phases-v1';
const PROJECT_SIZE_TIERS_STORAGE_KEY = 'projexhq-project-size-tiers-v1';

function loadStoredPhases(): WorkspacePhase[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(WORKSPACE_PHASES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as WorkspacePhase[];
  } catch {
    return [];
  }
}

function saveStoredPhases(phases: WorkspacePhase[]) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(WORKSPACE_PHASES_STORAGE_KEY, JSON.stringify(phases));
  } catch {
    // ignore quota errors
  }
}

/** Migration 0054: default size tiers seeded in localStorage/demo mode. */
const DEFAULT_SIZE_TIER_NAMES: Array<[string, number]> = [
  ['Low', 10],
  ['Medium', 20],
  ['High', 30],
];

function loadStoredSizeTiers(): ProjectSizeTier[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(PROJECT_SIZE_TIERS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as ProjectSizeTier[];
  } catch {
    return [];
  }
}

function saveStoredSizeTiers(tiers: ProjectSizeTier[]) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(PROJECT_SIZE_TIERS_STORAGE_KEY, JSON.stringify(tiers));
  } catch {
    // ignore quota errors
  }
}

function genLocalId(): string {
  return 'local-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/**
 * Phase 30.24: persist time entries in localStorage mode so the demo
 * timesheet experience works without Supabase. In API mode the source
 * of truth is the DB.
 */
const TIME_ENTRIES_STORAGE_KEY = 'projexhq-time-entries-v1';

function loadStoredTimeEntries(): TimeEntry[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(TIME_ENTRIES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as TimeEntry[];
  } catch {
    return [];
  }
}

function saveStoredTimeEntries(entries: TimeEntry[]) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(TIME_ENTRIES_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // ignore quota errors
  }
}

/**
 * Phase 30.27: Boards module localStorage keys. Persisted in localStorage
 * mode so the demo experience works without Supabase. In API mode these
 * are ignored — the DB is the source of truth.
 */
const BOARD_COLUMNS_STORAGE_KEY = 'projexhq-board-columns-v1';
const WORK_ITEM_TYPES_STORAGE_KEY = 'projexhq-work-item-types-v1';
const BOARDS_STORAGE_KEY = 'projexhq-boards-v1';
const WORK_ITEMS_STORAGE_KEY = 'projexhq-work-items-v1';
const SPRINTS_STORAGE_KEY = 'projexhq-sprints-v1';
const SNAPSHOTS_STORAGE_KEY = 'projexhq-burndown-v1';
const CALENDAR_STORAGE_KEY = 'projexhq-calendar-v1';
const CALENDAR_EXCEPTIONS_STORAGE_KEY = 'projexhq-calendar-exceptions-v1';
const INSIGHT_SNOOZES_STORAGE_KEY = 'projexhq-insight-snoozes-v1';
const INSIGHT_POPUPS_STORAGE_KEY = 'projexhq-insight-popups-v1';
const REQUIREMENTS_STORAGE_KEY = 'projexhq-requirements-v1';
const BENEFITS_STORAGE_KEY = 'projexhq-benefits-v1';
const DEMANDS_STORAGE_KEY = 'projexhq-demands-v1';
const DEMAND_WEIGHTS_STORAGE_KEY = 'projexhq-demand-weights-v1';
const LESSONS_STORAGE_KEY = 'projexhq-lessons-v1';
const TIME_OFF_STORAGE_KEY = 'projexhq-time-off-v1';
const RESOURCES_STORAGE_KEY = 'projexhq-resources-v1';
const PORTFOLIOS_STORAGE_KEY = 'projexhq-portfolios-v1';
const PROGRAMS_STORAGE_KEY = 'projexhq-programs-v1';

/**
 * Synthetic subscription used only in demo/localStorage mode so the demo can
 * showcase every paid module. Active + Enterprise tier ⇒ all feature gates
 * (canUseDemands/Lessons/Briefings/AI/Benefits) pass.
 */
const DEMO_SUBSCRIPTION: Subscription = {
  organization_id: 'demo',
  stripe_customer_id: 'DEMO',
  stripe_subscription_id: null,
  stripe_price_id: null,
  tier: 'enterprise',
  status: 'active',
  quantity: 50,
  current_period_start: null,
  current_period_end: null,
  trial_start: null,
  trial_end: null,
  cancel_at_period_end: false,
  canceled_at: null,
  created_at: '2026-01-01T00:00:00.000Z',
  updated_at: '2026-01-01T00:00:00.000Z',
};

/** Generate a project ID like "PRJ-A3F2K9" (mirrors NewProjectModal). */
function generateProjectId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'PRJ-';
  for (let i = 0; i < 6; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}
const TEST_CASES_STORAGE_KEY = 'projexhq-test-cases-v1';
const GATE_TEMPLATES_STORAGE_KEY = 'projexhq-gate-templates-v1';
const PROJECT_GATES_STORAGE_KEY = 'projexhq-project-gates-v1';

function loadStored<T>(key: string): T[] {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as T[];
  } catch {
    return [];
  }
}

function saveStored<T>(key: string, items: T[]) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // ignore quota errors
  }
}

/**
 * Phase 30.27: default statuses and types for localStorage-mode workspaces.
 * Matches migration 0028's seed rows. Injected on first read when no
 * persisted data exists.
 */
function defaultBoardColumns(orgId: string): BoardColumn[] {
  const now = new Date().toISOString();
  return [
    { id: 'local-col-todo', organizationId: orgId, name: 'To Do', color: '#94A3B8', sortOrder: 1, isTerminal: false, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-col-inprog', organizationId: orgId, name: 'In Progress', color: '#F59E0B', sortOrder: 2, isTerminal: false, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-col-done', organizationId: orgId, name: 'Done', color: '#10B981', sortOrder: 3, isTerminal: true, isArchived: false, createdAt: now, updatedAt: now },
  ];
}

function defaultWorkItemTypes(orgId: string): WorkItemType[] {
  const now = new Date().toISOString();
  return [
    { id: 'local-type-task', organizationId: orgId, name: 'Task', icon: 'check-square', color: '#3B82F6', sortOrder: 1, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-type-story', organizationId: orgId, name: 'Story', icon: 'bookmark', color: '#8B5CF6', sortOrder: 2, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-type-bug', organizationId: orgId, name: 'Bug', icon: 'bug', color: '#EF4444', sortOrder: 3, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-type-spike', organizationId: orgId, name: 'Spike', icon: 'flame', color: '#F97316', sortOrder: 4, isArchived: false, createdAt: now, updatedAt: now },
    { id: 'local-type-epic', organizationId: orgId, name: 'Epic', icon: 'crown', color: '#EC4899', sortOrder: 5, isArchived: false, createdAt: now, updatedAt: now },
  ];
}

// =============================================================================
// Predecessor/successor recalc helpers (preserved from Phase 10)
// =============================================================================
function recalculateSuccessors(tasks: Task[]): Task[] {
  const successorMap = new Map<number, number[]>();
  tasks.forEach((task) => {
    if (!task.predecessors) return;
    task.predecessors.split(',').forEach((p) => {
      const m = p.trim().match(/^(\d+)/);
      if (m) {
        const predId = parseInt(m[1], 10);
        if (!successorMap.has(predId)) successorMap.set(predId, []);
        successorMap.get(predId)!.push(task.taskId);
      }
    });
  });
  return tasks.map((t) => ({
    ...t,
    successors: (successorMap.get(t.taskId) ?? []).join(','),
  }));
}

// =============================================================================
// Summary rollup (Phase 19.2)
// =============================================================================
/**
 * Summary tasks aggregate their child WBS items. Their dates and completion
 * are computed from descendants — not user-typed.
 *
 * Hierarchy is determined by WBS code: a task with WBS "1.2.3" is a
 * descendant of summaries "1.2" and "1". A summary's children are tasks
 * whose WBS starts with the summary's WBS + ".".
 *
 * We do a bottom-up sort by WBS depth (deepest first) so that nested
 * summaries roll up correctly: summary "1.2" gets its dates from leaves
 * "1.2.1" / "1.2.2" first, then summary "1" rolls up from "1.1" and "1.2".
 *
 * Rollup rules:
 *   - startDate: min of all leaf descendants' startDates
 *   - endDate: max of all leaf descendants' endDates
 *   - duration: (endDate - startDate) + 1 days
 *   - percentComplete: duration-weighted average of leaf descendants
 *
 * Empty summaries (no descendants found) are LEFT UNCHANGED. We don't
 * blank out dates the user typed if the summary just happens to have no
 * children yet.
 */
/**
 * Phase 30.36: canonical WBS renumbering.
 *
 * Walk the task array in display order and assign each task a WBS code
 * derived purely from position + depth. This makes WBS match display
 * order after a mid-schedule insert — closing the gap where you'd see
 * "1, 2, 3, 6, 4, 5" because the inserted task grabbed the next free
 * sibling code without shifting neighbors.
 *
 * Depth is derived from the task's CURRENT WBS (segment count), so a
 * task at "2.1" stays at depth 2 after renumbering; it just gets a new
 * numeric code that matches its position within its parent.
 *
 * SAFETY GATE: only runs when every existing WBS is empty or purely
 * numeric-with-dots (e.g. "1", "1.2.3"). If any task has a custom scheme
 * like "OPS-1" or "A.2", we leave everything alone — the user is
 * managing WBS by hand and we shouldn't overwrite their scheme.
 *
 * Predecessors reference taskId internally, not WBS, so renumbering
 * never breaks dependency links.
 */
function renumberAllWbs(tasks: Task[]): Task[] {
  if (tasks.length === 0) return tasks;

  // Safety gate: bail if any WBS is non-numeric. We tolerate trailing/leading
  // dots and double-dots (they'd just be renumbered away) — the gate only
  // fails on true custom schemes like "OPS-1" or "A.2".
  const isNumericSegment = (s: string) => /^\d+$/.test(s);
  const isNumericCompatibleWbs = (wbs: string | undefined) => {
    if (!wbs) return true; // empty is fine — we'll assign one
    const segments = wbs.split('.').filter(Boolean); // ignore trailing/leading/double dots
    if (segments.length === 0) return true; // "..." → nothing meaningful → renumber
    return segments.every(isNumericSegment);
  };
  if (!tasks.every((t) => isNumericCompatibleWbs(t.wbs))) return tasks;

  // Counters per depth; index 0 = depth 1
  const counters: number[] = [];
  let anyChanged = false;
  const next = tasks.map((t) => {
    const currentSegments = (t.wbs || '').split('.').filter(Boolean);
    const depth = Math.max(1, currentSegments.length);

    // Truncate counters to this depth (deeper levels reset for next descendant chain)
    while (counters.length > depth) counters.pop();
    // Ensure counters extend to this depth
    while (counters.length < depth) counters.push(0);

    // If any ancestor counter was never set (orphan child), pin it to 1
    for (let i = 0; i < depth - 1; i++) {
      if (counters[i] === 0) counters[i] = 1;
    }

    // Increment this depth's counter
    counters[depth - 1] = (counters[depth - 1] || 0) + 1;

    const newWbs = counters.slice(0, depth).join('.');
    if (newWbs === (t.wbs || '')) return t;
    anyChanged = true;
    return { ...t, wbs: newWbs };
  });

  return anyChanged ? next : tasks;
}

function recomputeSummaryRollups(tasks: Task[]): Task[] {
  // Index tasks by WBS for fast child lookup
  const summaries = tasks.filter((t) => t.type === 'summary' && t.wbs);
  if (summaries.length === 0) return tasks;

  // Sort summaries deepest-first so nested rollups work bottom-up
  const sortedSummaries = [...summaries].sort((a, b) => {
    const aDepth = (a.wbs || '').split('.').length;
    const bDepth = (b.wbs || '').split('.').length;
    return bDepth - aDepth;
  });

  // We'll mutate a working map keyed by taskId
  const byId = new Map<number, Task>();
  tasks.forEach((t) => byId.set(t.taskId, { ...t }));

  for (const summary of sortedSummaries) {
    const summaryWbs = summary.wbs || '';
    if (!summaryWbs) continue;
    const childPrefix = summaryWbs + '.';

    // Find all LEAF descendants (non-summary tasks whose WBS starts with the
    // summary's WBS prefix). We use leaves only because summary dates are
    // already aggregated from THEIR children — using them again would
    // double-count. With deepest-first traversal, intermediate summary
    // values are already correct by the time we read them, but for
    // simplicity and correctness we collect leaves directly.
    const descendants: Task[] = [];
    for (const t of byId.values()) {
      if (t.taskId === summary.taskId) continue;
      if (t.type === 'summary') continue; // skip nested summaries; we collect their leaves
      const w = t.wbs || '';
      if (w.startsWith(childPrefix)) {
        descendants.push(t);
      }
    }

    if (descendants.length === 0) {
      // Empty summary — leave dates and completion alone
      continue;
    }

    // Compute min start, max end
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    for (const d of descendants) {
      if (d.startDate) {
        if (!minStart || d.startDate < minStart) minStart = d.startDate;
      }
      if (d.endDate) {
        if (!maxEnd || d.endDate > maxEnd) maxEnd = d.endDate;
      }
    }

    if (!minStart || !maxEnd) continue; // not enough date info; leave alone

    // Duration = inclusive-day span
    const startMs = new Date(minStart + 'T00:00:00').getTime();
    const endMs = new Date(maxEnd + 'T00:00:00').getTime();
    const dayCount = Math.max(1, Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1);

    // Duration-weighted % complete across leaf descendants.
    // weight = task.duration (or 1 if missing/zero)
    let totalWeight = 0;
    let weightedSum = 0;
    for (const d of descendants) {
      const w = Math.max(1, d.duration ?? 1);
      const pct = Math.min(100, Math.max(0, d.percentComplete ?? 0));
      totalWeight += w;
      weightedSum += w * pct;
    }
    const rolledPct = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;

    const updated: Task = {
      ...byId.get(summary.taskId)!,
      startDate: minStart,
      endDate: maxEnd,
      duration: dayCount,
      percentComplete: rolledPct,
    };
    byId.set(summary.taskId, updated);
  }

  // Preserve the original task order
  return tasks.map((t) => byId.get(t.taskId) ?? t);
}

function cleanupReferences(tasks: Task[], deletedTaskId: number): Task[] {
  return tasks.map((t) => ({
    ...t,
    predecessors: (t.predecessors ?? '')
      .split(',')
      .map((p) => p.trim())
      .filter((p) => {
        const m = p.match(/^(\d+)/);
        return !m || parseInt(m[1], 10) !== deletedTaskId;
      })
      .join(','),
  }));
}

// =============================================================================
// Store interface (unchanged from Phase 10 except for added bootstrap/status)
// =============================================================================

export type StoreMode = 'localStorage' | 'api' | 'unknown';
export type StoreStatus = 'idle' | 'loading' | 'error';

interface ProjectState {
  // Data
  projects: Project[];
  /** Phase 18: portfolios in the active org */
  portfolios: Portfolio[];
  /** Programs in the active org (Projects → Programs → Portfolios) */
  programs: Program[];
  /** Phase 23: billing subscription for the active org. Null in localStorage mode or until loaded. */
  subscription: Subscription | null;
  /**
   * Phase 23.2: whether the subscription fetch has completed for the active org.
   * Distinguishes "still loading" (false) from "loaded, and there is genuinely
   * no subscription row" (true + subscription === null). Without this flag the
   * Billing page can't tell the two apart and spins forever for workspaces
   * created after the billing migration ran.
   */
  subscriptionLoaded: boolean;
  // Mode + status
  mode: StoreMode;
  status: StoreStatus;
  errorMessage: string | null;
  isInitialized: boolean;
  /** The currently active organization id (in API mode). Null in localStorage mode. */
  activeOrgId: string | null;
  /** Phase 20.3: full active organization record (for currency, plan, etc.) */
  activeOrg: CurrentOrg | null;

  // Bootstrap (called by AuthProvider when auth state changes)
  bootstrap: (authed: boolean) => Promise<void>;
  /** Re-fetch all data from current source (used after seed_sample_project) */
  refresh: () => Promise<void>;
  /** Switch to a different org (must be one the user belongs to). Re-loads data. */
  switchOrg: (orgId: string) => Promise<void>;
  /** Phase 20.3: change the active workspace's display currency. */
  updateActiveOrgCurrency: (currency: string) => void;
  /** Phase 23: re-fetch the workspace subscription. */
  reloadSubscription: () => Promise<void>;

  /**
   * Phase 30.20: resource capacity registry. Keyed by `name_normalized`
   * (same normalization src/lib/resources.ts uses). Missing keys default
   * to 40 hrs/wk. Loaded on workspace switch; updated optimistically.
   */
  resourceCapacities: Record<string, { hoursPerWeek: number; displayName: string; notes: string | null; hourlyRate: number | null; isArchived: boolean }>;
  resourceCapacitiesLoaded: boolean;
  reloadResourceCapacities: () => Promise<void>;
  setResourceCapacity: (nameNormalized: string, displayName: string, hoursPerWeek: number, hourlyRate: number | null, notes?: string | null) => Promise<void>;
  setResourceArchived: (nameNormalized: string, displayName: string, archived: boolean) => Promise<void>;
  clearResourceCapacity: (nameNormalized: string) => Promise<void>;

  /**
   * Phase 30.23: workspace lifecycle phase definitions. Owner/Admin can
   * create/edit/archive; all members read. Loaded on workspace switch.
   */
  workspacePhases: WorkspacePhase[];
  workspacePhasesLoaded: boolean;
  reloadWorkspacePhases: () => Promise<void>;
  createWorkspacePhase: (name: string, description?: string) => Promise<WorkspacePhase | null>;
  updateWorkspacePhase: (id: string, patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean }) => Promise<void>;
  deleteWorkspacePhase: (id: string) => Promise<void>;

  // Migration 0054: PMO-configurable project size tiers (mirrors phases above)
  projectSizeTiers: ProjectSizeTier[];
  projectSizeTiersLoaded: boolean;
  reloadProjectSizeTiers: () => Promise<void>;
  createProjectSizeTier: (name: string, description?: string) => Promise<ProjectSizeTier | null>;
  updateProjectSizeTier: (id: string, patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean }) => Promise<void>;
  deleteProjectSizeTier: (id: string) => Promise<void>;
  /** Seed default Low/Medium/High tiers for this workspace (no-op if any exist). */
  seedDefaultSizeTiers: () => Promise<void>;

  /**
   * Phase 30.24: time entries (planned vs actual). User-scoped slice — by
   * default contains the current user's own entries (loaded on demand for
   * the timesheet page). Aggregate maps for project-level reporting are
   * loaded separately.
   */
  myTimeEntries: TimeEntry[];
  myTimeEntriesLoaded: boolean;
  /** Per-project actual hours by task_id, lazily loaded on project open */
  projectActualHoursByTask: Record<string, Record<string, number>>;
  /** Per-project actual hours by user_id (Phase 3 EVM: timesheet-costed AC) */
  projectActualHoursByUser: Record<string, Record<string, number>>;
  reloadMyTimeEntries: (filters?: { startDate?: string; endDate?: string }) => Promise<void>;
  createTimeEntry: (e: { projectId: string; taskId: string | null; taskNameSnapshot: string; entryDate: string; hours: number; description?: string }) => Promise<TimeEntry | null>;
  updateTimeEntry: (id: string, patch: { hours?: number; description?: string | null; entryDate?: string; taskId?: string | null; taskNameSnapshot?: string }) => Promise<void>;
  deleteTimeEntry: (id: string) => Promise<void>;
  reloadProjectActualHours: (projectId: string) => Promise<void>;

  /**
   * Phase 30.27: Boards module — workspace-level statuses and types (shared
   * across all boards), plus per-project boards and per-board work items.
   *
   * Loading strategy:
   *   - boardColumns + workItemTypes: loaded once on org switch/bootstrap
   *   - boardsByProject: loaded lazily when a project's Board tab opens
   *   - workItemsByBoard: loaded lazily when a board is viewed
   */
  boardColumns: BoardColumn[];
  boardColumnsLoaded: boolean;
  workItemTypes: WorkItemType[];
  workItemTypesLoaded: boolean;
  boardsByProject: Record<string, Board[]>;
  workItemsByBoard: Record<string, WorkItem[]>;
  /**
   * Phase 30.28: all work items for a project (across all its boards).
   * Loaded lazily on Schedule tab open to compute per-task linked counts
   * and the % nudge. Kept separate from workItemsByBoard because the two
   * caches serve different queries (board-scoped rendering vs project-wide
   * task linkage).
   */
  workItemsByProject: Record<string, WorkItem[]>;
  reloadBoardColumns: () => Promise<void>;
  createBoardColumn: (data: { name: string; color?: string; isTerminal?: boolean }) => Promise<BoardColumn | null>;
  updateBoardColumn: (id: string, patch: { name?: string; color?: string | null; sortOrder?: number; isTerminal?: boolean; isArchived?: boolean }) => Promise<void>;
  deleteBoardColumn: (id: string) => Promise<void>;
  reloadWorkItemTypes: () => Promise<void>;
  createWorkItemType: (data: { name: string; icon?: string; color?: string }) => Promise<WorkItemType | null>;
  updateWorkItemType: (id: string, patch: { name?: string; icon?: string | null; color?: string | null; sortOrder?: number; isArchived?: boolean }) => Promise<void>;
  deleteWorkItemType: (id: string) => Promise<void>;
  reloadBoards: (projectId: string) => Promise<void>;
  createBoard: (projectId: string, name: string, description?: string) => Promise<Board | null>;
  updateBoard: (id: string, patch: { name?: string; description?: string | null; sortOrder?: number; isArchived?: boolean }) => Promise<void>;
  deleteBoard: (id: string) => Promise<void>;
  reloadWorkItems: (boardId: string) => Promise<void>;
  /** Phase 30.28: load all work items for a project (across boards) */
  reloadProjectWorkItems: (projectId: string) => Promise<void>;

  /**
   * Phase 30.29: Sprints — per-board iteration entities.
   * sprintsByBoard[boardId] holds all sprints for that board.
   */
  sprintsByBoard: Record<string, Sprint[]>;
  reloadSprints: (boardId: string) => Promise<void>;
  createSprint: (boardId: string, data: { name: string; goal?: string; startDate?: string | null; endDate?: string | null; state?: Sprint['state'] }) => Promise<Sprint | null>;
  updateSprint: (id: string, patch: { name?: string; goal?: string | null; startDate?: string | null; endDate?: string | null; state?: Sprint['state'] }) => Promise<void>;
  deleteSprint: (id: string) => Promise<void>;
  /**
   * Complete a sprint: transitions state to 'completed' and moves incomplete
   * items either to the backlog (targetSprintId=null) or to another sprint.
   */
  completeSprint: (sprintId: string, targetSprintId: string | null) => Promise<{ moved: number } | null>;

  /**
   * Phase 30.33: workspace working calendar. Loaded once per org switch.
   * `workingCalendar` is the default calendar row; `workingCalendarExceptions`
   * are its exception rows. Used by schedule math and Settings UI.
   */
  workingCalendar: WorkingCalendar | null;
  workingCalendarLoaded: boolean;
  workingCalendarExceptions: WorkingCalendarException[];
  reloadWorkingCalendar: () => Promise<void>;
  updateWorkingCalendarBitmask: (bitmask: number) => Promise<void>;
  createCalendarException: (data: {
    name: string;
    description?: string;
    dateFrom: string;
    dateTo: string;
    exceptionType: WorkingCalendarException['exceptionType'];
    isWorking: boolean;
  }) => Promise<WorkingCalendarException | null>;
  updateCalendarException: (id: string, patch: {
    name?: string;
    description?: string | null;
    dateFrom?: string;
    dateTo?: string;
    exceptionType?: WorkingCalendarException['exceptionType'];
    isWorking?: boolean;
  }) => Promise<void>;
  deleteCalendarException: (id: string) => Promise<void>;

  /**
   * Phase 30.35: user's active insight snoozes. Loaded on org switch.
   * Filtered by snoozedUntil > now to determine which insights to hide.
   */
  insightSnoozes: InsightSnooze[];
  insightSnoozesLoaded: boolean;
  reloadInsightSnoozes: () => Promise<void>;
  snoozeInsight: (
    projectId: string,
    ruleId: string,
    targetKind: InsightTargetKind,
    targetId: string,
    days?: number,
  ) => Promise<void>;
  unsnoozeInsight: (snoozeId: string) => Promise<void>;

  /**
   * Phase 30.35: whether to show critical insights as toasts on first
   * appearance. Defaults to false — user opts in via Settings.
   */
  insightPopupsEnabled: boolean;
  setInsightPopupsEnabled: (enabled: boolean) => void;

  /**
   * Phase 31: business requirements and QA test cases. Keyed by projectId.
   * Loaded lazily when the Requirements tab is opened; cached until the
   * user switches projects.
   */
  requirementsByProject: Record<string, Requirement[]>;
  testCasesByProject: Record<string, RequirementTestCase[]>;
  requirementsLoadedForProject: Record<string, boolean>;
  reloadRequirements: (projectId: string) => Promise<void>;
  createRequirement: (
    projectId: string,
    data: {
      reqCode: string;
      title: string;
      description?: string;
      category: RequirementCategory;
      status?: RequirementStatus;
      priority?: RequirementPriority;
      source?: string;
    },
  ) => Promise<Requirement | null>;
  updateRequirement: (id: string, patch: Partial<{
    reqCode: string;
    title: string;
    description: string | null;
    category: RequirementCategory;
    status: RequirementStatus;
    priority: RequirementPriority;
    source: string | null;
  }>) => Promise<void>;
  deleteRequirement: (id: string) => Promise<void>;

  // Benefits Realization (paid module — Pro/Enterprise)
  benefitsByProject: Record<string, Benefit[]>;
  benefitsLoadedForProject: Record<string, boolean>;
  reloadBenefits: (projectId: string) => Promise<void>;
  createBenefit: (
    projectId: string,
    data: {
      name: string;
      description?: string;
      category: BenefitCategory;
      owner?: string;
      unit?: string;
      baselineValue?: number;
      targetValue?: number;
      actualValue?: number;
      higherIsBetter?: boolean;
      targetDate?: string;
      realizedDate?: string;
      status?: BenefitStatus;
    },
  ) => Promise<Benefit | null>;
  updateBenefit: (id: string, patch: Partial<{
    name: string;
    description: string | null;
    category: BenefitCategory;
    owner: string | null;
    unit: string | null;
    baselineValue: number | null;
    targetValue: number | null;
    actualValue: number | null;
    higherIsBetter: boolean | null;
    targetDate: string | null;
    realizedDate: string | null;
    status: BenefitStatus;
  }>) => Promise<void>;
  deleteBenefit: (id: string) => Promise<void>;

  // Intake & Demand Management (paid module — Pro/Enterprise)
  demands: Demand[];
  demandsLoaded: boolean;
  demandWeights: DemandWeights;
  reloadDemands: () => Promise<void>;
  loadDemandWeights: () => Promise<void>;
  saveDemandWeights: (weights: DemandWeights) => Promise<void>;
  createDemand: (data: {
    title: string;
    description?: string;
    requester?: string;
    type?: string;
    estimatedCost?: number;
    estimatedValue?: number;
    targetDate?: string;
    scoreAlignment?: number;
    scoreValue?: number;
    scoreUrgency?: number;
    scoreRisk?: number;
    scoreEffort?: number;
    stage?: DemandStage;
    decisionNotes?: string;
    revisitDate?: string;
  }) => Promise<Demand | null>;
  updateDemand: (id: string, patch: Partial<{
    title: string;
    description: string;
    requester: string;
    type: string;
    estimatedCost: number;
    estimatedValue: number;
    targetDate: string;
    scoreAlignment: number;
    scoreValue: number;
    scoreUrgency: number;
    scoreRisk: number;
    scoreEffort: number;
    stage: DemandStage;
    decisionNotes: string;
    revisitDate: string;
    convertedProjectId: string;
    pipelineProjectId: string;
  }>) => Promise<void>;
  deleteDemand: (id: string) => Promise<void>;
  convertDemandToProject: (id: string) => Promise<string | null>;
  /** Send an approved demand to a portfolio's roadmap as a linked pipeline project. */
  sendDemandToRoadmap: (demandId: string, portfolioId: string) => Promise<string | null>;
  /** Start a pipeline project as a real project (and link both, and its source demand). */
  startPipelineProject: (pipelineProjectId: string) => Promise<string | null>;

  // Lessons-Learned (paid module — Pro/Enterprise)
  lessons: Lesson[];
  lessonsLoaded: boolean;
  reloadLessons: () => Promise<void>;
  createLesson: (data: {
    projectId?: string | null;
    title: string;
    category: LessonCategory;
    description?: string;
    tags?: string[];
  }) => Promise<Lesson | null>;
  updateLesson: (id: string, patch: Partial<{
    projectId: string | null;
    title: string;
    category: LessonCategory;
    description: string | null;
    tags: string[];
  }>) => Promise<void>;
  deleteLesson: (id: string) => Promise<void>;
  // Resource availability / PTO
  timeOff: ResourceTimeOff[];
  timeOffLoaded: boolean;
  reloadTimeOff: () => Promise<void>;
  addTimeOff: (data: {
    nameNormalized: string;
    displayName: string;
    startDate: string;
    endDate: string;
    kind: TimeOffKind;
    note?: string | null;
  }) => Promise<ResourceTimeOff | null>;
  deleteTimeOff: (id: string) => Promise<void>;
  // Global resource registry
  resources: Resource[];
  resourcesLoaded: boolean;
  reloadResources: () => Promise<void>;
  addResource: (data: {
    displayName: string;
    userId?: string | null;
    email?: string | null;
    role?: string | null;
    department?: string | null;
    grade?: string | null;
    isActive?: boolean;
  }) => Promise<Resource | null>;
  updateResource: (id: string, patch: Partial<{
    displayName: string;
    nameNormalized: string;
    userId: string | null;
    email: string | null;
    role: string | null;
    department: string | null;
    grade: string | null;
    skills: string[];
    isActive: boolean;
  }>) => Promise<void>;
  deleteResource: (id: string) => Promise<void>;
  /**
   * Bulk-import people into the registry from a CSV/Excel parse. Upserts by
   * normalized name (existing rows are updated, not duplicated), sets weekly
   * hours + hourly rate when provided, and — when `invite` is set — sends a
   * workspace member invite to each row that has an email (api mode only).
   */
  importResources: (
    rows: ImportedResource[],
    opts: { invite: boolean },
  ) => Promise<{ added: number; updated: number; invited: number; inviteFailed: number }>;
  /** Toggle active by normalized key; upserts a registry row if none exists. */
  setResourceActive: (nameNormalized: string, displayName: string, active: boolean) => Promise<void>;
  /** Migration 0063: what-if scenarios. */
  scenarios: ResourceScenario[];
  scenariosLoaded: boolean;
  reloadScenarios: () => Promise<void>;
  saveScenario: (name: string, overrides: ScenarioOverride[], id?: string) => Promise<string | null>;
  deleteScenario: (id: string) => Promise<void>;
  /** Migration 0064: utilisation trend snapshots. */
  utilizationSnapshots: UtilizationSnapshot[];
  utilizationSnapshotsLoaded: boolean;
  reloadUtilizationSnapshots: () => Promise<void>;
  captureUtilizationSnapshot: (force?: boolean) => Promise<'captured' | 'recent' | 'empty'>;
  /**
   * Rewrite every name-keyed occurrence of `fromKey` to `toDisplayName` across
   * team members, task assignments, capacity, time off, and role assignments.
   * Does NOT touch the registry row itself (callers handle rename vs merge).
   * Returns how many projects/tasks were rewritten.
   */
  rewriteResourceName: (fromKey: string, toDisplayName: string) => Promise<{ projects: number; tasks: number }>;
  createTestCase: (
    projectId: string,
    requirementId: string,
    data: {
      testCode?: string;
      testName: string;
      testSteps?: string;
      expectedResult?: string;
      testStatus?: TestCaseStatus;
    },
  ) => Promise<RequirementTestCase | null>;
  updateTestCase: (id: string, patch: Partial<{
    testCode: string | null;
    testName: string;
    testSteps: string | null;
    expectedResult: string | null;
    actualResult: string | null;
    testStatus: TestCaseStatus;
    testedBy: string | null;
    testedAt: string | null;
    notes: string | null;
  }>) => Promise<void>;
  deleteTestCase: (id: string) => Promise<void>;

  /**
   * Phase 32a: Change request workflow — history + workspace policy.
   */
  crHistoryByCr: Record<string, ChangeRequestHistoryEntry[]>;
  crApprovalRoles: string[];
  gatePolicy: 'soft' | 'hard';
  /** Migration 0075: whether the CR approval workflow is enabled for this workspace. */
  crApprovalEnabled: boolean;
  /** Migration 0075: number of distinct approvers required to approve a CR. */
  crApprovalsRequired: number;
  myRoleInOrg: string | null;
  workspacePolicyLoaded: boolean;
  reloadWorkspacePolicy: () => Promise<void>;
  /** Migration 0060: workspace RAG health thresholds (status report + insights). */
  healthThresholds: HealthThresholds;
  healthThresholdsLoaded: boolean;
  reloadHealthThresholds: () => Promise<void>;
  updateHealthThresholds: (patch: Partial<HealthThresholds>) => Promise<void>;
  /**
   * Migration 0066 (R1): workspace resource-management mode. The raw stored
   * value; consumers should read it through `useResourceMode()`, which forces
   * 'standard' when the workspace isn't on the Enterprise tier.
   */
  resourceMode: ResourceMode;
  resourceModeLoaded: boolean;
  reloadResourceMode: () => Promise<void>;
  setResourceMode: (mode: ResourceMode) => Promise<void>;
  reloadCrHistory: (changeRequestId: string) => Promise<void>;
  transitionChangeRequest: (
    projectId: string,
    changeRequestId: string,
    toStatus: ChangeRequestStatus,
    comment: string | undefined,
    actorDisplay: string | undefined,
  ) => Promise<boolean>;

  /**
   * Phase 32b: Gate templates + Project gates.
   */
  gateTemplates: GateTemplate[];
  gateTemplatesLoaded: boolean;
  gatesByProject: Record<string, ProjectGate[]>;
  gatesLoadedForProject: Record<string, boolean>;
  reloadGateTemplates: () => Promise<void>;
  createGateTemplate: (data: { name: string; description?: string }) => Promise<GateTemplate | null>;
  updateGateTemplate: (id: string, patch: Partial<{ name: string; description: string | null; isArchived: boolean }>) => Promise<void>;
  deleteGateTemplate: (id: string) => Promise<void>;
  createGateTemplateItem: (
    templateId: string,
    data: { label: string; description?: string; isRequired?: boolean; sortOrder?: number },
  ) => Promise<GateTemplateItem | null>;
  updateGateTemplateItem: (
    id: string,
    patch: Partial<{ label: string; description: string | null; isRequired: boolean; sortOrder: number }>,
  ) => Promise<void>;
  deleteGateTemplateItem: (id: string) => Promise<void>;
  reloadProjectGates: (projectId: string) => Promise<void>;
  createProjectGate: (
    projectId: string,
    data: {
      name: string;
      workspacePhaseId?: string;
      templateId?: string;
      requiresApproval?: boolean;
      itemsToCopy?: GateTemplateItem[];
    },
  ) => Promise<ProjectGate | null>;
  updateProjectGate: (
    id: string,
    patch: Partial<{ name: string; requiresApproval: boolean; notes: string | null }>,
  ) => Promise<void>;
  deleteProjectGate: (id: string) => Promise<void>;
  toggleProjectGateItem: (itemId: string, isChecked: boolean, actorDisplay: string | undefined) => Promise<void>;
  transitionProjectGate: (
    gateId: string,
    toStatus: ProjectGateStatus,
    comment: string | undefined,
    actorDisplay: string | undefined,
  ) => Promise<boolean>;
  updateWorkspacePolicy: (patch: Partial<{ crApprovalRoles: string[]; gatePolicy: 'soft' | 'hard'; crApprovalEnabled: boolean; crApprovalsRequired: number }>) => Promise<void>;

  /**
   * Phase 34a: Notifications.
   */
  notifications: AppNotification[];
  notificationsLoaded: boolean;
  reloadNotifications: () => Promise<void>;
  createNotification: (data: {
    recipientUserId: string;
    kind: NotificationKind;
    entityType: NotificationEntityType;
    entityId: string;
    projectId?: string;
    title: string;
    body?: string;
    actorDisplay?: string;
  }) => Promise<void>;
  markNotificationRead: (id: string) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;

  /**
   * Phase 35a: Roadmap foundation state.
   *
   * Resource roles and assignments live at the workspace level; pipeline
   * projects live under a portfolio. Loaded lazily on first access.
   */
  resourceRoles: ResourceRole[];
  resourceRolesLoaded: boolean;
  resourceRoleAssignments: ResourceRoleAssignment[];
  resourceRoleAssignmentsLoaded: boolean;
  pipelineProjectsByPortfolio: Record<string, PipelineProject[]>;
  pipelineProjectsLoadedForPortfolio: Record<string, boolean>;
  reloadResourceRoles: () => Promise<void>;
  createResourceRole: (data: { name: string; description?: string; color?: string }) => Promise<ResourceRole | null>;
  updateResourceRole: (id: string, patch: Partial<{ name: string; description: string | null; color: string | null; isArchived: boolean }>) => Promise<void>;
  deleteResourceRole: (id: string) => Promise<void>;
  reloadResourceRoleAssignments: () => Promise<void>;
  /** Set the resource's SOLE role (clears any others), always primary. Standard-mode path. */
  assignResourceRole: (resourceName: string, roleId: string) => Promise<void>;
  /** Add an additional role (R3 / Enterprise). Becomes primary if it's the first. */
  addResourceRole: (resourceName: string, roleId: string) => Promise<void>;
  /** Make an already-assigned role the primary one (R3 / Enterprise). */
  setPrimaryResourceRole: (resourceName: string, roleId: string) => Promise<void>;
  unassignResourceRole: (assignmentId: string) => Promise<void>;
  /** Enterprise model R2 (migration 0067): role/grade rate cards. */
  rateCards: RateCard[];
  rateCardsLoaded: boolean;
  reloadRateCards: () => Promise<void>;
  createRateCard: (data: { roleId: string; grade?: string | null; costRate: number; billRate?: number | null; effectiveFrom?: string; effectiveTo?: string | null }) => Promise<RateCard | null>;
  updateRateCard: (id: string, patch: Partial<{ roleId: string; grade: string | null; costRate: number; billRate: number | null; effectiveFrom: string; effectiveTo: string | null }>) => Promise<void>;
  deleteRateCard: (id: string) => Promise<void>;
  /** Enterprise model R3 (migration 0069): skill proficiency levels. */
  resourceSkillLevels: ResourceSkillLevel[];
  resourceSkillLevelsLoaded: boolean;
  reloadResourceSkillLevels: () => Promise<void>;
  setSkillLevel: (nameNormalized: string, skill: string, level: SkillLevel) => Promise<void>;
  clearSkillLevel: (id: string) => Promise<void>;
  /** Enterprise model R4 (migration 0070): department registry. */
  departments: Department[];
  departmentsLoaded: boolean;
  reloadDepartments: () => Promise<void>;
  createDepartment: (data: { name: string; costCenter?: string | null; managerUserId?: string | null; parentId?: string | null; utilAmberPct?: number | null; utilRedPct?: number | null }) => Promise<Department | null>;
  updateDepartment: (id: string, patch: Partial<{ name: string; costCenter: string | null; managerUserId: string | null; parentId: string | null; utilAmberPct: number | null; utilRedPct: number | null }>) => Promise<void>;
  deleteDepartment: (id: string) => Promise<void>;
  reloadPipelineProjects: (portfolioId: string) => Promise<void>;
  createPipelineProject: (portfolioId: string, data: {
    name: string;
    description?: string;
    status?: PipelineProjectStatus;
    priority?: PipelineProjectPriority;
    businessValue?: number;
    effortWeeks?: number;
    targetStartDate?: string;
    targetEndDate?: string;
    sourceDemandId?: string;
  }) => Promise<PipelineProject | null>;
  updatePipelineProject: (id: string, patch: Partial<PipelineProject>) => Promise<void>;
  deletePipelineProject: (id: string) => Promise<void>;

  /**
   * Phase 35b: pipeline resource needs (per-project role requirements).
   * Loaded workspace-wide on first access — indexed by pipeline_project_id.
   */
  pipelineResourceNeeds: PipelineResourceNeed[];
  pipelineResourceNeedsLoaded: boolean;
  reloadPipelineResourceNeeds: () => Promise<void>;
  createPipelineResourceNeed: (data: {
    pipelineProjectId: string;
    roleId: string;
    count: number;
    weeks?: number;
    startOffsetWeeks?: number;
    notes?: string;
  }) => Promise<PipelineResourceNeed | null>;
  updatePipelineResourceNeed: (id: string, patch: Partial<PipelineResourceNeed>) => Promise<void>;
  deletePipelineResourceNeed: (id: string) => Promise<void>;

  /**
   * Phase 30.30: sprint burndown snapshots. Keyed by sprintId. Loaded
   * lazily when a sprint report is opened. Also cached as we compute
   * new snapshots on mutations that affect sprint composition.
   */
  snapshotsBySprint: Record<string, SprintBurndownSnapshot[]>;
  reloadSprintSnapshots: (sprintId: string) => Promise<void>;
  reloadSprintSnapshotsBatch: (sprintIds: string[]) => Promise<void>;
  /**
   * Compute current metrics for a sprint from the local caches and upsert
   * a snapshot for today. Called after any mutation that could affect
   * sprint composition. Silently no-ops if the sprint is 'planning' or
   * 'completed' — we only track burndown during active sprints (plus one
   * final snapshot when sprint completes).
   */
  snapshotSprintNow: (sprintId: string) => Promise<void>;
  createWorkItem: (boardId: string, projectId: string, data: {
    title: string;
    description?: string;
    typeId?: string | null;
    statusId?: string | null;
    assigneeUserId?: string | null;
    dueDate?: string | null;
    storyPoints?: number | null;
    taskId?: string | null;
    sprintId?: string | null;
  }) => Promise<WorkItem | null>;
  updateWorkItem: (id: string, patch: {
    title?: string;
    description?: string | null;
    typeId?: string | null;
    statusId?: string | null;
    assigneeUserId?: string | null;
    dueDate?: string | null;
    storyPoints?: number | null;
    taskId?: string | null;
    sprintId?: string | null;
    sortOrder?: number;
  }) => Promise<void>;
  deleteWorkItem: (id: string) => Promise<void>;

  // Project CRUD
  setProjects: (projects: Project[]) => void;
  addProject: (project: Project) => void;
  /**
   * Bulk-create projects from an import (each already carries its wbsTasks).
   * Persists locally in both modes; in api mode creates each project row then
   * upserts its tasks in sequence (tasks FK the project, so order matters).
   */
  importProjects: (projects: Project[]) => void;
  /** Replace an existing project's entire schedule with imported tasks. */
  replaceProjectSchedule: (projectId: string, tasks: Task[]) => void;
  /**
   * Create a project from the Estimator, seeding its schedule and budget in one
   * step. In api mode the create is awaited before tasks and budget lines are
   * written (both FK the project), so there is no ordering race.
   */
  createEstimatedProject: (
    project: Project,
    tasks: Task[],
    budgetLines: ImportedBudgetLine[],
  ) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  resetToSeed: () => void;

  // Portfolio CRUD (Phase 18)
  addPortfolio: (portfolio: Portfolio) => void;
  updatePortfolio: (id: string, updates: Partial<Portfolio>) => void;
  deletePortfolio: (id: string) => void;
  /** Bulk-assign projects to a portfolio (or null to unassign). */
  assignProjectsToPortfolio: (
    projectIds: string[],
    portfolioId: string | null,
  ) => void;

  // Program CRUD (Projects → Programs → Portfolios)
  addProgram: (program: Program) => void;
  updateProgram: (id: string, updates: Partial<Program>) => void;
  deleteProgram: (id: string) => void;
  /** Bulk-assign projects to a program (or null to unassign). */
  assignProjectsToProgram: (projectIds: string[], programId: string | null) => void;

  // Task CRUD
  addTask: (projectId: string, task: Task) => void;
  /**
   * Phase 30.32: insert a task at a specific position in the project's task
   * list. If afterTaskId is null, insert at the top. Otherwise insert
   * immediately after the task with that id. Used by the schedule's
   * "insert row" hover control.
   */
  insertTaskAt: (projectId: string, task: Task, afterTaskId: number | null) => void;
  /**
   * Phase 30.36: manually re-run WBS renumbering across the whole project.
   * Useful for cleaning up gaps after deletes or manual WBS edits. No-op
   * if any task has a non-numeric WBS scheme.
   */
  renumberProjectWbs: (projectId: string) => void;
  updateTask: (projectId: string, taskId: number, updates: Partial<Task>) => void;
  deleteTask: (projectId: string, taskId: number) => void;
  reorderTasks: (projectId: string, tasks: Task[]) => void;

  // Phase 30.6: WBS hierarchy operations.
  // Each returns true on success, false if the operation isn't valid
  // (e.g. trying to indent the first row, outdent a root task).
  indentTask: (projectId: string, taskId: number) => boolean;
  outdentTask: (projectId: string, taskId: number) => boolean;
  /** Insert a new task as the next child of `parentTaskId`. */
  addSubtaskUnder: (projectId: string, parentTaskId: number) => void;

  // Risk CRUD
  addRisk: (projectId: string, risk: Risk) => void;
  updateRisk: (projectId: string, riskId: string, updates: Partial<Risk>) => void;
  deleteRisk: (projectId: string, riskId: string) => void;

  // Phase 30.9 (Budget B2): Budget line CRUD
  // Phase 30.12 (Budget B3): all signatures now include versionId since two
  // versions can have lines with the same id (post-fork). Identifying a
  // line requires the composite (projectId, versionId, lineId).
  addBudgetLine: (projectId: string, line: BudgetLine) => void;
  /**
   * Import budget/cost lines from a file into a project's current budget
   * version (creating a default version if none exists). `replace` clears the
   * version's existing lines first. Assigns project-scoped ids; persists in
   * both demo and api modes.
   */
  importBudgetLines: (projectId: string, lines: ImportedBudgetLine[], replace: boolean) => void;
  updateBudgetLine: (
    projectId: string,
    versionId: string,
    lineId: string,
    updates: Partial<BudgetLine>,
  ) => void;
  deleteBudgetLine: (projectId: string, versionId: string, lineId: string) => void;

  // Phase 30.12 (Budget B3): Budget version CRUD. 30.12 only exposes
  // list-via-project-fetch and `setCurrentBudgetVersion`. Scenario create/
  // rename/delete come in 30.13.
  addBudgetVersion: (projectId: string, version: BudgetVersion) => void;
  updateBudgetVersion: (
    projectId: string,
    versionId: string,
    updates: Partial<BudgetVersion>,
  ) => void;
  deleteBudgetVersion: (projectId: string, versionId: string) => void;
  /** Set which version is current for a project. Persists to projects.current_budget_version_id. */
  setCurrentBudgetVersion: (projectId: string, versionId: string) => void;

  /**
   * Phase 30.12.2: awaitable helper that creates a budget version and points
   * the project's current_budget_version_id at it in sequence, returning
   * only after BOTH server writes have committed.
   *
   * Used by the BudgetTab's "add the first line" flow on projects that have
   * no budget data yet. Sequencing matters because if the version-create and
   * pointer-set fire as fire-and-forget, a subsequent project update (e.g.
   * the user edits Approved Budget on Overview) can race ahead — its
   * realtime echo carries the pre-pointer-set state with
   * current_budget_version_id = NULL, and the client's line filter empties.
   * Awaiting both writes here makes the race structurally impossible.
   *
   * Local state is updated optimistically before the API calls, so the UI
   * reflects the new version immediately even while the server commits.
   */
  ensureCurrentBudgetVersion: (
    projectId: string,
    version: BudgetVersion,
  ) => Promise<void>;

  /**
   * Phase 30.13 (Budget B3 step 2): create a new budget version by copying
   * all lines from a source version into it. This is the scenario-fork
   * operation. Awaitable — returns after the version row AND all copied
   * lines have committed server-side.
   *
   * If sourceVersionId is null, the new version is created with no lines
   * (a blank scenario). The B3 UI doesn't expose blank fork; all forks go
   * from current.
   *
   * Line IDs are preserved across the fork. A line `SAFXUT-BUD-001` in
   * version A becomes a separate row `SAFXUT-BUD-001` in version B —
   * distinct because the composite PK is (project_id, budget_version_id, id).
   * Same line id across versions enables a stable identity for the diff
   * view (Phase 30.14) and for CR linkages that survive a promote.
   */
  forkBudgetVersion: (
    projectId: string,
    args: {
      sourceVersionId: string | null;
      newVersion: BudgetVersion;
    },
  ) => Promise<BudgetVersion>;

  /**
   * Phase 30.13: promote a scenario to be the new current version.
   *
   * Operations performed, in order:
   *   1. Rename the old current version to "{name} (archived YYYY-MM-DD)"
   *      so it's preserved as history.
   *   2. Update projects.current_budget_version_id to the new version.
   *   3. Walk all CRs in this project. For any whose budget_line_id is not
   *      in the new current version's lines, set budget_line_id = null
   *      (auto-clear stale linkages — Decision 5 of the B3 blueprint).
   *
   * Awaitable so the calling UI can show a loading state. The modal closes
   * only after the server confirms.
   */
  promoteBudgetVersion: (
    projectId: string,
    toVersionId: string,
  ) => Promise<void>;

  /**
   * Phase 30.15 (Budget B4): per-month period CRUD. Each action does
   * optimistic local updates including the corresponding line-level
   * planned/actual recomputation (mirroring the server-side trigger), so
   * the UI stays consistent without waiting for the round trip.
   */
  upsertBudgetLinePeriod: (
    projectId: string,
    period: BudgetLinePeriod,
  ) => void;
  deleteBudgetLinePeriod: (
    projectId: string,
    versionId: string,
    lineId: string,
    periodStart: string,
  ) => void;
  /**
   * Phase 30.15: distribute a line's amount evenly across N consecutive
   * months starting from `firstMonth`. Replaces any existing periods for
   * the line. Awaitable so the caller can show feedback.
   */
  distributeBudgetLineEvenly: (
    projectId: string,
    versionId: string,
    lineId: string,
    args: { totalPlanned: number; firstMonth: string; monthCount: number },
  ) => Promise<void>;

  // Issue CRUD
  addIssue: (projectId: string, issue: Issue) => void;
  updateIssue: (projectId: string, issueId: string, updates: Partial<Issue>) => void;
  deleteIssue: (projectId: string, issueId: string) => void;

  /**
   * Phase 20.4: convert a risk to an issue. Performs the soft conversion
   * (risk status='occurred' + new issue with back-pointer). Both records are
   * persisted via the same fireApi path.
   *
   * @param note Optional explanation of why the risk materialized; goes into the issue description.
   * @returns The new issue id, so the UI can navigate to it.
   */
  convertRiskToIssue: (
    projectId: string,
    riskId: string,
    note: string,
  ) => string | null;

  // Change Request CRUD
  addChange: (projectId: string, change: ChangeRequest) => void;
  updateChange: (
    projectId: string,
    changeId: string,
    updates: Partial<ChangeRequest>,
  ) => void;
  deleteChange: (projectId: string, changeId: string) => void;

  // Decision ops (Migration 0055 — no approval workflow)
  addDecision: (projectId: string, decision: Decision) => void;
  updateDecision: (
    projectId: string,
    decisionId: string,
    updates: Partial<Decision>,
  ) => void;
  deleteDecision: (projectId: string, decisionId: string) => void;

  // Action item ops (Migration 0056 — lightweight follow-ups)
  addActionItem: (projectId: string, item: ActionItem) => void;
  updateActionItem: (
    projectId: string,
    itemId: string,
    updates: Partial<ActionItem>,
  ) => void;
  deleteActionItem: (projectId: string, itemId: string) => void;

  // Baseline ops
  createBaseline: (projectId: string, baseline: Baseline) => void;
  setActiveBaseline: (projectId: string, baselineId: string) => void;
  deleteBaseline: (projectId: string, baselineId: string) => void;
}

// =============================================================================
// Store implementation
// =============================================================================

/**
 * In API mode, every mutation is optimistic: local state updates synchronously,
 * the API call fires in the background, and on failure we surface a toast.
 * On success we don't refetch (the local state already reflects the truth).
 *
 * If a mutation fails we DO NOT auto-revert. Reverting cascade-style bulk
 * mutations is non-trivial and would create surprising UX (the user sees the
 * change, then it disappears). Instead, the toast invites the user to refresh.
 */
export const useProjectStore = create<ProjectState>((set, get) => {
  // -------------------------------------------------------------------------
  // Helpers operating on local state
  // -------------------------------------------------------------------------
  const updateLocalProjects = (
    fn: (projects: Project[]) => Project[],
  ) => {
    set((s) => ({ projects: fn(s.projects) }));
    // In localStorage mode, persist after every mutation
    if (get().mode === 'localStorage') {
      localStore().set(get().projects);
    }
  };

  /**
   * Fire an API call in the background. On error, surface a toast.
   * Does not block the optimistic update; does not revert state.
   */
  const fireApi = async (
    label: string,
    p: Promise<{ error: string | null }>,
  ) => {
    try {
      const res = await p;
      if (res.error) {
        toast.error(`${label}: ${res.error}. Refresh to re-sync.`, 6000);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`${label}: ${msg}. Refresh to re-sync.`, 6000);
    }
  };

  /**
   * Persist a project's rolled-up completion (see rollUpCompletion in lib/evm) to
   * the backend after a task change. Reads the already-updated local project so
   * the value matches what the UI shows. No-op in localStorage mode (the local
   * update already carries it) and when the project has no rollup value.
   */
  const persistRolledCompletion = (projectId: string) => {
    if (get().mode !== 'api') return;
    const proj = get().projects.find((p) => p.id === projectId);
    if (!proj) return;
    const rolled = rollUpCompletion(proj.wbsTasks ?? []);
    if (rolled === null) return;
    fireApi('Update progress', api.updateProject(projectId, { completion: rolled }));
  };

  // -------------------------------------------------------------------------
  // Phase 20.5: Realtime event handlers
  // -------------------------------------------------------------------------
  // These mutate local state WITHOUT firing outbound API calls — the change
  // originated from the server. All handlers are idempotent: an INSERT that
  // duplicates an existing row replaces it; an UPDATE on a missing row falls
  // through to no-op; a DELETE of a missing row is silently ignored.
  //
  // The "echo loop" is implicit last-write-wins: when the local user makes a
  // change, the change flows back through Realtime and overwrites the
  // optimistic local state with the same data. No flicker, no special handling.

  const buildRealtimeHandlers = (): RealtimeHandlers => ({
    onProjectInsert: (projectId, project) => {
      updateLocalProjects((ps) => {
        if (ps.some((p) => p.id === projectId)) return ps; // already exists
        return [...ps, project];
      });
    },
    onProjectUpdate: (projectId, project) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            // Preserve nested arrays (risks/tasks/etc) — those flow via their own channels.
            // Phase 30.11.1: budgetLines was missing from this preservation list,
            // causing line items to vanish from the UI when the project's own
            // realtime update fired (e.g. on saving Approved Budget). Lines
            // weren't deleted server-side — a hard refresh re-fetched them —
            // but local state lost them between updates. Fixed by adding the
            // budgetLines preservation here.
            //
            // Phase 30.12: budgetVersions added to the preservation list for
            // the same reason. currentBudgetVersionId is NOT preserved by
            // default — it IS a scalar column on the projects row, so the
            // realtime payload carries the fresh server value and we want
            // that to flow through.
            //
            // Phase 30.12.2: BUT — defensive handling for races. If the echo
            // carries currentBudgetVersionId = null while local has a real
            // value, that's almost certainly a stale echo (a project-row
            // update committed and echoed before a paired version-pointer
            // write landed). Prefer the local value in that asymmetric case.
            //
            // The legitimate case where the server genuinely cleared the
            // pointer (e.g. someone deleted the current version) is handled
            // by our local cascade in deleteBudgetVersion, which clears the
            // local pointer at the same time as the version delete — so
            // when the echo arrives, local already has null and the
            // defensive logic is a no-op.
            //
            // Edge case not covered: external deletion (SQL editor, second
            // tab) of the current version. In that case, local has a value
            // but the version is actually gone server-side. The defensive
            // code would preserve the stale pointer until next page load.
            // Hard refresh recovers; acceptable cost for this rare scenario.
            // Phase 30.15: budgetLinePeriods added to the preservation list
            // for the same reason. periods are a separate collection that
            // flows via its own writes; we don't want a project-row echo to
            // wipe locally-cached period data.
            ? {
                ...project,
                currentBudgetVersionId:
                  project.currentBudgetVersionId ?? p.currentBudgetVersionId,
                wbsTasks: p.wbsTasks,
                risks: p.risks,
                issues: p.issues,
                changes: p.changes,
                baselines: p.baselines,
                budgetLines: p.budgetLines,
                budgetVersions: p.budgetVersions,
                budgetLinePeriods: p.budgetLinePeriods,
              }
            : p,
        ),
      );
    },
    onProjectDelete: (projectId) => {
      updateLocalProjects((ps) => ps.filter((p) => p.id !== projectId));
    },

    onTaskInsert: (projectId, task) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const tasks = p.wbsTasks ?? [];
          if (tasks.some((t) => t.taskId === task.taskId)) return p;
          return { ...p, wbsTasks: [...tasks, task] };
        }),
      );
    },
    onTaskUpdate: (projectId, task) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            wbsTasks: (p.wbsTasks ?? []).map((t) =>
              t.taskId === task.taskId ? task : t,
            ),
          };
        }),
      );
    },
    onTaskDelete: (projectId, taskId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, wbsTasks: (p.wbsTasks ?? []).filter((t) => t.taskId !== taskId) };
        }),
      );
    },

    onRiskInsert: (projectId, risk) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          if ((p.risks ?? []).some((r) => r.id === risk.id)) return p;
          return { ...p, risks: [...(p.risks ?? []), risk] };
        }),
      );
    },
    onRiskUpdate: (projectId, risk) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            risks: (p.risks ?? []).map((r) => (r.id === risk.id ? risk : r)),
          };
        }),
      );
    },
    onRiskDelete: (projectId, riskId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, risks: (p.risks ?? []).filter((r) => r.id !== riskId) };
        }),
      );
    },

    onIssueInsert: (projectId, issue) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          if (p.issues.some((i) => i.id === issue.id)) return p;
          return { ...p, issues: [...p.issues, issue] };
        }),
      );
    },
    onIssueUpdate: (projectId, issue) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, issues: p.issues.map((i) => (i.id === issue.id ? issue : i)) };
        }),
      );
    },
    onIssueDelete: (projectId, issueId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, issues: p.issues.filter((i) => i.id !== issueId) };
        }),
      );
    },

    onChangeInsert: (projectId, change) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          if ((p.changes ?? []).some((c) => c.id === change.id)) return p;
          return { ...p, changes: [...(p.changes ?? []), change] };
        }),
      );
    },
    onChangeUpdate: (projectId, change) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            changes: (p.changes ?? []).map((c) => (c.id === change.id ? change : c)),
          };
        }),
      );
    },
    onChangeDelete: (projectId, changeId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, changes: (p.changes ?? []).filter((c) => c.id !== changeId) };
        }),
      );
    },

    onBaselineInsert: (projectId, baseline) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          if ((p.baselines ?? []).some((b) => b.id === baseline.id)) return p;
          return { ...p, baselines: [...(p.baselines ?? []), baseline] };
        }),
      );
    },
    onBaselineUpdate: (projectId, baseline) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            baselines: (p.baselines ?? []).map((b) => (b.id === baseline.id ? baseline : b)),
          };
        }),
      );
    },
    onBaselineDelete: (projectId, baselineId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return { ...p, baselines: (p.baselines ?? []).filter((b) => b.id !== baselineId) };
        }),
      );
    },

    onPortfolioInsert: (portfolio) => {
      set((s) => {
        if (s.portfolios.some((p) => p.id === portfolio.id)) return s;
        return { portfolios: [...s.portfolios, portfolio] };
      });
    },
    onPortfolioUpdate: (portfolio) => {
      set((s) => ({
        portfolios: s.portfolios.map((p) => (p.id === portfolio.id ? portfolio : p)),
      }));
    },
    onPortfolioDelete: (portfolioId) => {
      set((s) => ({
        portfolios: s.portfolios.filter((p) => p.id !== portfolioId),
      }));
    },

    onProgramInsert: (program) => {
      set((s) => {
        if (s.programs.some((p) => p.id === program.id)) return s;
        return { programs: [...s.programs, program] };
      });
    },
    onProgramUpdate: (program) => {
      set((s) => ({
        programs: s.programs.map((p) => (p.id === program.id ? program : p)),
      }));
    },
    onProgramDelete: (programId) => {
      set((s) => ({
        programs: s.programs.filter((p) => p.id !== programId),
      }));
    },
  });

  // -------------------------------------------------------------------------
  // Core actions
  // -------------------------------------------------------------------------
  return {
    projects: [],
    portfolios: [],
    programs: [],
    subscription: null,
    subscriptionLoaded: false,
    resourceCapacities: {},
    resourceCapacitiesLoaded: false,
    workspacePhases: [],
    workspacePhasesLoaded: false,
    projectSizeTiers: [],
    projectSizeTiersLoaded: false,
    myTimeEntries: [],
    myTimeEntriesLoaded: false,
    projectActualHoursByTask: {},
    projectActualHoursByUser: {},
    boardColumns: [],
    boardColumnsLoaded: false,
    workItemTypes: [],
    workItemTypesLoaded: false,
    boardsByProject: {},
    workItemsByBoard: {},
    workItemsByProject: {},
    sprintsByBoard: {},
    snapshotsBySprint: {},
    workingCalendar: null,
    workingCalendarLoaded: false,
    workingCalendarExceptions: [],
    insightSnoozes: [],
    insightSnoozesLoaded: false,
    insightPopupsEnabled: (() => {
      try {
        return typeof window !== 'undefined' && window.localStorage.getItem('projexhq-insight-popups-v1') === '1';
      } catch { return false; }
    })(),
    benefitsByProject: {},
    benefitsLoadedForProject: {},
    demands: [],
    demandsLoaded: false,
    demandWeights: DEFAULT_DEMAND_WEIGHTS,
    lessons: [],
    lessonsLoaded: false,
    timeOff: [],
    timeOffLoaded: false,
    resources: [],
    resourcesLoaded: false,
    scenarios: [],
    scenariosLoaded: false,
    utilizationSnapshots: [],
    utilizationSnapshotsLoaded: false,
    requirementsByProject: {},
    testCasesByProject: {},
    requirementsLoadedForProject: {},
    crHistoryByCr: {},
    crApprovalRoles: ['owner', 'admin'],
    gatePolicy: 'soft',
    crApprovalEnabled: true,
    crApprovalsRequired: 1,
    myRoleInOrg: null,
    workspacePolicyLoaded: false,
    healthThresholds: DEFAULT_HEALTH_THRESHOLDS,
    healthThresholdsLoaded: false,
    resourceMode: DEFAULT_RESOURCE_MODE,
    resourceModeLoaded: false,
    gateTemplates: [],
    gateTemplatesLoaded: false,
    gatesByProject: {},
    gatesLoadedForProject: {},
    notifications: [],
    notificationsLoaded: false,
    // Phase 35a: roadmap foundation
    resourceRoles: [],
    resourceRolesLoaded: false,
    resourceRoleAssignments: [],
    resourceRoleAssignmentsLoaded: false,
    rateCards: [],
    rateCardsLoaded: false,
    resourceSkillLevels: [],
    resourceSkillLevelsLoaded: false,
    departments: [],
    departmentsLoaded: false,
    pipelineProjectsByPortfolio: {},
    pipelineProjectsLoadedForPortfolio: {},
    pipelineResourceNeeds: [],
    pipelineResourceNeedsLoaded: false,
    mode: 'unknown',
    status: 'idle',
    errorMessage: null,
    isInitialized: false,
    activeOrgId: null,
    activeOrg: null,

    bootstrap: async (authed) => {
      // Phase 20.5: defensive teardown — if bootstrap is called again (e.g.
      // after sign-out followed by sign-in), tear down any prior Realtime
      // subscriptions before we set up new ones.
      unsubscribeAll();

      // Decide mode
      const useApi = authed && isSupabaseConfigured;
      const newMode: StoreMode = useApi ? 'api' : 'localStorage';
      set({ mode: newMode, status: 'loading', errorMessage: null });

      if (newMode === 'localStorage') {
        // Load from localStorage; fall back to seed if empty
        const stored = localStore().get();
        const projects = stored.length > 0 ? stored : seedProjects;
        if (stored.length === 0) localStore().set(seedProjects); // first-load seed write
        // Phase 42: portfolios + programs persist in demo mode too (mirror
        // projects) so the grouping tiers survive a full page reload. On first
        // load (empty storage) fall back to the seed and write it through, the
        // same seed-fallback pattern used for projects above.
        const storedPortfolios = loadStored<Portfolio>(PORTFOLIOS_STORAGE_KEY);
        const portfolios = storedPortfolios.length > 0 ? storedPortfolios : seedPortfolios;
        if (storedPortfolios.length === 0) saveStored(PORTFOLIOS_STORAGE_KEY, seedPortfolios);
        const storedPrograms = loadStored<Program>(PROGRAMS_STORAGE_KEY);
        const programs = storedPrograms.length > 0 ? storedPrograms : seedPrograms;
        if (storedPrograms.length === 0) saveStored(PROGRAMS_STORAGE_KEY, seedPrograms);
        set({
          projects,
          portfolios,
          programs,
          // Demo mode has no billing backend, so unlock every paid module
          // (Intake & Demand, Lessons-Learned, Executive Briefing, AI, Benefits)
          // with a synthetic active Enterprise subscription. Without this,
          // subscriptionLoaded stays false and those pages render a blank null.
          subscription: DEMO_SUBSCRIPTION,
          subscriptionLoaded: true,
          status: 'idle',
          isInitialized: true,
          activeOrgId: null,
          activeOrg: null,
        });
        // Phase 30.20.3: also load capacities from localStorage in this mode.
        // Without this, capacities saved via setResourceCapacity() were written
        // to localStorage but never re-loaded into the store on refresh.
        void get().reloadResourceCapacities();
        void get().reloadResources();
      // Enterprise model R2: rate cards + role assignments feed the rate
      // resolver in the Cost/Budget/Estimator views. Tiny tables; empty in
      // demo (which runs enterprise mode) so cost figures still match Standard.
      void get().reloadResourceRoleAssignments();
      void get().reloadRateCards();
      void get().reloadResourceSkillLevels();
      void get().reloadDepartments();
        // Phase 30.23: load workspace phases from localStorage too.
        void get().reloadWorkspacePhases();
        void get().reloadProjectSizeTiers();
        void get().reloadBoardColumns();
        void get().reloadWorkingCalendar();
        void get().reloadInsightSnoozes();
        void get().reloadWorkspacePolicy();
        void get().reloadHealthThresholds();
        void get().reloadResourceMode();
        void get().reloadGateTemplates();
        void get().reloadPipelineResourceNeeds();
        void get().reloadWorkItemTypes();
        return;
      }

      // API mode: determine which org to load
      // Prefer the user's previously-active org from localStorage, but only
      // if they're still a member of it. Otherwise fall back to first org.
      const orgsRes = await api.listMyOrgs();
      if (orgsRes.error || !orgsRes.data || orgsRes.data.length === 0) {
        set({
          projects: [],
          status: 'error',
          errorMessage: orgsRes.error ?? 'No workspaces found',
          isInitialized: true,
          activeOrgId: null,
          activeOrg: null,
        });
        if (orgsRes.error) toast.error(`Could not load workspaces: ${orgsRes.error}`, 6000);
        return;
      }

      const orgs = orgsRes.data;
      const stored = getStoredActiveOrgId();
      const activeOrg =
        (stored && orgs.find((o) => o.id === stored)) || orgs[0];
      setStoredActiveOrgId(activeOrg.id);

      // Fetch projects + portfolios + programs in parallel — they're independent
      const [res, portRes, progRes] = await Promise.all([
        api.loadAllProjectsFull(activeOrg.id),
        api.listPortfolios(activeOrg.id),
        api.listPrograms(activeOrg.id),
      ]);

      if (res.error) {
        set({
          projects: res.data ?? [],
          portfolios: portRes.data ?? [],
          programs: progRes.data ?? [],
          status: 'error',
          errorMessage: res.error,
          isInitialized: true,
          activeOrgId: activeOrg.id,
          activeOrg,
        });
        toast.error(`Could not load projects: ${res.error}`, 6000);
        return;
      }
      if (portRes.error) {
        // Non-fatal — projects loaded fine; portfolios just missing
        toast.error(`Could not load portfolios: ${portRes.error}`, 6000);
      }
      set({
        projects: res.data ?? [],
        portfolios: portRes.data ?? [],
        programs: progRes.data ?? [],
        status: 'idle',
        errorMessage: null,
        isInitialized: true,
        activeOrgId: activeOrg.id,
        activeOrg,
      });

      // Phase 20.5: subscribe to Realtime for this workspace.
      // Idempotent — any prior subscriptions are torn down first.
      subscribeToWorkspace(activeOrg.id, buildRealtimeHandlers());

      // Phase 23: load billing subscription. Non-fatal on failure.
      // Phase 23.2: always mark the fetch complete (subscriptionLoaded) so the
      // Billing page can distinguish "no row exists" from "still loading".
      // A workspace created after the billing migration legitimately has no
      // subscription row until it goes through Checkout.
      const subRes = await getWorkspaceSubscription(activeOrg.id);
      set({ subscription: subRes.data ?? null, subscriptionLoaded: true });

      // Phase 30.20: load resource capacities (cheap; non-fatal).
      void get().reloadResourceCapacities();
      void get().reloadResources();
      // Enterprise model R2: rate cards + role assignments feed the rate
      // resolver in the Cost/Budget/Estimator views. Tiny tables; empty in
      // demo (which runs enterprise mode) so cost figures still match Standard.
      void get().reloadResourceRoleAssignments();
      void get().reloadRateCards();
      void get().reloadResourceSkillLevels();
      void get().reloadDepartments();
      // Phase 30.23: load workspace phases (cheap; non-fatal).
      void get().reloadWorkspacePhases();
      void get().reloadProjectSizeTiers();
        void get().reloadBoardColumns();
        void get().reloadWorkingCalendar();
        void get().reloadInsightSnoozes();
        void get().reloadWorkspacePolicy();
        void get().reloadHealthThresholds();
        void get().reloadResourceMode();
        void get().reloadGateTemplates();
        void get().reloadPipelineResourceNeeds();
        void get().reloadWorkItemTypes();
    },

    refresh: async () => {
      const { mode, activeOrgId } = get();
      if (mode === 'api') {
        set({ status: 'loading' });
        const [res, portRes, progRes] = await Promise.all([
          api.loadAllProjectsFull(activeOrgId ?? undefined),
          api.listPortfolios(activeOrgId ?? undefined),
          api.listPrograms(activeOrgId ?? undefined),
        ]);
        if (res.error) {
          set({ status: 'error', errorMessage: res.error });
          toast.error(`Refresh failed: ${res.error}`);
          return;
        }
        set({
          projects: res.data ?? [],
          portfolios: portRes.data ?? [],
          programs: progRes.data ?? [],
          status: 'idle',
          errorMessage: null,
        });
      }
      // localStorage mode: nothing to refresh from
    },

    switchOrg: async (orgId) => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api') return; // No-op in localStorage mode
      if (orgId === activeOrgId) return; // Already on this org

      // Phase 20.5: tear down current workspace's Realtime subscriptions.
      // We resubscribe to the new workspace after the project data loads.
      unsubscribeAll();

      set({ status: 'loading', activeOrgId: orgId });
      setStoredActiveOrgId(orgId);

      // Phase 20.3: fetch projects, portfolios, programs, AND the org record (for currency)
      const [res, portRes, progRes, orgRes] = await Promise.all([
        api.loadAllProjectsFull(orgId),
        api.listPortfolios(orgId),
        api.listPrograms(orgId),
        api.getOrgById(orgId),
      ]);
      if (res.error) {
        set({
          projects: res.data ?? [],
          portfolios: portRes.data ?? [],
          programs: progRes.data ?? [],
          status: 'error',
          errorMessage: res.error,
          activeOrg: orgRes.data ?? null,
        });
        toast.error(`Could not load workspace: ${res.error}`, 6000);
        return;
      }
      set({
        projects: res.data ?? [],
        portfolios: portRes.data ?? [],
        programs: progRes.data ?? [],
        status: 'idle',
        errorMessage: null,
        activeOrg: orgRes.data ?? null,
      });

      // Phase 20.5: subscribe to the new workspace
      subscribeToWorkspace(orgId, buildRealtimeHandlers());

      // Phase 23: clear stale, load new subscription
      // Phase 23.2: reset subscriptionLoaded while switching, then set it true
      // once the fetch resolves — whether or not a row came back.
      set({ subscription: null, subscriptionLoaded: false });
      const subRes = await getWorkspaceSubscription(orgId);
      set({ subscription: subRes.data ?? null, subscriptionLoaded: true });

      // Phase 30.20: clear + reload resource capacities for the new workspace.
      set({ resourceCapacities: {}, resourceCapacitiesLoaded: false });
      void get().reloadResourceCapacities();
      set({ resources: [], resourcesLoaded: false });
      void get().reloadResources();
      // Enterprise model R2: rate cards + role assignments feed the rate
      // resolver in the Cost/Budget/Estimator views. Tiny tables; empty in
      // demo (which runs enterprise mode) so cost figures still match Standard.
      void get().reloadResourceRoleAssignments();
      void get().reloadRateCards();
      void get().reloadResourceSkillLevels();
      void get().reloadDepartments();
      // Phase 30.23: clear + reload workspace phases for the new workspace.
      set({ workspacePhases: [], workspacePhasesLoaded: false });
      set({ projectSizeTiers: [], projectSizeTiersLoaded: false });
      void get().reloadWorkspacePhases();
      void get().reloadProjectSizeTiers();
        void get().reloadBoardColumns();
        void get().reloadWorkingCalendar();
        void get().reloadInsightSnoozes();
        void get().reloadWorkspacePolicy();
        void get().reloadHealthThresholds();
        void get().reloadResourceMode();
        void get().reloadGateTemplates();
        void get().reloadPipelineResourceNeeds();
        void get().reloadWorkItemTypes();
    },

    /**
     * Phase 20.3: optimistic update of the active workspace's currency.
     * Updates local state immediately so UI re-renders with the new currency,
     * then fires the API write in the background.
     */
    updateActiveOrgCurrency: (currency) => {
      const { activeOrg, mode } = get();
      if (!activeOrg) {
        toast.error('No active workspace.');
        return;
      }
      // Optimistic local update — every formatCurrency call site reading
      // useWorkspaceCurrency() will re-render with the new symbol
      set({ activeOrg: { ...activeOrg, currency } });
      if (mode === 'api') {
        fireApi(
          'Update workspace currency',
          api.updateOrgCurrency(activeOrg.id, currency).then((r) => ({ error: r.error })),
        );
      }
    },

    /** Phase 23: re-fetch the workspace subscription. */
    reloadSubscription: async () => {
      const { activeOrgId, mode } = get();
      if (mode !== 'api' || !activeOrgId) return;
      const res = await getWorkspaceSubscription(activeOrgId);
      // Phase 23.2: mark loaded regardless of whether a row exists.
      set({ subscription: res.data ?? null, subscriptionLoaded: true });
    },

    // ---------- Phase 30.20: Resource capacity ----------

    reloadResourceCapacities: async () => {
      const { activeOrgId, mode } = get();
      if (mode !== 'api' || !activeOrgId) {
        // Phase 30.20.2: localStorage mode — load from localStorage so values
        // persist across refresh. Source of truth in this mode is the browser.
        const local = loadStoredCapacities();
        set({ resourceCapacities: local, resourceCapacitiesLoaded: true });
        return;
      }
      const res = await api.listResourceCapacities(activeOrgId);
      if (res.error || !res.data) {
        // Don't toast — capacity is optional metadata; failing silently is OK.
        set({ resourceCapacitiesLoaded: true });
        return;
      }
      const map: CapacityMap = {};
      for (const r of res.data) {
        map[r.nameNormalized] = { hoursPerWeek: r.hoursPerWeek, displayName: r.displayName, notes: r.notes, hourlyRate: r.hourlyRate, isArchived: r.isArchived };
      }
      set({ resourceCapacities: map, resourceCapacitiesLoaded: true });
    },

    setResourceCapacity: async (nameNormalized, displayName, hoursPerWeek, hourlyRate = null, notes = null) => {
      // Preserve any existing archived state — editing hours must not reactivate.
      const wasArchived = get().resourceCapacities[nameNormalized]?.isArchived ?? false;
      // Optimistic local update first
      set((s) => ({
        resourceCapacities: {
          ...s.resourceCapacities,
          [nameNormalized]: { hoursPerWeek, displayName, notes, hourlyRate, isArchived: wasArchived },
        },
      }));
      const { activeOrgId, mode, resourceCapacities } = get();
      if (mode !== 'api' || !activeOrgId) {
        // Phase 30.20.2: persist to localStorage in non-API mode
        saveStoredCapacities(resourceCapacities);
        return;
      }
      const res = await api.upsertResourceCapacity(activeOrgId, {
        nameNormalized,
        displayName,
        hoursPerWeek,
        hourlyRate,
        isArchived: wasArchived,
        notes,
      });
      if (res.error) {
        toast.error(`Failed to save capacity for ${displayName}: ${res.error}`);
        // Reload to drop the optimistic write
        await get().reloadResourceCapacities();
      }
    },

    setResourceArchived: async (nameNormalized, displayName, archived) => {
      // Preserve existing capacity/rate/notes; only flip the archived flag.
      const existing = get().resourceCapacities[nameNormalized];
      const hoursPerWeek = existing?.hoursPerWeek ?? 40;
      const hourlyRate = existing?.hourlyRate ?? null;
      const notes = existing?.notes ?? null;
      set((s) => ({
        resourceCapacities: {
          ...s.resourceCapacities,
          [nameNormalized]: { hoursPerWeek, displayName, notes, hourlyRate, isArchived: archived },
        },
      }));
      const { activeOrgId, mode, resourceCapacities } = get();
      if (mode !== 'api' || !activeOrgId) {
        saveStoredCapacities(resourceCapacities);
        return;
      }
      const res = await api.upsertResourceCapacity(activeOrgId, {
        nameNormalized,
        displayName,
        hoursPerWeek,
        hourlyRate,
        isArchived: archived,
        notes,
      });
      if (res.error) {
        toast.error(`Failed to ${archived ? 'deactivate' : 'reactivate'} ${displayName}: ${res.error}`);
        await get().reloadResourceCapacities();
      }
    },

    clearResourceCapacity: async (nameNormalized) => {
      // Optimistic remove
      set((s) => {
        const next = { ...s.resourceCapacities };
        delete next[nameNormalized];
        return { resourceCapacities: next };
      });
      const { activeOrgId, mode, resourceCapacities } = get();
      if (mode !== 'api' || !activeOrgId) {
        // Phase 30.20.2: persist to localStorage in non-API mode
        saveStoredCapacities(resourceCapacities);
        return;
      }
      const res = await api.deleteResourceCapacity(activeOrgId, nameNormalized);
      if (res.error) {
        toast.error(`Failed to clear capacity: ${res.error}`);
        await get().reloadResourceCapacities();
      }
    },

    // ---------- Phase 30.23: Workspace lifecycle phases ----------

    reloadWorkspacePhases: async () => {
      const { activeOrgId, mode } = get();
      if (mode !== 'api' || !activeOrgId) {
        const local = loadStoredPhases();
        set({ workspacePhases: local, workspacePhasesLoaded: true });
        return;
      }
      const res = await api.listWorkspacePhases(activeOrgId);
      if (res.error || !res.data) {
        set({ workspacePhasesLoaded: true });
        return;
      }
      set({ workspacePhases: res.data, workspacePhasesLoaded: true });
    },

    createWorkspacePhase: async (name, description) => {
      const { activeOrgId, mode, workspacePhases } = get();
      const trimmedName = name.trim();
      if (!trimmedName) {
        toast.error('Phase name is required');
        return null;
      }
      if (trimmedName.length > 60) {
        toast.error('Phase name must be 60 characters or less');
        return null;
      }
      const nextSortOrder =
        workspacePhases.length > 0
          ? Math.max(...workspacePhases.map((p) => p.sortOrder)) + 10
          : 10;

      if (mode !== 'api' || !activeOrgId) {
        // localStorage mode: build a local phase, persist, return
        const phase: WorkspacePhase = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          name: trimmedName,
          description: description?.trim() || undefined,
          sortOrder: nextSortOrder,
          isArchived: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const next = [...workspacePhases, phase];
        set({ workspacePhases: next });
        saveStoredPhases(next);
        return phase;
      }

      const res = await api.createWorkspacePhase(activeOrgId, {
        name: trimmedName,
        description: description?.trim() || undefined,
        sortOrder: nextSortOrder,
      });
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create phase');
        return null;
      }
      set({ workspacePhases: [...workspacePhases, res.data].sort((a, b) => a.sortOrder - b.sortOrder) });
      return res.data;
    },

    updateWorkspacePhase: async (id, patch) => {
      const { mode, workspacePhases } = get();
      // Optimistic local update
      const next = workspacePhases.map((p) =>
        p.id === id
          ? {
              ...p,
              ...(patch.name !== undefined && { name: patch.name }),
              ...(patch.description !== undefined && { description: patch.description ?? undefined }),
              ...(patch.sortOrder !== undefined && { sortOrder: patch.sortOrder }),
              ...(patch.isArchived !== undefined && { isArchived: patch.isArchived }),
              updatedAt: new Date().toISOString(),
            }
          : p,
      );
      set({ workspacePhases: next });
      if (mode !== 'api') {
        saveStoredPhases(next);
        return;
      }
      const res = await api.updateWorkspacePhase(id, patch);
      if (res.error) {
        toast.error(`Failed to update phase: ${res.error}`);
        await get().reloadWorkspacePhases();
      }
    },

    deleteWorkspacePhase: async (id) => {
      const { mode, workspacePhases } = get();
      const next = workspacePhases.filter((p) => p.id !== id);
      set({ workspacePhases: next });
      if (mode !== 'api') {
        saveStoredPhases(next);
        return;
      }
      const res = await api.deleteWorkspacePhase(id);
      if (res.error) {
        toast.error(`Failed to delete phase: ${res.error}`);
        await get().reloadWorkspacePhases();
      }
    },

    // ---------- Migration 0054: Project size tiers (mirrors phases) ----------

    reloadProjectSizeTiers: async () => {
      const { activeOrgId, mode } = get();
      if (mode !== 'api' || !activeOrgId) {
        let local = loadStoredSizeTiers();
        // Seed Low/Medium/High on first load so demo/localStorage has options.
        if (local.length === 0) {
          local = DEFAULT_SIZE_TIER_NAMES.map(([name, sortOrder]) => ({
            id: genLocalId(),
            organizationId: activeOrgId ?? 'local',
            name,
            sortOrder,
            isArchived: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }));
          saveStoredSizeTiers(local);
        }
        set({ projectSizeTiers: local, projectSizeTiersLoaded: true });
        return;
      }
      const res = await api.listProjectSizeTiers(activeOrgId);
      if (res.error || !res.data) {
        set({ projectSizeTiersLoaded: true });
        return;
      }
      set({ projectSizeTiers: res.data, projectSizeTiersLoaded: true });
    },

    createProjectSizeTier: async (name, description) => {
      const { activeOrgId, mode, projectSizeTiers } = get();
      const trimmedName = name.trim();
      if (!trimmedName) {
        toast.error('Size name is required');
        return null;
      }
      if (trimmedName.length > 60) {
        toast.error('Size name must be 60 characters or less');
        return null;
      }
      const nextSortOrder =
        projectSizeTiers.length > 0
          ? Math.max(...projectSizeTiers.map((t) => t.sortOrder)) + 10
          : 10;

      if (mode !== 'api' || !activeOrgId) {
        const tier: ProjectSizeTier = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          name: trimmedName,
          description: description?.trim() || undefined,
          sortOrder: nextSortOrder,
          isArchived: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const next = [...projectSizeTiers, tier];
        set({ projectSizeTiers: next });
        saveStoredSizeTiers(next);
        return tier;
      }

      const res = await api.createProjectSizeTier(activeOrgId, {
        name: trimmedName,
        description: description?.trim() || undefined,
        sortOrder: nextSortOrder,
      });
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create size tier');
        return null;
      }
      set({ projectSizeTiers: [...projectSizeTiers, res.data].sort((a, b) => a.sortOrder - b.sortOrder) });
      return res.data;
    },

    updateProjectSizeTier: async (id, patch) => {
      const { mode, projectSizeTiers } = get();
      const next = projectSizeTiers.map((t) =>
        t.id === id
          ? {
              ...t,
              ...(patch.name !== undefined && { name: patch.name }),
              ...(patch.description !== undefined && { description: patch.description ?? undefined }),
              ...(patch.sortOrder !== undefined && { sortOrder: patch.sortOrder }),
              ...(patch.isArchived !== undefined && { isArchived: patch.isArchived }),
              updatedAt: new Date().toISOString(),
            }
          : t,
      );
      set({ projectSizeTiers: next });
      if (mode !== 'api') {
        saveStoredSizeTiers(next);
        return;
      }
      const res = await api.updateProjectSizeTier(id, patch);
      if (res.error) {
        toast.error(`Failed to update size tier: ${res.error}`);
        await get().reloadProjectSizeTiers();
      }
    },

    deleteProjectSizeTier: async (id) => {
      const { mode, projectSizeTiers } = get();
      const next = projectSizeTiers.filter((t) => t.id !== id);
      set({ projectSizeTiers: next });
      if (mode !== 'api') {
        saveStoredSizeTiers(next);
        return;
      }
      const res = await api.deleteProjectSizeTier(id);
      if (res.error) {
        toast.error(`Failed to delete size tier: ${res.error}`);
        await get().reloadProjectSizeTiers();
      }
    },

    seedDefaultSizeTiers: async () => {
      // Create Low/Medium/High only if the workspace has no tiers yet.
      if (get().projectSizeTiers.length > 0) return;
      for (const [name] of DEFAULT_SIZE_TIER_NAMES) {
        await get().createProjectSizeTier(name);
      }
    },

    // ---------- Phase 30.24: Time entries ----------

    reloadMyTimeEntries: async (filters) => {
      const { activeOrgId, mode } = get();
      if (mode !== 'api' || !activeOrgId) {
        // localStorage mode — load all entries and filter client-side
        const all = loadStoredTimeEntries();
        let filtered = all;
        if (filters?.startDate) {
          filtered = filtered.filter((e) => e.entryDate >= filters.startDate!);
        }
        if (filters?.endDate) {
          filtered = filtered.filter((e) => e.entryDate <= filters.endDate!);
        }
        // Sort by date desc, then created desc
        filtered.sort((a, b) => {
          if (a.entryDate !== b.entryDate) return b.entryDate.localeCompare(a.entryDate);
          return b.createdAt.localeCompare(a.createdAt);
        });
        set({ myTimeEntries: filtered, myTimeEntriesLoaded: true });
        return;
      }
      // API mode — fetch own entries with filters
      const { data: userData } = await supabase!.auth.getUser();
      if (!userData?.user) {
        set({ myTimeEntriesLoaded: true });
        return;
      }
      const res = await api.listTimeEntries(activeOrgId, {
        userId: userData.user.id,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      });
      if (res.error || !res.data) {
        set({ myTimeEntriesLoaded: true });
        return;
      }
      set({ myTimeEntries: res.data, myTimeEntriesLoaded: true });
    },

    createTimeEntry: async (entry) => {
      const { activeOrgId, mode, myTimeEntries } = get();
      if (mode !== 'api' || !activeOrgId) {
        // localStorage mode — fabricate a local entry
        const newEntry: TimeEntry = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          userId: 'local-user',
          projectId: entry.projectId,
          taskId: entry.taskId,
          taskNameSnapshot: entry.taskNameSnapshot,
          entryDate: entry.entryDate,
          hours: entry.hours,
          description: entry.description,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const all = loadStoredTimeEntries();
        const updated = [...all, newEntry];
        saveStoredTimeEntries(updated);
        set({ myTimeEntries: [newEntry, ...myTimeEntries] });
        // Refresh project actual hours
        void get().reloadProjectActualHours(entry.projectId);
        return newEntry;
      }
      const res = await api.createTimeEntry(activeOrgId, entry);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to log time');
        return null;
      }
      set({ myTimeEntries: [res.data, ...myTimeEntries] });
      // Refresh project actual hours (in API mode, re-fetch from DB)
      void get().reloadProjectActualHours(entry.projectId);
      return res.data;
    },

    updateTimeEntry: async (id, patch) => {
      const { mode, myTimeEntries } = get();
      // Find the entry first to know which project to refresh
      const existing = myTimeEntries.find((e) => e.id === id);
      // Optimistic local update
      const next = myTimeEntries.map((e) =>
        e.id === id
          ? {
              ...e,
              ...(patch.hours !== undefined && { hours: patch.hours }),
              ...(patch.description !== undefined && { description: patch.description ?? undefined }),
              ...(patch.entryDate !== undefined && { entryDate: patch.entryDate }),
              ...(patch.taskId !== undefined && { taskId: patch.taskId }),
              ...(patch.taskNameSnapshot !== undefined && { taskNameSnapshot: patch.taskNameSnapshot }),
              updatedAt: new Date().toISOString(),
            }
          : e,
      );
      set({ myTimeEntries: next });
      if (mode !== 'api') {
        // Persist locally
        const all = loadStoredTimeEntries();
        const updated = all.map((e) => (e.id === id ? next.find((n) => n.id === id) ?? e : e));
        saveStoredTimeEntries(updated);
        if (existing) void get().reloadProjectActualHours(existing.projectId);
        return;
      }
      const res = await api.updateTimeEntry(id, patch);
      if (res.error) {
        toast.error(`Failed to update entry: ${res.error}`);
        await get().reloadMyTimeEntries();
      } else if (existing) {
        void get().reloadProjectActualHours(existing.projectId);
      }
    },

    deleteTimeEntry: async (id) => {
      const { mode, myTimeEntries } = get();
      const entry = myTimeEntries.find((e) => e.id === id);
      set({ myTimeEntries: myTimeEntries.filter((e) => e.id !== id) });
      if (mode !== 'api') {
        const all = loadStoredTimeEntries().filter((e) => e.id !== id);
        saveStoredTimeEntries(all);
        if (entry) void get().reloadProjectActualHours(entry.projectId);
        return;
      }
      const res = await api.deleteTimeEntry(id);
      if (res.error) {
        toast.error(`Failed to delete entry: ${res.error}`);
        await get().reloadMyTimeEntries();
      } else if (entry) {
        void get().reloadProjectActualHours(entry.projectId);
      }
    },

    reloadProjectActualHours: async (projectId) => {
      const { mode, projectActualHoursByTask, projectActualHoursByUser } = get();
      if (mode !== 'api') {
        // Aggregate from localStorage (single-user context: all entries are the
        // current user's, so by-user costing isn't meaningful offline).
        const all = loadStoredTimeEntries().filter((e) => e.projectId === projectId);
        const m: Record<string, number> = {};
        for (const e of all) {
          if (!e.taskId) continue;
          m[e.taskId] = (m[e.taskId] ?? 0) + e.hours;
        }
        set({
          projectActualHoursByTask: { ...projectActualHoursByTask, [projectId]: m },
        });
        return;
      }
      const [byTask, byUser] = await Promise.all([
        api.getProjectActualHoursByTask(projectId),
        api.getProjectActualHoursByUser(projectId),
      ]);
      const patch: Partial<ProjectState> = {};
      if (!byTask.error && byTask.data) {
        patch.projectActualHoursByTask = { ...projectActualHoursByTask, [projectId]: byTask.data };
      }
      if (!byUser.error && byUser.data) {
        patch.projectActualHoursByUser = { ...projectActualHoursByUser, [projectId]: byUser.data };
      }
      if (Object.keys(patch).length) set(patch);
    },

    // ---------- Phase 30.27: Boards ----------

    reloadBoardColumns: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        // localStorage mode — load or fall back to defaults
        let cols = loadStored<BoardColumn>(BOARD_COLUMNS_STORAGE_KEY);
        if (cols.length === 0) {
          cols = defaultBoardColumns(activeOrgId ?? 'local');
          saveStored(BOARD_COLUMNS_STORAGE_KEY, cols);
        }
        set({ boardColumns: cols, boardColumnsLoaded: true });
        return;
      }
      const res = await api.listBoardColumns(activeOrgId);
      if (res.error || !res.data) {
        set({ boardColumnsLoaded: true });
        return;
      }
      set({ boardColumns: res.data, boardColumnsLoaded: true });
    },

    createBoardColumn: async (data) => {
      const { mode, activeOrgId, boardColumns } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const col: BoardColumn = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          name: data.name,
          color: data.color,
          sortOrder: (boardColumns.length > 0
            ? Math.max(...boardColumns.map((c) => c.sortOrder)) : 0) + 1,
          isTerminal: data.isTerminal ?? false,
          isArchived: false,
          createdAt: now,
          updatedAt: now,
        };
        const next = [...boardColumns, col];
        set({ boardColumns: next });
        saveStored(BOARD_COLUMNS_STORAGE_KEY, next);
        return col;
      }
      const res = await api.createBoardColumn(activeOrgId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create status');
        return null;
      }
      set({ boardColumns: [...boardColumns, res.data] });
      return res.data;
    },

    updateBoardColumn: async (id, patch) => {
      const { mode, boardColumns } = get();
      const next = boardColumns.map((c) =>
        c.id === id ? { ...c, ...patch, color: patch.color === null ? undefined : patch.color ?? c.color, updatedAt: new Date().toISOString() } : c,
      );
      set({ boardColumns: next });
      if (mode !== 'api') {
        saveStored(BOARD_COLUMNS_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateBoardColumn(id, patch);
      if (res.error) {
        toast.error(`Failed to update status: ${res.error}`);
        await get().reloadBoardColumns();
      }
    },

    deleteBoardColumn: async (id) => {
      const { mode, boardColumns } = get();
      const next = boardColumns.filter((c) => c.id !== id);
      set({ boardColumns: next });
      if (mode !== 'api') {
        saveStored(BOARD_COLUMNS_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteBoardColumn(id);
      if (res.error) {
        toast.error(`Failed to delete status: ${res.error}`);
        await get().reloadBoardColumns();
      }
    },

    reloadWorkItemTypes: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        let types = loadStored<WorkItemType>(WORK_ITEM_TYPES_STORAGE_KEY);
        if (types.length === 0) {
          types = defaultWorkItemTypes(activeOrgId ?? 'local');
          saveStored(WORK_ITEM_TYPES_STORAGE_KEY, types);
        }
        set({ workItemTypes: types, workItemTypesLoaded: true });
        return;
      }
      const res = await api.listWorkItemTypes(activeOrgId);
      if (res.error || !res.data) {
        set({ workItemTypesLoaded: true });
        return;
      }
      set({ workItemTypes: res.data, workItemTypesLoaded: true });
    },

    createWorkItemType: async (data) => {
      const { mode, activeOrgId, workItemTypes } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const t: WorkItemType = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          name: data.name,
          icon: data.icon,
          color: data.color,
          sortOrder: (workItemTypes.length > 0
            ? Math.max(...workItemTypes.map((x) => x.sortOrder)) : 0) + 1,
          isArchived: false,
          createdAt: now,
          updatedAt: now,
        };
        const next = [...workItemTypes, t];
        set({ workItemTypes: next });
        saveStored(WORK_ITEM_TYPES_STORAGE_KEY, next);
        return t;
      }
      const res = await api.createWorkItemType(activeOrgId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create type');
        return null;
      }
      set({ workItemTypes: [...workItemTypes, res.data] });
      return res.data;
    },

    updateWorkItemType: async (id, patch) => {
      const { mode, workItemTypes } = get();
      const next = workItemTypes.map((t) =>
        t.id === id
          ? {
              ...t,
              ...patch,
              icon: patch.icon === null ? undefined : patch.icon ?? t.icon,
              color: patch.color === null ? undefined : patch.color ?? t.color,
              updatedAt: new Date().toISOString(),
            }
          : t,
      );
      set({ workItemTypes: next });
      if (mode !== 'api') {
        saveStored(WORK_ITEM_TYPES_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateWorkItemType(id, patch);
      if (res.error) {
        toast.error(`Failed to update type: ${res.error}`);
        await get().reloadWorkItemTypes();
      }
    },

    deleteWorkItemType: async (id) => {
      const { mode, workItemTypes } = get();
      const next = workItemTypes.filter((t) => t.id !== id);
      set({ workItemTypes: next });
      if (mode !== 'api') {
        saveStored(WORK_ITEM_TYPES_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteWorkItemType(id);
      if (res.error) {
        toast.error(`Failed to delete type: ${res.error}`);
        await get().reloadWorkItemTypes();
      }
    },

    reloadBoards: async (projectId) => {
      const { mode, boardsByProject } = get();
      if (mode !== 'api') {
        const all = loadStored<Board>(BOARDS_STORAGE_KEY);
        const forProject = all
          .filter((b) => b.projectId === projectId && !b.isArchived)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        set({
          boardsByProject: { ...boardsByProject, [projectId]: forProject },
        });
        return;
      }
      const res = await api.listBoards(projectId);
      if (res.error || !res.data) return;
      set({
        boardsByProject: { ...boardsByProject, [projectId]: res.data },
      });
    },

    createBoard: async (projectId, name, description) => {
      const { mode, activeOrgId, boardsByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const existing = boardsByProject[projectId] ?? [];
        const board: Board = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          projectId,
          name,
          description,
          sortOrder: (existing.length > 0
            ? Math.max(...existing.map((b) => b.sortOrder)) : 0) + 1,
          isArchived: false,
          createdAt: now,
          updatedAt: now,
        };
        const nextForProject = [...existing, board];
        set({
          boardsByProject: { ...boardsByProject, [projectId]: nextForProject },
        });
        const all = loadStored<Board>(BOARDS_STORAGE_KEY);
        saveStored(BOARDS_STORAGE_KEY, [...all, board]);
        return board;
      }
      const res = await api.createBoard(activeOrgId, projectId, name, description);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create board');
        return null;
      }
      const existing = boardsByProject[projectId] ?? [];
      set({
        boardsByProject: {
          ...boardsByProject,
          [projectId]: [...existing, res.data],
        },
      });
      return res.data;
    },

    updateBoard: async (id, patch) => {
      const { mode, boardsByProject } = get();
      const nextMap: Record<string, Board[]> = {};
      let updatedBoard: Board | null = null;
      for (const [pid, boards] of Object.entries(boardsByProject)) {
        nextMap[pid] = boards.map((b) => {
          if (b.id === id) {
            const updated = {
              ...b,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? b.description,
              updatedAt: new Date().toISOString(),
            };
            updatedBoard = updated;
            return updated;
          }
          return b;
        });
      }
      set({ boardsByProject: nextMap });
      if (mode !== 'api') {
        const all = loadStored<Board>(BOARDS_STORAGE_KEY);
        saveStored(
          BOARDS_STORAGE_KEY,
          all.map((b) => (b.id === id && updatedBoard ? updatedBoard : b)),
        );
        return;
      }
      const res = await api.updateBoard(id, patch);
      if (res.error) {
        toast.error(`Failed to update board: ${res.error}`);
      }
    },

    deleteBoard: async (id) => {
      const { mode, boardsByProject, workItemsByBoard } = get();
      const nextMap: Record<string, Board[]> = {};
      for (const [pid, boards] of Object.entries(boardsByProject)) {
        nextMap[pid] = boards.filter((b) => b.id !== id);
      }
      // Also purge workItemsByBoard entry for this board
      const nextWorkItems = { ...workItemsByBoard };
      delete nextWorkItems[id];
      set({ boardsByProject: nextMap, workItemsByBoard: nextWorkItems });
      if (mode !== 'api') {
        const all = loadStored<Board>(BOARDS_STORAGE_KEY);
        saveStored(BOARDS_STORAGE_KEY, all.filter((b) => b.id !== id));
        const allItems = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(WORK_ITEMS_STORAGE_KEY, allItems.filter((w) => w.boardId !== id));
        return;
      }
      const res = await api.deleteBoard(id);
      if (res.error) {
        toast.error(`Failed to delete board: ${res.error}`);
      }
    },

    reloadWorkItems: async (boardId) => {
      const { mode, workItemsByBoard } = get();
      if (mode !== 'api') {
        const all = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        const forBoard = all
          .filter((w) => w.boardId === boardId)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        set({
          workItemsByBoard: { ...workItemsByBoard, [boardId]: forBoard },
        });
        return;
      }
      const res = await api.listWorkItems(boardId);
      if (res.error || !res.data) return;
      set({
        workItemsByBoard: { ...workItemsByBoard, [boardId]: res.data },
      });
    },

    reloadProjectWorkItems: async (projectId) => {
      const { mode, workItemsByProject } = get();
      if (mode !== 'api') {
        // localStorage mode — aggregate all items by projectId
        const all = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        const forProject = all.filter((w) => w.projectId === projectId);
        set({
          workItemsByProject: { ...workItemsByProject, [projectId]: forProject },
        });
        return;
      }
      const res = await api.listWorkItemsByProject(projectId);
      if (res.error || !res.data) return;
      set({
        workItemsByProject: { ...workItemsByProject, [projectId]: res.data },
      });
    },

    createWorkItem: async (boardId, projectId, data) => {
      const { mode, activeOrgId, workItemsByBoard, workItemsByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const existing = workItemsByBoard[boardId] ?? [];
        const item: WorkItem = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          boardId,
          projectId,
          taskId: data.taskId ?? null,
          sprintId: data.sprintId ?? null,
          title: data.title,
          description: data.description,
          typeId: data.typeId ?? null,
          statusId: data.statusId ?? null,
          assigneeUserId: data.assigneeUserId ?? null,
          dueDate: data.dueDate ?? null,
          storyPoints: data.storyPoints ?? null,
          sortOrder: (existing.length > 0
            ? Math.max(...existing.map((w) => w.sortOrder)) : 0) + 1,
          createdAt: now,
          updatedAt: now,
        };
        const existingProject = workItemsByProject[projectId] ?? [];
        set({
          workItemsByBoard: { ...workItemsByBoard, [boardId]: [...existing, item] },
          workItemsByProject: { ...workItemsByProject, [projectId]: [...existingProject, item] },
        });
        const all = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(WORK_ITEMS_STORAGE_KEY, [...all, item]);
        return item;
      }
      const res = await api.createWorkItem(activeOrgId, boardId, projectId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create work item');
        return null;
      }
      const existing = workItemsByBoard[boardId] ?? [];
      set({
        workItemsByBoard: {
          ...workItemsByBoard,
          [boardId]: [...existing, res.data],
        },
      });
      // Phase 30.28: refresh project-scope cache so ScheduleTab badges update
      void get().reloadProjectWorkItems(projectId);
      // Phase 30.30: snapshot the sprint if the new item is in one
      if (res.data.sprintId) {
        void get().snapshotSprintNow(res.data.sprintId);
      }
      return res.data;
    },

    updateWorkItem: async (id, patch) => {
      const { mode, workItemsByBoard, workItemsByProject } = get();
      const nextMap: Record<string, WorkItem[]> = {};
      let updatedItem: WorkItem | null = null;
      // Phase 30.30: track the pre-mutation sprintId so we can snapshot
      // both the source (item leaving) and target (item joining) sprints
      let priorSprintId: string | null = null;
      for (const [bid, items] of Object.entries(workItemsByBoard)) {
        nextMap[bid] = items.map((w) => {
          if (w.id === id) {
            priorSprintId = w.sprintId;
            const updated: WorkItem = {
              ...w,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? w.description,
              updatedAt: new Date().toISOString(),
            };
            updatedItem = updated;
            return updated;
          }
          return w;
        });
      }
      // Also update projectWorkItems cache in place — status/taskId changes
      // must propagate immediately for the badge to reflect them.
      const nextProjectMap: Record<string, WorkItem[]> = {};
      for (const [pid, items] of Object.entries(workItemsByProject)) {
        nextProjectMap[pid] = items.map((w) => {
          if (w.id === id) {
            const updated: WorkItem = {
              ...w,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? w.description,
              updatedAt: new Date().toISOString(),
            };
            return updated;
          }
          return w;
        });
      }
      set({ workItemsByBoard: nextMap, workItemsByProject: nextProjectMap });
      if (mode !== 'api') {
        const all = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(
          WORK_ITEMS_STORAGE_KEY,
          all.map((w) => (w.id === id && updatedItem ? updatedItem : w)),
        );
      } else {
        const res = await api.updateWorkItem(id, patch);
        if (res.error) {
          toast.error(`Failed to update work item: ${res.error}`);
        }
      }
      // Phase 30.30: snapshot affected sprints. If status changed or item
      // moved sprints, both source and target need to record the new state.
      const newSprintId = (updatedItem as WorkItem | null)?.sprintId ?? null;
      const affected = new Set<string>();
      if (priorSprintId) affected.add(priorSprintId);
      if (newSprintId) affected.add(newSprintId);
      for (const sid of affected) {
        void get().snapshotSprintNow(sid);
      }
    },

    deleteWorkItem: async (id) => {
      const { mode, workItemsByBoard, workItemsByProject } = get();
      const nextMap: Record<string, WorkItem[]> = {};
      for (const [bid, items] of Object.entries(workItemsByBoard)) {
        nextMap[bid] = items.filter((w) => w.id !== id);
      }
      const nextProjectMap: Record<string, WorkItem[]> = {};
      for (const [pid, items] of Object.entries(workItemsByProject)) {
        nextProjectMap[pid] = items.filter((w) => w.id !== id);
      }
      set({ workItemsByBoard: nextMap, workItemsByProject: nextProjectMap });
      if (mode !== 'api') {
        const all = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(WORK_ITEMS_STORAGE_KEY, all.filter((w) => w.id !== id));
        return;
      }
      const res = await api.deleteWorkItem(id);
      if (res.error) {
        toast.error(`Failed to delete work item: ${res.error}`);
      }
    },

    // ---------- Phase 30.29: Sprints ----------

    reloadSprints: async (boardId) => {
      const { mode, sprintsByBoard } = get();
      if (mode !== 'api') {
        const all = loadStored<Sprint>(SPRINTS_STORAGE_KEY);
        const forBoard = all
          .filter((s) => s.boardId === boardId)
          .sort((a, b) => {
            // Newest first, unstarted (no dates) at bottom
            if (a.startDate && b.startDate) return b.startDate.localeCompare(a.startDate);
            if (a.startDate) return -1;
            if (b.startDate) return 1;
            return b.createdAt.localeCompare(a.createdAt);
          });
        set({
          sprintsByBoard: { ...sprintsByBoard, [boardId]: forBoard },
        });
        return;
      }
      const res = await api.listSprints(boardId);
      if (res.error || !res.data) return;
      set({
        sprintsByBoard: { ...sprintsByBoard, [boardId]: res.data },
      });
    },

    createSprint: async (boardId, data) => {
      const { mode, activeOrgId, sprintsByBoard } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const sprint: Sprint = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          boardId,
          name: data.name,
          goal: data.goal,
          startDate: data.startDate ?? null,
          endDate: data.endDate ?? null,
          state: data.state ?? 'planning',
          completedAt: null,
          createdAt: now,
          updatedAt: now,
        };
        const existing = sprintsByBoard[boardId] ?? [];
        set({
          sprintsByBoard: { ...sprintsByBoard, [boardId]: [sprint, ...existing] },
        });
        const all = loadStored<Sprint>(SPRINTS_STORAGE_KEY);
        saveStored(SPRINTS_STORAGE_KEY, [...all, sprint]);
        return sprint;
      }
      const res = await api.createSprint(activeOrgId, boardId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create sprint');
        return null;
      }
      const existing = sprintsByBoard[boardId] ?? [];
      set({
        sprintsByBoard: {
          ...sprintsByBoard,
          [boardId]: [res.data, ...existing],
        },
      });
      return res.data;
    },

    updateSprint: async (id, patch) => {
      const { mode, sprintsByBoard } = get();
      const nextMap: Record<string, Sprint[]> = {};
      let updated: Sprint | null = null;
      for (const [bid, sprints] of Object.entries(sprintsByBoard)) {
        nextMap[bid] = sprints.map((s) => {
          if (s.id === id) {
            const patched: Sprint = {
              ...s,
              ...patch,
              goal: patch.goal === null ? undefined : patch.goal ?? s.goal,
              completedAt: patch.state === 'completed' ? new Date().toISOString() : s.completedAt,
              updatedAt: new Date().toISOString(),
            };
            updated = patched;
            return patched;
          }
          return s;
        });
      }
      set({ sprintsByBoard: nextMap });
      if (mode !== 'api') {
        const all = loadStored<Sprint>(SPRINTS_STORAGE_KEY);
        saveStored(SPRINTS_STORAGE_KEY, all.map((s) => (s.id === id && updated ? updated : s)));
      } else {
        const res = await api.updateSprint(id, patch);
        if (res.error) {
          toast.error(`Failed to update sprint: ${res.error}`);
        }
      }
      // Phase 30.30: on transition to active (sprint start), snapshot the
      // initial committed state — this becomes day-0 of the burndown.
      // On transition to completed, snapshot the final state.
      if (patch.state === 'active' || patch.state === 'completed') {
        void get().snapshotSprintNow(id);
      }
    },

    deleteSprint: async (id) => {
      const { mode, sprintsByBoard, workItemsByBoard, workItemsByProject } = get();
      const nextMap: Record<string, Sprint[]> = {};
      for (const [bid, sprints] of Object.entries(sprintsByBoard)) {
        nextMap[bid] = sprints.filter((s) => s.id !== id);
      }
      // Migration 0029: on sprint delete, items with sprint_id = id have
      // sprint_id set to null by the DB. Mirror that in the local caches
      // so the backlog view immediately reflects the returned items.
      const nextItemsByBoard: Record<string, WorkItem[]> = {};
      for (const [bid, items] of Object.entries(workItemsByBoard)) {
        nextItemsByBoard[bid] = items.map((w) => w.sprintId === id ? { ...w, sprintId: null } : w);
      }
      const nextItemsByProject: Record<string, WorkItem[]> = {};
      for (const [pid, items] of Object.entries(workItemsByProject)) {
        nextItemsByProject[pid] = items.map((w) => w.sprintId === id ? { ...w, sprintId: null } : w);
      }
      set({
        sprintsByBoard: nextMap,
        workItemsByBoard: nextItemsByBoard,
        workItemsByProject: nextItemsByProject,
      });
      if (mode !== 'api') {
        const allSprints = loadStored<Sprint>(SPRINTS_STORAGE_KEY);
        saveStored(SPRINTS_STORAGE_KEY, allSprints.filter((s) => s.id !== id));
        const allItems = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(WORK_ITEMS_STORAGE_KEY, allItems.map((w) => w.sprintId === id ? { ...w, sprintId: null } : w));
        return;
      }
      const res = await api.deleteSprint(id);
      if (res.error) {
        toast.error(`Failed to delete sprint: ${res.error}`);
      }
    },

    completeSprint: async (sprintId, targetSprintId) => {
      const { mode, sprintsByBoard, workItemsByBoard, workItemsByProject, boardColumns } = get();
      const terminalIds = boardColumns.filter((c) => c.isTerminal).map((c) => c.id);
      const terminal = new Set(terminalIds);

      // Compute moved count from current cache before we mutate
      let moved = 0;
      const nextItemsByBoard: Record<string, WorkItem[]> = {};
      for (const [bid, items] of Object.entries(workItemsByBoard)) {
        nextItemsByBoard[bid] = items.map((w) => {
          if (w.sprintId === sprintId && (!w.statusId || !terminal.has(w.statusId))) {
            moved += 1;
            return { ...w, sprintId: targetSprintId };
          }
          return w;
        });
      }
      const nextItemsByProject: Record<string, WorkItem[]> = {};
      for (const [pid, items] of Object.entries(workItemsByProject)) {
        nextItemsByProject[pid] = items.map((w) => {
          if (w.sprintId === sprintId && (!w.statusId || !terminal.has(w.statusId))) {
            return { ...w, sprintId: targetSprintId };
          }
          return w;
        });
      }
      // Transition sprint to completed
      const nextSprints: Record<string, Sprint[]> = {};
      for (const [bid, sprints] of Object.entries(sprintsByBoard)) {
        nextSprints[bid] = sprints.map((s) =>
          s.id === sprintId
            ? { ...s, state: 'completed' as const, completedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
            : s,
        );
      }
      set({
        sprintsByBoard: nextSprints,
        workItemsByBoard: nextItemsByBoard,
        workItemsByProject: nextItemsByProject,
      });

      if (mode !== 'api') {
        const allSprints = loadStored<Sprint>(SPRINTS_STORAGE_KEY);
        saveStored(
          SPRINTS_STORAGE_KEY,
          allSprints.map((s) => s.id === sprintId
            ? { ...s, state: 'completed', completedAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
            : s),
        );
        const allItems = loadStored<WorkItem>(WORK_ITEMS_STORAGE_KEY);
        saveStored(
          WORK_ITEMS_STORAGE_KEY,
          allItems.map((w) => {
            if (w.sprintId === sprintId && (!w.statusId || !terminal.has(w.statusId))) {
              return { ...w, sprintId: targetSprintId };
            }
            return w;
          }),
        );
        return { moved };
      }

      // API mode: two operations
      const moveRes = await api.moveIncompleteSprintItems(sprintId, terminalIds, targetSprintId);
      if (moveRes.error) {
        toast.error(`Move failed: ${moveRes.error}`);
        return null;
      }
      const stateRes = await api.updateSprint(sprintId, { state: 'completed' });
      if (stateRes.error) {
        toast.error(`Sprint state update failed: ${stateRes.error}`);
        return null;
      }
      return { moved: moveRes.data ?? moved };
    },

    // ---------- Phase 30.30: Burndown snapshots ----------

    reloadSprintSnapshots: async (sprintId) => {
      const { mode, snapshotsBySprint } = get();
      if (mode !== 'api') {
        const all = loadStored<SprintBurndownSnapshot>(SNAPSHOTS_STORAGE_KEY);
        const forSprint = all
          .filter((s) => s.sprintId === sprintId)
          .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));
        set({
          snapshotsBySprint: { ...snapshotsBySprint, [sprintId]: forSprint },
        });
        return;
      }
      const res = await api.listSprintSnapshots(sprintId);
      if (res.error || !res.data) return;
      set({
        snapshotsBySprint: { ...snapshotsBySprint, [sprintId]: res.data },
      });
    },

    reloadSprintSnapshotsBatch: async (sprintIds) => {
      const { mode, snapshotsBySprint } = get();
      if (sprintIds.length === 0) return;
      if (mode !== 'api') {
        const all = loadStored<SprintBurndownSnapshot>(SNAPSHOTS_STORAGE_KEY);
        const next = { ...snapshotsBySprint };
        for (const sid of sprintIds) {
          next[sid] = all
            .filter((s) => s.sprintId === sid)
            .sort((a, b) => a.snapshotDate.localeCompare(b.snapshotDate));
        }
        set({ snapshotsBySprint: next });
        return;
      }
      const res = await api.listSprintSnapshotsBatch(sprintIds);
      if (res.error || !res.data) return;
      const next = { ...snapshotsBySprint };
      for (const sid of sprintIds) {
        next[sid] = res.data[sid] ?? [];
      }
      set({ snapshotsBySprint: next });
    },

    snapshotSprintNow: async (sprintId) => {
      const {
        mode,
        sprintsByBoard,
        workItemsByBoard,
        boardColumns,
        snapshotsBySprint,
      } = get();
      // Find the sprint (search all boards' arrays)
      let sprint: Sprint | undefined;
      for (const sprints of Object.values(sprintsByBoard)) {
        sprint = sprints.find((s) => s.id === sprintId);
        if (sprint) break;
      }
      if (!sprint) return;
      // Only snapshot for active sprints (or on the completion event, which
      // updates state first, then calls this — but that's handled in
      // completeSprint separately if needed). Skip 'planning' — the sprint
      // hasn't started; a burndown chart would be meaningless.
      if (sprint.state === 'planning') return;

      // Compute metrics from the sprint's items in the local cache
      const boardItems = workItemsByBoard[sprint.boardId] ?? [];
      const items = boardItems.filter((w) => w.sprintId === sprintId);
      const terminal = new Set(boardColumns.filter((c) => c.isTerminal).map((c) => c.id));
      let doneItems = 0;
      let totalPoints = 0;
      let donePoints = 0;
      for (const w of items) {
        const pts = w.storyPoints ?? 0;
        totalPoints += pts;
        if (w.statusId && terminal.has(w.statusId)) {
          doneItems += 1;
          donePoints += pts;
        }
      }
      const metrics = {
        totalItems: items.length,
        doneItems,
        totalPoints,
        remainingPoints: Math.max(0, totalPoints - donePoints),
      };

      const today = new Date().toISOString().split('T')[0];

      // Update local cache: upsert on (sprintId, today)
      const existing = snapshotsBySprint[sprintId] ?? [];
      const sameDayIdx = existing.findIndex((s) => s.snapshotDate === today);
      const now = new Date().toISOString();
      let nextForSprint: SprintBurndownSnapshot[];
      if (sameDayIdx >= 0) {
        nextForSprint = existing.map((s, i) =>
          i === sameDayIdx
            ? { ...s, ...metrics, updatedAt: now }
            : s,
        );
      } else {
        const newSnap: SprintBurndownSnapshot = {
          id: genLocalId(),
          sprintId,
          snapshotDate: today,
          ...metrics,
          createdAt: now,
          updatedAt: now,
        };
        nextForSprint = [...existing, newSnap];
      }
      set({
        snapshotsBySprint: { ...snapshotsBySprint, [sprintId]: nextForSprint },
      });

      if (mode !== 'api') {
        // Mirror to localStorage
        const all = loadStored<SprintBurndownSnapshot>(SNAPSHOTS_STORAGE_KEY);
        const withoutToday = all.filter((s) => !(s.sprintId === sprintId && s.snapshotDate === today));
        const currentToday = nextForSprint.find((s) => s.snapshotDate === today);
        if (currentToday) {
          saveStored(SNAPSHOTS_STORAGE_KEY, [...withoutToday, currentToday]);
        }
        return;
      }

      // API mode: upsert to DB
      const res = await api.upsertSprintSnapshot(sprintId, metrics);
      if (res.error) {
        // Silent — snapshots are best-effort; don't disrupt the primary action
        console.warn('Sprint snapshot upsert failed:', res.error);
      }
    },

    // ---------- Phase 30.33: Working calendar ----------

    reloadWorkingCalendar: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        // localStorage mode: load from storage or default
        const stored = loadStored<WorkingCalendar>(CALENDAR_STORAGE_KEY);
        const calendar = stored[0] ?? {
          id: 'local-cal',
          organizationId: activeOrgId ?? 'local',
          name: 'Default Calendar',
          workingDaysBitmask: 62,
          isDefault: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const exceptions = loadStored<WorkingCalendarException>(CALENDAR_EXCEPTIONS_STORAGE_KEY);
        set({
          workingCalendar: calendar,
          workingCalendarExceptions: exceptions,
          workingCalendarLoaded: true,
        });
        return;
      }
      const calRes = await api.getDefaultCalendar(activeOrgId);
      if (calRes.error || !calRes.data) {
        set({ workingCalendarLoaded: true });
        return;
      }
      const excRes = await api.listCalendarExceptions(calRes.data.id);
      set({
        workingCalendar: calRes.data,
        workingCalendarExceptions: excRes.data ?? [],
        workingCalendarLoaded: true,
      });
    },

    updateWorkingCalendarBitmask: async (bitmask) => {
      const { mode, workingCalendar } = get();
      if (!workingCalendar) return;
      const updated = { ...workingCalendar, workingDaysBitmask: bitmask, updatedAt: new Date().toISOString() };
      set({ workingCalendar: updated });
      if (mode !== 'api') {
        saveStored(CALENDAR_STORAGE_KEY, [updated]);
        return;
      }
      const res = await api.updateWorkingCalendar(workingCalendar.id, { workingDaysBitmask: bitmask });
      if (res.error) toast.error(`Calendar update failed: ${res.error}`);
    },

    createCalendarException: async (data) => {
      const { mode, activeOrgId, workingCalendar, workingCalendarExceptions } = get();
      if (!workingCalendar || !activeOrgId) {
        toast.error('No calendar loaded');
        return null;
      }
      if (mode !== 'api') {
        const now = new Date().toISOString();
        const ex: WorkingCalendarException = {
          id: genLocalId(),
          calendarId: workingCalendar.id,
          organizationId: activeOrgId,
          name: data.name,
          description: data.description,
          dateFrom: data.dateFrom,
          dateTo: data.dateTo,
          exceptionType: data.exceptionType,
          isWorking: data.isWorking,
          createdAt: now,
          updatedAt: now,
        };
        const next = [...workingCalendarExceptions, ex];
        set({ workingCalendarExceptions: next });
        saveStored(CALENDAR_EXCEPTIONS_STORAGE_KEY, next);
        return ex;
      }
      const res = await api.createCalendarException(activeOrgId, workingCalendar.id, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create exception');
        return null;
      }
      set({ workingCalendarExceptions: [...workingCalendarExceptions, res.data] });
      return res.data;
    },

    updateCalendarException: async (id, patch) => {
      const { mode, workingCalendarExceptions } = get();
      const next = workingCalendarExceptions.map((e) =>
        e.id === id
          ? {
              ...e,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? e.description,
              updatedAt: new Date().toISOString(),
            }
          : e,
      );
      set({ workingCalendarExceptions: next });
      if (mode !== 'api') {
        saveStored(CALENDAR_EXCEPTIONS_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateCalendarException(id, patch);
      if (res.error) toast.error(`Failed to update exception: ${res.error}`);
    },

    deleteCalendarException: async (id) => {
      const { mode, workingCalendarExceptions } = get();
      const next = workingCalendarExceptions.filter((e) => e.id !== id);
      set({ workingCalendarExceptions: next });
      if (mode !== 'api') {
        saveStored(CALENDAR_EXCEPTIONS_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteCalendarException(id);
      if (res.error) toast.error(`Failed to delete exception: ${res.error}`);
    },

    // ---------- Phase 30.35: Insight snoozes ----------

    reloadInsightSnoozes: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const stored = loadStored<InsightSnooze>(INSIGHT_SNOOZES_STORAGE_KEY);
        set({ insightSnoozes: stored, insightSnoozesLoaded: true });
        return;
      }
      const res = await api.listInsightSnoozes(activeOrgId);
      if (res.error) {
        set({ insightSnoozesLoaded: true });
        return;
      }
      set({ insightSnoozes: res.data ?? [], insightSnoozesLoaded: true });
    },

    snoozeInsight: async (projectId, ruleId, targetKind, targetId, days = 7) => {
      const { mode, activeOrgId, insightSnoozes } = get();
      const until = new Date();
      until.setDate(until.getDate() + days);
      const snoozedUntil = until.toISOString();

      if (mode !== 'api' || !activeOrgId) {
        // localStorage: upsert by (ruleId, targetKind, targetId, projectId)
        const others = insightSnoozes.filter(
          (s) => !(s.ruleId === ruleId && s.targetKind === targetKind && s.targetId === targetId && s.projectId === projectId),
        );
        const snooze: InsightSnooze = {
          id: genLocalId(),
          userId: 'local',
          organizationId: activeOrgId ?? 'local',
          projectId,
          ruleId,
          targetKind,
          targetId,
          snoozedUntil,
          createdAt: new Date().toISOString(),
        };
        const next = [...others, snooze];
        set({ insightSnoozes: next });
        saveStored(INSIGHT_SNOOZES_STORAGE_KEY, next);
        return;
      }
      const res = await api.upsertInsightSnooze(activeOrgId, projectId, ruleId, targetKind, targetId, snoozedUntil);
      if (res.error || !res.data) {
        toast.error(`Snooze failed: ${res.error}`);
        return;
      }
      // Replace-or-append in local state
      const others = insightSnoozes.filter((s) => s.id !== res.data!.id);
      set({ insightSnoozes: [...others, res.data] });
    },

    unsnoozeInsight: async (snoozeId) => {
      const { mode, insightSnoozes } = get();
      const next = insightSnoozes.filter((s) => s.id !== snoozeId);
      set({ insightSnoozes: next });
      if (mode !== 'api') {
        saveStored(INSIGHT_SNOOZES_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteInsightSnooze(snoozeId);
      if (res.error) toast.error(`Un-snooze failed: ${res.error}`);
    },

    setInsightPopupsEnabled: (enabled) => {
      set({ insightPopupsEnabled: enabled });
      try {
        window.localStorage.setItem(INSIGHT_POPUPS_STORAGE_KEY, enabled ? '1' : '0');
      } catch {}
    },

    // ---------- Phase 31: Requirements + test cases ----------

    reloadRequirements: async (projectId) => {
      const { mode } = get();
      if (mode !== 'api') {
        // localStorage: load all, filter by project
        const allReqs = loadStored<Requirement>(REQUIREMENTS_STORAGE_KEY);
        const allTcs = loadStored<RequirementTestCase>(TEST_CASES_STORAGE_KEY);
        const reqs = allReqs.filter((r) => r.projectId === projectId);
        const reqIds = new Set(reqs.map((r) => r.id));
        const tcs = allTcs.filter((tc) => reqIds.has(tc.requirementId));
        set((s) => ({
          requirementsByProject: { ...s.requirementsByProject, [projectId]: reqs },
          testCasesByProject: { ...s.testCasesByProject, [projectId]: tcs },
          requirementsLoadedForProject: { ...s.requirementsLoadedForProject, [projectId]: true },
        }));
        return;
      }
      const [reqRes, tcRes] = await Promise.all([
        api.listRequirements(projectId),
        api.listTestCases(projectId),
      ]);
      set((s) => ({
        requirementsByProject: { ...s.requirementsByProject, [projectId]: reqRes.data ?? [] },
        testCasesByProject: { ...s.testCasesByProject, [projectId]: tcRes.data ?? [] },
        requirementsLoadedForProject: { ...s.requirementsLoadedForProject, [projectId]: true },
      }));
    },

    createRequirement: async (projectId, data) => {
      const { mode, activeOrgId, requirementsByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const req: Requirement = {
          id: genLocalId(),
          projectId,
          organizationId: activeOrgId ?? 'local',
          reqCode: data.reqCode,
          title: data.title,
          description: data.description,
          category: data.category,
          status: data.status ?? 'draft',
          priority: data.priority ?? 'medium',
          source: data.source,
          createdAt: now,
          updatedAt: now,
        };
        const existing = requirementsByProject[projectId] ?? [];
        const next = [...existing, req];
        set((s) => ({
          requirementsByProject: { ...s.requirementsByProject, [projectId]: next },
        }));
        // Persist all requirements across projects to storage
        const allReqs = loadStored<Requirement>(REQUIREMENTS_STORAGE_KEY);
        saveStored(REQUIREMENTS_STORAGE_KEY, [...allReqs.filter((r) => r.projectId !== projectId), ...next]);
        return req;
      }
      const res = await api.createRequirement(activeOrgId, projectId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create requirement');
        return null;
      }
      const existing = requirementsByProject[projectId] ?? [];
      set((s) => ({
        requirementsByProject: { ...s.requirementsByProject, [projectId]: [...existing, res.data!] },
      }));
      return res.data;
    },

    updateRequirement: async (id, patch) => {
      const { mode, requirementsByProject } = get();
      // Find which project this requirement belongs to
      let owningProjectId: string | null = null;
      for (const [pid, reqs] of Object.entries(requirementsByProject)) {
        if (reqs.some((r) => r.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const now = new Date().toISOString();
      const next = requirementsByProject[owningProjectId].map((r) =>
        r.id === id
          ? {
              ...r,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? r.description,
              source: patch.source === null ? undefined : patch.source ?? r.source,
              updatedAt: now,
            }
          : r,
      );
      set((s) => ({
        requirementsByProject: { ...s.requirementsByProject, [owningProjectId]: next },
      }));
      if (mode !== 'api') {
        const allReqs = loadStored<Requirement>(REQUIREMENTS_STORAGE_KEY);
        saveStored(REQUIREMENTS_STORAGE_KEY, [...allReqs.filter((r) => r.projectId !== owningProjectId), ...next]);
        return;
      }
      const res = await api.updateRequirement(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteRequirement: async (id) => {
      const { mode, requirementsByProject, testCasesByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, reqs] of Object.entries(requirementsByProject)) {
        if (reqs.some((r) => r.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const nextReqs = requirementsByProject[owningProjectId].filter((r) => r.id !== id);
      const nextTcs = (testCasesByProject[owningProjectId] ?? []).filter((tc) => tc.requirementId !== id);
      set((s) => ({
        requirementsByProject: { ...s.requirementsByProject, [owningProjectId]: nextReqs },
        testCasesByProject: { ...s.testCasesByProject, [owningProjectId]: nextTcs },
      }));
      if (mode !== 'api') {
        const allReqs = loadStored<Requirement>(REQUIREMENTS_STORAGE_KEY);
        saveStored(REQUIREMENTS_STORAGE_KEY, [...allReqs.filter((r) => r.projectId !== owningProjectId), ...nextReqs]);
        const allTcs = loadStored<RequirementTestCase>(TEST_CASES_STORAGE_KEY);
        saveStored(TEST_CASES_STORAGE_KEY, allTcs.filter((tc) => tc.requirementId !== id));
        return;
      }
      const res = await api.deleteRequirement(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    // ---- Benefits Realization ------------------------------------------
    reloadBenefits: async (projectId) => {
      const { mode } = get();
      if (mode !== 'api') {
        const all = loadStored<Benefit>(BENEFITS_STORAGE_KEY);
        const rows = all.filter((b) => b.projectId === projectId);
        set((s) => ({
          benefitsByProject: { ...s.benefitsByProject, [projectId]: rows },
          benefitsLoadedForProject: { ...s.benefitsLoadedForProject, [projectId]: true },
        }));
        return;
      }
      const res = await api.listBenefits(projectId);
      set((s) => ({
        benefitsByProject: { ...s.benefitsByProject, [projectId]: res.data ?? [] },
        benefitsLoadedForProject: { ...s.benefitsLoadedForProject, [projectId]: true },
      }));
    },

    createBenefit: async (projectId, data) => {
      const { mode, activeOrgId, benefitsByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const benefit: Benefit = {
          id: genLocalId(),
          projectId,
          organizationId: activeOrgId ?? 'local',
          name: data.name,
          description: data.description,
          category: data.category,
          owner: data.owner,
          unit: data.unit,
          baselineValue: data.baselineValue,
          targetValue: data.targetValue,
          actualValue: data.actualValue,
          higherIsBetter: data.higherIsBetter,
          targetDate: data.targetDate,
          realizedDate: data.realizedDate,
          status: data.status ?? 'planned',
          createdAt: now,
          updatedAt: now,
        };
        const existing = benefitsByProject[projectId] ?? [];
        const next = [...existing, benefit];
        set((s) => ({
          benefitsByProject: { ...s.benefitsByProject, [projectId]: next },
        }));
        const all = loadStored<Benefit>(BENEFITS_STORAGE_KEY);
        saveStored(BENEFITS_STORAGE_KEY, [...all.filter((b) => b.projectId !== projectId), ...next]);
        return benefit;
      }
      const res = await api.createBenefit(activeOrgId, projectId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create benefit');
        return null;
      }
      const existing = benefitsByProject[projectId] ?? [];
      set((s) => ({
        benefitsByProject: { ...s.benefitsByProject, [projectId]: [...existing, res.data!] },
      }));
      return res.data;
    },

    updateBenefit: async (id, patch) => {
      const { mode, benefitsByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, rows] of Object.entries(benefitsByProject)) {
        if (rows.some((b) => b.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const now = new Date().toISOString();
      const next = benefitsByProject[owningProjectId].map((b) =>
        b.id === id
          ? {
              ...b,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? b.description,
              owner: patch.owner === null ? undefined : patch.owner ?? b.owner,
              unit: patch.unit === null ? undefined : patch.unit ?? b.unit,
              baselineValue: patch.baselineValue === null ? undefined : patch.baselineValue ?? b.baselineValue,
              targetValue: patch.targetValue === null ? undefined : patch.targetValue ?? b.targetValue,
              actualValue: patch.actualValue === null ? undefined : patch.actualValue ?? b.actualValue,
              higherIsBetter: patch.higherIsBetter === null ? undefined : patch.higherIsBetter ?? b.higherIsBetter,
              targetDate: patch.targetDate === null ? undefined : patch.targetDate ?? b.targetDate,
              realizedDate: patch.realizedDate === null ? undefined : patch.realizedDate ?? b.realizedDate,
              updatedAt: now,
            }
          : b,
      );
      set((s) => ({
        benefitsByProject: { ...s.benefitsByProject, [owningProjectId]: next },
      }));
      if (mode !== 'api') {
        const all = loadStored<Benefit>(BENEFITS_STORAGE_KEY);
        saveStored(BENEFITS_STORAGE_KEY, [...all.filter((b) => b.projectId !== owningProjectId), ...next]);
        return;
      }
      const res = await api.updateBenefit(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteBenefit: async (id) => {
      const { mode, benefitsByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, rows] of Object.entries(benefitsByProject)) {
        if (rows.some((b) => b.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const next = benefitsByProject[owningProjectId].filter((b) => b.id !== id);
      set((s) => ({
        benefitsByProject: { ...s.benefitsByProject, [owningProjectId]: next },
      }));
      if (mode !== 'api') {
        const all = loadStored<Benefit>(BENEFITS_STORAGE_KEY);
        saveStored(BENEFITS_STORAGE_KEY, [...all.filter((b) => b.projectId !== owningProjectId), ...next]);
        return;
      }
      const res = await api.deleteBenefit(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    // ---- Intake & Demand Management --------------------------------------
    reloadDemands: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const all = loadStored<Demand>(DEMANDS_STORAGE_KEY);
        set({
          demands: all.filter((d) => d.organizationId === (activeOrgId ?? 'local')),
          demandsLoaded: true,
        });
        return;
      }
      const res = await api.listDemands(activeOrgId);
      set({ demands: res.data ?? [], demandsLoaded: true });
    },

    loadDemandWeights: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const stored = loadStored<DemandWeights>(DEMAND_WEIGHTS_STORAGE_KEY);
        set({ demandWeights: stored[0] ?? DEFAULT_DEMAND_WEIGHTS });
        return;
      }
      const res = await api.getDemandSettings(activeOrgId);
      if (res.data) {
        set({
          demandWeights: {
            weightAlignment: res.data.weightAlignment,
            weightValue: res.data.weightValue,
            weightUrgency: res.data.weightUrgency,
            weightRisk: res.data.weightRisk,
            weightEffort: res.data.weightEffort,
          },
        });
      } else {
        set({ demandWeights: DEFAULT_DEMAND_WEIGHTS });
      }
    },

    saveDemandWeights: async (weights) => {
      const { mode, activeOrgId } = get();
      set({ demandWeights: weights });
      if (mode !== 'api' || !activeOrgId) {
        saveStored(DEMAND_WEIGHTS_STORAGE_KEY, [weights]);
        return;
      }
      const res = await api.upsertDemandSettings(activeOrgId, weights);
      if (res.error) toast.error(`Could not save weights: ${res.error}`);
    },

    createDemand: async (data) => {
      const { mode, activeOrgId, demands } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const demand: Demand = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          title: data.title,
          description: data.description,
          requester: data.requester,
          type: data.type,
          estimatedCost: data.estimatedCost,
          estimatedValue: data.estimatedValue,
          targetDate: data.targetDate,
          scoreAlignment: data.scoreAlignment,
          scoreValue: data.scoreValue,
          scoreUrgency: data.scoreUrgency,
          scoreRisk: data.scoreRisk,
          scoreEffort: data.scoreEffort,
          stage: data.stage ?? 'new',
          decisionNotes: data.decisionNotes,
          revisitDate: data.revisitDate,
          createdAt: now,
          updatedAt: now,
        };
        const next = [demand, ...demands];
        set({ demands: next });
        saveStored(DEMANDS_STORAGE_KEY, next);
        return demand;
      }
      const res = await api.createDemand(activeOrgId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create demand');
        return null;
      }
      set({ demands: [res.data, ...demands] });
      return res.data;
    },

    updateDemand: async (id, patch) => {
      const { mode, demands } = get();
      const prev = demands.find((d) => d.id === id);
      const now = new Date().toISOString();
      const next = demands.map((d) => (d.id === id ? { ...d, ...patch, updatedAt: now } : d));
      set({ demands: next });
      // Governance: notify the demand's creator when it's approved or rejected
      // (createNotification auto-skips if the creator is the acting user).
      if (
        prev?.createdBy &&
        (patch.stage === 'approved' || patch.stage === 'rejected') &&
        prev.stage !== patch.stage
      ) {
        const approved = patch.stage === 'approved';
        void get().createNotification({
          recipientUserId: prev.createdBy,
          kind: approved ? 'demand_approved' : 'demand_rejected',
          entityType: 'demand',
          entityId: id,
          title: `Demand ${approved ? 'approved' : 'rejected'}: ${prev.title}`,
          body: patch.decisionNotes ?? prev.decisionNotes ?? undefined,
        });
      }
      if (mode !== 'api') {
        saveStored(DEMANDS_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateDemand(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteDemand: async (id) => {
      const { mode, demands } = get();
      const next = demands.filter((d) => d.id !== id);
      set({ demands: next });
      if (mode !== 'api') {
        saveStored(DEMANDS_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteDemand(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    convertDemandToProject: async (id) => {
      const demand = get().demands.find((d) => d.id === id);
      if (!demand) return null;
      const now = new Date().toISOString();
      const projectId = generateProjectId();
      const project: Project = {
        id: projectId,
        name: demand.title,
        description: demand.description ?? '',
        status: 'planning',
        health: 'healthy',
        priority: 'medium',
        methodology: 'Waterfall',
        startDate: now.slice(0, 10),
        endDate: demand.targetDate ?? now.slice(0, 10),
        completion: 0,
        budget: demand.estimatedCost ?? 0,
        spent: 0,
        pm: '',
        sponsor: demand.requester || undefined,
        portfolioId: null,
        team: [],
        scope: '',
        objectives: [],
        phases: [],
        wbsTasks: [],
        risks: [],
        issues: [],
        changes: [],
        baselines: [],
        activeBaselineId: null,
        createdDate: now,
        modifiedDate: now,
      };
      get().addProject(project);
      await get().updateDemand(id, { stage: 'converted', convertedProjectId: projectId });
      return projectId;
    },

    sendDemandToRoadmap: async (demandId, portfolioId) => {
      const demand = get().demands.find((d) => d.id === demandId);
      if (!demand) return null;
      // Map the weighted priority score to a pipeline priority.
      const score = computeDemandScore(demand, get().demandWeights);
      const priority: PipelineProjectPriority =
        score == null ? 'medium'
          : score >= 4.5 ? 'critical'
          : score >= 3.5 ? 'high'
          : score >= 2.5 ? 'medium'
          : 'low';
      const pp = await get().createPipelineProject(portfolioId, {
        name: demand.title,
        description: demand.description,
        status: 'approved',
        priority,
        businessValue: demand.estimatedValue,
        targetStartDate: demand.targetDate,
        sourceDemandId: demand.id,
      });
      if (!pp) return null;
      await get().updateDemand(demandId, { stage: 'converted', pipelineProjectId: pp.id });
      return pp.id;
    },

    startPipelineProject: async (pipelineProjectId) => {
      let pp: PipelineProject | undefined;
      for (const list of Object.values(get().pipelineProjectsByPortfolio)) {
        pp = list.find((p) => p.id === pipelineProjectId);
        if (pp) break;
      }
      if (!pp) return null;
      // Migration 0054: carry the size tier chosen at intake onto the project.
      const sourceDemand = pp.sourceDemandId
        ? get().demands.find((d) => d.id === pp.sourceDemandId)
        : undefined;
      const now = new Date().toISOString();
      const projectId = generateProjectId();
      const start = pp.targetStartDate ?? now.slice(0, 10);
      const end = pp.effortWeeks
        ? new Date(new Date(start).getTime() + pp.effortWeeks * 7 * 86400000)
            .toISOString()
            .slice(0, 10)
        : pp.targetEndDate ?? start;
      const project: Project = {
        id: projectId,
        name: pp.name,
        description: pp.description ?? '',
        status: 'planning',
        health: 'healthy',
        priority: pp.priority,
        methodology: 'Waterfall',
        startDate: start,
        endDate: end,
        completion: 0,
        budget: 0,
        spent: 0,
        pm: '',
        sponsor: undefined,
        portfolioId: pp.portfolioId,
        sizeTierId: sourceDemand?.sizeTierId ?? null,
        team: [],
        scope: '',
        objectives: [],
        phases: [],
        wbsTasks: [],
        risks: [],
        issues: [],
        changes: [],
        baselines: [],
        activeBaselineId: null,
        createdDate: now,
        modifiedDate: now,
      };
      get().addProject(project);
      await get().updatePipelineProject(pipelineProjectId, {
        status: 'started',
        startedProjectId: projectId,
      });
      // Close the loop back to the originating demand, if any.
      if (pp.sourceDemandId) {
        await get().updateDemand(pp.sourceDemandId, { convertedProjectId: projectId });
      }
      return projectId;
    },

    // ---- Lessons-Learned -------------------------------------------------
    reloadLessons: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const all = loadStored<Lesson>(LESSONS_STORAGE_KEY);
        set({
          lessons: all.filter((l) => l.organizationId === (activeOrgId ?? 'local')),
          lessonsLoaded: true,
        });
        return;
      }
      const res = await api.listLessons(activeOrgId);
      set({ lessons: res.data ?? [], lessonsLoaded: true });
    },

    createLesson: async (data) => {
      const { mode, activeOrgId, lessons } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const lesson: Lesson = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          projectId: data.projectId ?? undefined,
          title: data.title,
          category: data.category,
          description: data.description,
          tags: data.tags ?? [],
          createdAt: now,
          updatedAt: now,
        };
        const next = [lesson, ...lessons];
        set({ lessons: next });
        saveStored(LESSONS_STORAGE_KEY, next);
        return lesson;
      }
      const res = await api.createLesson(activeOrgId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create lesson');
        return null;
      }
      set({ lessons: [res.data, ...lessons] });
      return res.data;
    },

    updateLesson: async (id, patch) => {
      const { mode, lessons } = get();
      const now = new Date().toISOString();
      const next = lessons.map((l) =>
        l.id === id
          ? {
              ...l,
              ...patch,
              projectId: patch.projectId === null ? undefined : patch.projectId ?? l.projectId,
              description: patch.description === null ? undefined : patch.description ?? l.description,
              updatedAt: now,
            }
          : l,
      );
      set({ lessons: next });
      if (mode !== 'api') {
        saveStored(LESSONS_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateLesson(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteLesson: async (id) => {
      const { mode, lessons } = get();
      const next = lessons.filter((l) => l.id !== id);
      set({ lessons: next });
      if (mode !== 'api') {
        saveStored(LESSONS_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteLesson(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    // ---- Resource availability / PTO ------------------------------------
    reloadTimeOff: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const all = loadStored<ResourceTimeOff>(TIME_OFF_STORAGE_KEY);
        set({
          timeOff: all.filter((t) => t.organizationId === (activeOrgId ?? 'local')),
          timeOffLoaded: true,
        });
        return;
      }
      const res = await api.listResourceTimeOff(activeOrgId);
      set({ timeOff: res.data ?? [], timeOffLoaded: true });
    },

    addTimeOff: async (data) => {
      const { mode, activeOrgId, timeOff } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const entry: ResourceTimeOff = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          nameNormalized: data.nameNormalized,
          displayName: data.displayName,
          startDate: data.startDate,
          endDate: data.endDate,
          kind: data.kind,
          note: data.note ?? undefined,
          createdAt: now,
          updatedAt: now,
        };
        const next = [...timeOff, entry];
        set({ timeOff: next });
        saveStored(TIME_OFF_STORAGE_KEY, next);
        return entry;
      }
      const res = await api.createResourceTimeOff(activeOrgId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to add time off');
        return null;
      }
      set({ timeOff: [...timeOff, res.data] });
      return res.data;
    },

    deleteTimeOff: async (id) => {
      const { mode, timeOff } = get();
      const next = timeOff.filter((t) => t.id !== id);
      set({ timeOff: next });
      if (mode !== 'api') {
        saveStored(TIME_OFF_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteResourceTimeOff(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    // ---- Global resource registry ---------------------------------------
    reloadResources: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const org = activeOrgId ?? 'local';
        const all = loadStored<Resource>(RESOURCES_STORAGE_KEY).filter((r) => r.organizationId === org);
        if (all.length > 0) {
          set({ resources: all, resourcesLoaded: true });
          return;
        }
        // Demo-mode equivalent of the SQL backfill: derive one registry row per
        // distinct resource key from the current projects. Guarded on empty so
        // it never clobbers user edits on refresh.
        const now = new Date().toISOString();
        const derived: Resource[] = aggregateResources(get().projects).map((s) => ({
          id: genLocalId(),
          organizationId: org,
          nameNormalized: s.key,
          displayName: s.displayName,
          role: s.roles[0],
          department: s.department,
          isActive: true,
          createdAt: now,
          updatedAt: now,
        }));
        // Persist alongside any rows from OTHER orgs already stored.
        const others = loadStored<Resource>(RESOURCES_STORAGE_KEY).filter((r) => r.organizationId !== org);
        saveStored(RESOURCES_STORAGE_KEY, [...others, ...derived]);
        set({ resources: derived, resourcesLoaded: true });
        return;
      }
      const res = await api.listResources(activeOrgId);
      set({ resources: res.data ?? [], resourcesLoaded: true });
    },

    addResource: async (data) => {
      const { mode, activeOrgId, resources } = get();
      const nameNormalized = normalizeResourceName(data.displayName);
      if (!nameNormalized) return null;
      // De-dupe by normalized key — return the existing row instead.
      const existing = resources.find((r) => r.nameNormalized === nameNormalized);
      if (existing) return existing;
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const resource: Resource = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          nameNormalized,
          displayName: data.displayName.trim(),
          userId: data.userId ?? undefined,
          email: data.email ?? undefined,
          role: data.role ?? undefined,
          department: data.department ?? undefined,
          grade: data.grade ?? undefined,
          isActive: data.isActive ?? true,
          createdAt: now,
          updatedAt: now,
        };
        const next = [...resources, resource];
        set({ resources: next });
        saveStored(RESOURCES_STORAGE_KEY, next);
        return resource;
      }
      const res = await api.createResource(activeOrgId, { ...data, nameNormalized, displayName: data.displayName.trim() });
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to add resource');
        return null;
      }
      set({ resources: [...resources, res.data] });
      return res.data;
    },

    updateResource: async (id, patch) => {
      const { mode, resources } = get();
      const now = new Date().toISOString();
      const next = resources.map((r) =>
        r.id === id
          ? {
              ...r,
              ...patch,
              userId: patch.userId === null ? undefined : patch.userId ?? r.userId,
              email: patch.email === null ? undefined : patch.email ?? r.email,
              role: patch.role === null ? undefined : patch.role ?? r.role,
              department: patch.department === null ? undefined : patch.department ?? r.department,
              grade: patch.grade === null ? undefined : patch.grade ?? r.grade,
              updatedAt: now,
            }
          : r,
      );
      set({ resources: next });
      if (mode !== 'api') {
        saveStored(RESOURCES_STORAGE_KEY, next);
        return;
      }
      const res = await api.updateResource(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteResource: async (id) => {
      const { mode, resources } = get();
      const next = resources.filter((r) => r.id !== id);
      set({ resources: next });
      if (mode !== 'api') {
        saveStored(RESOURCES_STORAGE_KEY, next);
        return;
      }
      const res = await api.deleteResource(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    importResources: async (rows, opts) => {
      let added = 0;
      let updated = 0;
      let invited = 0;
      let inviteFailed = 0;

      for (const row of rows) {
        const displayName = row.displayName.trim();
        const nameNormalized = normalizeResourceName(displayName);
        if (!nameNormalized) continue;

        // Upsert the registry row by normalized name.
        const existing = get().resources.find((r) => r.nameNormalized === nameNormalized);
        if (existing) {
          const patch: Partial<{ email: string | null; role: string | null; department: string | null; isActive: boolean }> = {};
          if (row.email) patch.email = row.email;
          if (row.role) patch.role = row.role;
          if (row.department) patch.department = row.department;
          if (row.isActive !== undefined) patch.isActive = row.isActive;
          if (Object.keys(patch).length > 0) await get().updateResource(existing.id, patch);
          updated += 1;
        } else {
          const created = await get().addResource({
            displayName,
            email: row.email ?? null,
            role: row.role ?? null,
            department: row.department ?? null,
            isActive: row.isActive ?? true,
          });
          if (created) added += 1;
        }

        // Capacity + rate (merge with any existing values; don't clobber with defaults).
        if (row.hoursPerWeek !== undefined || row.hourlyRate !== undefined) {
          const cap = get().resourceCapacities[nameNormalized];
          const hours = row.hoursPerWeek ?? cap?.hoursPerWeek ?? 40;
          const rate = row.hourlyRate ?? cap?.hourlyRate ?? null;
          await get().setResourceCapacity(nameNormalized, displayName, hours, rate);
        }

        // Optional workspace invite (api mode only; needs an email).
        if (opts.invite && row.email) {
          const { mode, activeOrgId } = get();
          if (mode === 'api' && activeOrgId) {
            const res = await api.inviteMember(activeOrgId, row.email, 'member');
            if (res.error) inviteFailed += 1;
            else invited += 1;
          }
        }
      }

      const parts = [`${added} added`, `${updated} updated`];
      if (opts.invite) parts.push(`${invited} invited`);
      toast.success(`Resources imported — ${parts.join(', ')}.`);
      if (inviteFailed > 0) {
        toast.error(`${inviteFailed} invite${inviteFailed === 1 ? '' : 's'} couldn't be sent (already a member/invited, or invalid email).`);
      }
      return { added, updated, invited, inviteFailed };
    },

    setResourceActive: async (nameNormalized, displayName, active) => {
      const existing = get().resources.find((r) => r.nameNormalized === nameNormalized);
      if (existing) {
        await get().updateResource(existing.id, { isActive: active });
        return;
      }
      // No registry row yet — create one carrying the active state.
      await get().addResource({ displayName, isActive: active });
    },

    // --- What-if scenarios (Migration 0063) ---
    reloadScenarios: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const org = activeOrgId ?? 'local';
        const all = loadStored<ResourceScenario>(SCENARIOS_STORAGE_KEY).filter(
          (s) => s.organizationId === org,
        );
        set({ scenarios: all, scenariosLoaded: true });
        return;
      }
      const res = await api.listScenarios(activeOrgId);
      set({ scenarios: res.data ?? [], scenariosLoaded: true });
    },

    saveScenario: async (name, overrides, id) => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const org = activeOrgId ?? 'local';
        const now = new Date().toISOString();
        const others = loadStored<ResourceScenario>(SCENARIOS_STORAGE_KEY).filter(
          (s) => s.id !== id,
        );
        const sc: ResourceScenario = {
          id: id ?? genLocalId(),
          organizationId: org,
          name,
          overrides,
          createdAt: now,
          updatedAt: now,
        };
        saveStored(SCENARIOS_STORAGE_KEY, [...others, sc]);
        set({ scenarios: loadStored<ResourceScenario>(SCENARIOS_STORAGE_KEY).filter((s) => s.organizationId === org) });
        return sc.id;
      }
      const res = id
        ? await api.updateScenario(id, { name, overrides })
        : await api.createScenario(activeOrgId, name, overrides);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Could not save scenario');
        return null;
      }
      await get().reloadScenarios();
      return res.data.id;
    },

    deleteScenario: async (id) => {
      const { mode, activeOrgId } = get();
      set({ scenarios: get().scenarios.filter((s) => s.id !== id) });
      if (mode !== 'api' || !activeOrgId) {
        const remaining = loadStored<ResourceScenario>(SCENARIOS_STORAGE_KEY).filter((s) => s.id !== id);
        saveStored(SCENARIOS_STORAGE_KEY, remaining);
        return;
      }
      const res = await api.deleteScenario(id);
      if (res.error) toast.error(res.error);
    },

    // --- Utilisation snapshots (Migration 0064) ---
    reloadUtilizationSnapshots: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const org = activeOrgId ?? 'local';
        const all = loadStored<UtilizationSnapshot>(UTIL_SNAPSHOTS_STORAGE_KEY)
          .filter((s) => s.organizationId === org)
          .sort((a, b) => a.capturedOn.localeCompare(b.capturedOn));
        set({ utilizationSnapshots: all, utilizationSnapshotsLoaded: true });
        return;
      }
      const res = await api.listUtilizationSnapshots(activeOrgId);
      set({ utilizationSnapshots: res.data ?? [], utilizationSnapshotsLoaded: true });
    },

    captureUtilizationSnapshot: async (force) => {
      const { mode, activeOrgId, projects, utilizationSnapshots } = get();
      const org = activeOrgId ?? 'local';
      const capturedOn = weekMondayIso(new Date());
      // Skip if we already have a snapshot for this week (unless forced).
      if (!force && utilizationSnapshots.some((s) => s.organizationId === org && s.capturedOn === capturedOn)) {
        return 'recent';
      }
      const rows = aggregateResources(projects).map((s) => ({
        resourceKey: s.key,
        displayName: s.displayName,
        utilizationPct: Math.round(s.peakAllocation),
      }));
      if (rows.length === 0) return 'empty';

      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const others = loadStored<UtilizationSnapshot>(UTIL_SNAPSHOTS_STORAGE_KEY).filter(
          (s) => !(s.organizationId === org && s.capturedOn === capturedOn),
        );
        const added: UtilizationSnapshot[] = rows.map((r) => ({
          id: genLocalId(),
          organizationId: org,
          capturedOn,
          resourceKey: r.resourceKey,
          displayName: r.displayName,
          utilizationPct: r.utilizationPct,
          // createdAt not part of the type; ignored
        })) as unknown as UtilizationSnapshot[];
        saveStored(UTIL_SNAPSHOTS_STORAGE_KEY, [...others, ...added]);
        await get().reloadUtilizationSnapshots();
        void now;
        return 'captured';
      }
      const res = await api.insertUtilizationSnapshots(activeOrgId, capturedOn, rows);
      if (res.error) {
        toast.error(res.error);
        return 'empty';
      }
      await get().reloadUtilizationSnapshots();
      return 'captured';
    },

    rewriteResourceName: async (fromKey, toDisplayName) => {
      const toKey = normalizeResourceName(toDisplayName);
      if (!fromKey || !toKey || fromKey === toKey) return { projects: 0, tasks: 0 };
      const state = get();
      let projectsTouched = 0;
      let tasksTouched = 0;

      for (const p of state.projects) {
        // Team members — rewrite name; drop a duplicate that now collides.
        let teamChanged = false;
        const seenTeam = new Set<string>();
        const newTeam: typeof p.team = [];
        for (const m of p.team ?? []) {
          const mk = normalizeResourceName(m.name);
          // Unfilled roles have no name to rewrite, and several can share a role.
          if (!mk) {
            newTeam.push(m);
            continue;
          }
          const isMatch = mk === fromKey;
          const finalKey = isMatch ? toKey : mk;
          if (seenTeam.has(finalKey)) {
            teamChanged = true; // dropped a merge-duplicate
            continue;
          }
          seenTeam.add(finalKey);
          newTeam.push(isMatch ? { ...m, name: toDisplayName } : m);
          if (isMatch) teamChanged = true;
        }
        if (teamChanged) {
          get().updateProject(p.id, { team: newTeam });
          projectsTouched++;
        }

        // Task assignments — rewrite name in the parallel resources/allocation
        // arrays, de-duping a merge collision within a single task.
        for (const t of p.wbsTasks ?? []) {
          const resources = t.resources ?? [];
          if (!resources.some((r) => normalizeResourceName(r) === fromKey)) continue;
          const alloc = t.allocation ?? [];
          const seen = new Set<string>();
          const newResources: string[] = [];
          const newAlloc: number[] = [];
          resources.forEach((r, i) => {
            const isMatch = normalizeResourceName(r) === fromKey;
            const key = isMatch ? toKey : normalizeResourceName(r);
            if (seen.has(key)) return;
            seen.add(key);
            newResources.push(isMatch ? toDisplayName : r);
            newAlloc.push(alloc[i] ?? 100);
          });
          get().updateTask(p.id, t.taskId, { resources: newResources, allocation: newAlloc });
          tasksTouched++;
        }
      }

      // Capacity — move the row to the new key (keep the target's if it exists).
      const cap = state.resourceCapacities[fromKey];
      if (cap) {
        if (!state.resourceCapacities[toKey]) {
          await get().setResourceCapacity(toKey, toDisplayName, cap.hoursPerWeek, cap.hourlyRate, cap.notes);
        }
        await get().clearResourceCapacity(fromKey);
      }

      // Time off — retarget entries to the new name.
      for (const e of state.timeOff.filter((x) => x.nameNormalized === fromKey)) {
        await get().addTimeOff({ nameNormalized: toKey, displayName: toDisplayName, startDate: e.startDate, endDate: e.endDate, kind: e.kind, note: e.note ?? null });
        await get().deleteTimeOff(e.id);
      }

      // Role assignments — move ALL of fromKey's roles to toKey (R3: multi-role).
      // Delete non-primary first so removing the primary never triggers a
      // needless survivor-promotion; then re-add the distinct roles to the
      // target (primary first, so the primary designation is preserved on a
      // plain rename). Roles the target already has are skipped (merge).
      const fromAssigns = state.resourceRoleAssignments.filter((a) => normalizeResourceName(a.resourceName) === fromKey);
      if (fromAssigns.length) {
        const targetRoleIds = new Set(
          state.resourceRoleAssignments
            .filter((a) => normalizeResourceName(a.resourceName) === toKey)
            .map((a) => a.roleId),
        );
        const delOrder = [...fromAssigns].sort((a, b) => Number(a.isPrimary) - Number(b.isPrimary));
        for (const a of delOrder) await get().unassignResourceRole(a.id);
        const addOrder = [...fromAssigns].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
        for (const a of addOrder) {
          if (targetRoleIds.has(a.roleId)) continue;
          await get().addResourceRole(toDisplayName, a.roleId);
          targetRoleIds.add(a.roleId);
        }
      }

      // Skill levels (R3) — move fromKey's to toKey; skip skills the target has.
      const fromLevels = state.resourceSkillLevels.filter((l) => l.nameNormalized === fromKey);
      if (fromLevels.length) {
        const targetSkills = new Set(
          state.resourceSkillLevels.filter((l) => l.nameNormalized === toKey).map((l) => l.skill),
        );
        for (const l of fromLevels) {
          if (!targetSkills.has(l.skill)) {
            await get().setSkillLevel(toKey, l.skill, l.level);
            targetSkills.add(l.skill);
          }
          await get().clearSkillLevel(l.id);
        }
      }

      return { projects: projectsTouched, tasks: tasksTouched };
    },

    createTestCase: async (projectId, requirementId, data) => {
      const { mode, activeOrgId, testCasesByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const tc: RequirementTestCase = {
          id: genLocalId(),
          requirementId,
          organizationId: activeOrgId ?? 'local',
          testCode: data.testCode,
          testName: data.testName,
          testSteps: data.testSteps,
          expectedResult: data.expectedResult,
          testStatus: data.testStatus ?? 'not_tested',
          createdAt: now,
          updatedAt: now,
        };
        const existing = testCasesByProject[projectId] ?? [];
        const next = [...existing, tc];
        set((s) => ({
          testCasesByProject: { ...s.testCasesByProject, [projectId]: next },
        }));
        const allTcs = loadStored<RequirementTestCase>(TEST_CASES_STORAGE_KEY);
        saveStored(TEST_CASES_STORAGE_KEY, [...allTcs, tc]);
        return tc;
      }
      const res = await api.createTestCase(activeOrgId, requirementId, data);
      if (res.error || !res.data) {
        toast.error(res.error ?? 'Failed to create test case');
        return null;
      }
      const existing = testCasesByProject[projectId] ?? [];
      set((s) => ({
        testCasesByProject: { ...s.testCasesByProject, [projectId]: [...existing, res.data!] },
      }));
      return res.data;
    },

    updateTestCase: async (id, patch) => {
      const { mode, testCasesByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, tcs] of Object.entries(testCasesByProject)) {
        if (tcs.some((tc) => tc.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const now = new Date().toISOString();
      const next = testCasesByProject[owningProjectId].map((tc) => {
        if (tc.id !== id) return tc;
        const updated: RequirementTestCase = { ...tc, updatedAt: now };
        if (patch.testCode !== undefined) updated.testCode = patch.testCode ?? undefined;
        if (patch.testName !== undefined) updated.testName = patch.testName;
        if (patch.testSteps !== undefined) updated.testSteps = patch.testSteps ?? undefined;
        if (patch.expectedResult !== undefined) updated.expectedResult = patch.expectedResult ?? undefined;
        if (patch.actualResult !== undefined) updated.actualResult = patch.actualResult ?? undefined;
        if (patch.testStatus !== undefined) {
          updated.testStatus = patch.testStatus;
          // Auto-set testedAt when a status change is made away from not_tested
          if (patch.testStatus !== 'not_tested') {
            updated.testedAt = now;
          }
        }
        if (patch.testedBy !== undefined) updated.testedBy = patch.testedBy ?? undefined;
        if (patch.testedAt !== undefined) updated.testedAt = patch.testedAt ?? undefined;
        if (patch.notes !== undefined) updated.notes = patch.notes ?? undefined;
        return updated;
      });
      set((s) => ({
        testCasesByProject: { ...s.testCasesByProject, [owningProjectId]: next },
      }));
      if (mode !== 'api') {
        const allTcs = loadStored<RequirementTestCase>(TEST_CASES_STORAGE_KEY);
        saveStored(TEST_CASES_STORAGE_KEY, allTcs.map((tc) => next.find((n) => n.id === tc.id) ?? tc));
        return;
      }
      const res = await api.updateTestCase(id, patch);
      if (res.error) toast.error(`Update failed: ${res.error}`);
    },

    deleteTestCase: async (id) => {
      const { mode, testCasesByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, tcs] of Object.entries(testCasesByProject)) {
        if (tcs.some((tc) => tc.id === id)) {
          owningProjectId = pid;
          break;
        }
      }
      if (!owningProjectId) return;
      const next = testCasesByProject[owningProjectId].filter((tc) => tc.id !== id);
      set((s) => ({
        testCasesByProject: { ...s.testCasesByProject, [owningProjectId]: next },
      }));
      if (mode !== 'api') {
        const allTcs = loadStored<RequirementTestCase>(TEST_CASES_STORAGE_KEY);
        saveStored(TEST_CASES_STORAGE_KEY, allTcs.filter((tc) => tc.id !== id));
        return;
      }
      const res = await api.deleteTestCase(id);
      if (res.error) toast.error(`Delete failed: ${res.error}`);
    },

    // ---------- Phase 32a: Change Request workflow ----------

    reloadWorkspacePolicy: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        // localStorage: use defaults
        set({
          crApprovalRoles: ['owner', 'admin'],
          gatePolicy: 'soft',
          crApprovalEnabled: true,
          crApprovalsRequired: 1,
          myRoleInOrg: 'owner',
          workspacePolicyLoaded: true,
        });
        return;
      }
      const [policyRes, roleRes] = await Promise.all([
        api.getOrgPolicy(activeOrgId),
        api.getMyRoleInOrg(activeOrgId),
      ]);
      set({
        crApprovalRoles: policyRes.data?.crApprovalRoles ?? ['owner', 'admin'],
        gatePolicy: policyRes.data?.gatePolicy ?? 'soft',
        crApprovalEnabled: policyRes.data?.crApprovalEnabled ?? true,
        crApprovalsRequired: policyRes.data?.crApprovalsRequired ?? 1,
        myRoleInOrg: roleRes.data ?? null,
        workspacePolicyLoaded: true,
      });
    },

    reloadCrHistory: async (changeRequestId) => {
      const { mode } = get();
      if (mode !== 'api') {
        // localStorage: history is stored inline in the CR (not implemented in
        // this pass — return empty). API mode is the primary flow.
        set((s) => ({ crHistoryByCr: { ...s.crHistoryByCr, [changeRequestId]: [] } }));
        return;
      }
      const res = await api.listChangeRequestHistory(changeRequestId);
      set((s) => ({ crHistoryByCr: { ...s.crHistoryByCr, [changeRequestId]: res.data ?? [] } }));
    },

    transitionChangeRequest: async (projectId, changeRequestId, toStatus, comment, actorDisplay) => {
      const { mode, activeOrgId, projects, crHistoryByCr } = get();
      const project = projects.find((p) => p.id === projectId);
      const cr = project?.changes?.find((c) => c.id === changeRequestId);
      if (!cr) {
        toast.error('Change request not found');
        return false;
      }
      const fromStatus = cr.status;
      const now = new Date().toISOString();

      // Migration 0075: approvals threshold. When the workflow is enabled, an
      // "approved" action casts one approval; the CR only becomes Approved once
      // the number of DISTINCT approvers reaches cr_approvals_required. When the
      // workflow is disabled, a single approve finalizes immediately.
      const meId = useAuthStore.getState().user?.id ?? 'local-user';
      const approvalEnabled = get().crApprovalEnabled;
      const approvalsRequired = Math.max(1, get().crApprovalsRequired || 1);
      let nextApprovals = cr.approvals ?? [];
      let finalizeApproval = true;
      if (toStatus === 'approved') {
        nextApprovals = (cr.approvals ?? []).some((a) => a.userId === meId)
          ? (cr.approvals ?? [])
          : [...(cr.approvals ?? []), { userId: meId, by: actorDisplay, at: now, comment }];
        const distinct = new Set(nextApprovals.map((a) => a.userId)).size;
        finalizeApproval = !approvalEnabled || distinct >= approvalsRequired;
      }
      // When an approval doesn't yet meet the threshold, the CR stays in its
      // current status (we just record the cast approval).
      const effectiveStatus: ChangeRequestStatus =
        toStatus === 'approved' && !finalizeApproval ? fromStatus : toStatus;
      const statusChanged = effectiveStatus !== fromStatus;

      // Update the CR locally with new status + relevant timestamp fields
      const patched: ChangeRequest = { ...cr, status: effectiveStatus, approvals: nextApprovals };
      if (toStatus === 'submitted') { patched.submittedAt = now; patched.submittedBy = actorDisplay; }
      if (toStatus === 'review') { patched.reviewedAt = now; patched.reviewedBy = actorDisplay; }
      if (toStatus === 'approved' && finalizeApproval) { patched.approvedAt = now; patched.approvedBy = actorDisplay; patched.decision = comment ?? patched.decision; }
      if (toStatus === 'rejected') { patched.approvedAt = now; patched.approvedBy = actorDisplay; patched.decision = comment ?? patched.decision; }
      if (toStatus === 'implemented') { patched.implementedAt = now; }

      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id !== projectId
            ? p
            : { ...p, changes: (p.changes ?? []).map((c) => (c.id === changeRequestId ? patched : c)) },
        ),
      );

      // Migration 0075: auto re-baseline. When a re-baseline CR becomes Approved,
      // apply its impact deltas to the project and capture a new baseline. Uses
      // the project's pre-change state (the local CR update above didn't touch
      // budget/dates), so the deltas apply correctly, then the snapshot reflects
      // the updated values.
      if (toStatus === 'approved' && finalizeApproval && cr.rebaselineOnApproval) {
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) {
          const { updates, baseline } = applyCrRebaseline(proj, patched, { by: actorDisplay, now });
          get().updateProject(projectId, updates);
          get().createBaseline(projectId, baseline);
          toast.success(`Project re-baselined from ${cr.id}`);
        }
      }

      // Phase 32a v3: route through the standard upsert so the DB row carries
      // the new status AND the updated approvals list, even when no formal
      // transition row is written (a cast-but-not-final approval).
      if (get().mode === 'api') {
        fireApi(
          'Transition change request',
          api.upsertChangeRequest(projectId, patched),
        );
      }

      // Build a local history entry (for immediate UI feedback in both modes)
      const histComment =
        toStatus === 'approved' && !finalizeApproval
          ? `Approval ${new Set(nextApprovals.map((a) => a.userId)).size} of ${approvalsRequired}${comment ? ` — ${comment}` : ''}`
          : comment;
      const localHist: ChangeRequestHistoryEntry = {
        id: genLocalId(),
        changeRequestId,
        organizationId: activeOrgId ?? 'local',
        fromStatus,
        toStatus: effectiveStatus,
        actorDisplay,
        comment: histComment,
        createdAt: now,
      };
      const existingHist = crHistoryByCr[changeRequestId] ?? [];
      set((s) => ({
        crHistoryByCr: { ...s.crHistoryByCr, [changeRequestId]: [...existingHist, localHist] },
      }));

      if (mode !== 'api' || !activeOrgId) return true;

      // Only write a formal transition (history row + status update) when the
      // status actually changed. A cast-but-not-final approval persists via the
      // CR upsert above (status unchanged, approvals updated) — no transition row.
      if (!statusChanged) return true;

      const res = await api.transitionChangeRequest(
        activeOrgId,
        changeRequestId,
        fromStatus,
        effectiveStatus,
        comment,
        actorDisplay,
      );
      if (res.error || !res.data) {
        toast.error(`Transition failed: ${res.error}`);
        return false;
      }
      // Replace the optimistic local entry with the DB-canonical one
      set((s) => ({
        crHistoryByCr: {
          ...s.crHistoryByCr,
          [changeRequestId]: [
            ...(s.crHistoryByCr[changeRequestId] ?? []).filter((h) => h.id !== localHist.id),
            res.data!,
          ],
        },
      }));
      return true;
    },

    // ---------- Phase 32b: Gate templates + Project gates ----------

    reloadGateTemplates: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        const stored = loadStored<GateTemplate>(GATE_TEMPLATES_STORAGE_KEY);
        set({ gateTemplates: stored, gateTemplatesLoaded: true });
        return;
      }
      const res = await api.listGateTemplates(activeOrgId);
      set({ gateTemplates: res.data ?? [], gateTemplatesLoaded: true });
    },

    createGateTemplate: async (data) => {
      const { mode, activeOrgId, gateTemplates } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const tpl: GateTemplate = {
          id: genLocalId(),
          organizationId: activeOrgId ?? 'local',
          name: data.name,
          description: data.description,
          isArchived: false,
          createdAt: now,
          updatedAt: now,
          items: [],
        };
        const next = [...gateTemplates, tpl];
        set({ gateTemplates: next });
        saveStored(GATE_TEMPLATES_STORAGE_KEY, next);
        return tpl;
      }
      const res = await api.createGateTemplate(activeOrgId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Create failed'); return null; }
      set({ gateTemplates: [...gateTemplates, res.data] });
      return res.data;
    },

    updateGateTemplate: async (id, patch) => {
      const { mode, gateTemplates } = get();
      const next = gateTemplates.map((t) =>
        t.id === id
          ? {
              ...t,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? t.description,
              updatedAt: new Date().toISOString(),
            }
          : t,
      );
      set({ gateTemplates: next });
      if (mode !== 'api') { saveStored(GATE_TEMPLATES_STORAGE_KEY, next); return; }
      const res = await api.updateGateTemplate(id, patch);
      if (res.error) toast.error(res.error);
    },

    deleteGateTemplate: async (id) => {
      const { mode, gateTemplates } = get();
      const next = gateTemplates.filter((t) => t.id !== id);
      set({ gateTemplates: next });
      if (mode !== 'api') { saveStored(GATE_TEMPLATES_STORAGE_KEY, next); return; }
      const res = await api.deleteGateTemplate(id);
      if (res.error) toast.error(res.error);
    },

    createGateTemplateItem: async (templateId, data) => {
      const { mode, activeOrgId, gateTemplates } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const item: GateTemplateItem = {
          id: genLocalId(),
          templateId,
          organizationId: activeOrgId ?? 'local',
          label: data.label,
          description: data.description,
          isRequired: data.isRequired ?? true,
          sortOrder: data.sortOrder ?? (gateTemplates.find((t) => t.id === templateId)?.items?.length ?? 0),
          createdAt: now,
          updatedAt: now,
        };
        const next = gateTemplates.map((t) =>
          t.id === templateId ? { ...t, items: [...(t.items ?? []), item] } : t,
        );
        set({ gateTemplates: next });
        saveStored(GATE_TEMPLATES_STORAGE_KEY, next);
        return item;
      }
      const res = await api.createGateTemplateItem(activeOrgId, templateId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Create failed'); return null; }
      const next = gateTemplates.map((t) =>
        t.id === templateId ? { ...t, items: [...(t.items ?? []), res.data!] } : t,
      );
      set({ gateTemplates: next });
      return res.data;
    },

    updateGateTemplateItem: async (id, patch) => {
      const { mode, gateTemplates } = get();
      const next = gateTemplates.map((t) => ({
        ...t,
        items: (t.items ?? []).map((item) =>
          item.id === id
            ? {
                ...item,
                ...patch,
                description: patch.description === null ? undefined : patch.description ?? item.description,
                updatedAt: new Date().toISOString(),
              }
            : item,
        ),
      }));
      set({ gateTemplates: next });
      if (mode !== 'api') { saveStored(GATE_TEMPLATES_STORAGE_KEY, next); return; }
      const res = await api.updateGateTemplateItem(id, patch);
      if (res.error) toast.error(res.error);
    },

    deleteGateTemplateItem: async (id) => {
      const { mode, gateTemplates } = get();
      const next = gateTemplates.map((t) => ({
        ...t,
        items: (t.items ?? []).filter((item) => item.id !== id),
      }));
      set({ gateTemplates: next });
      if (mode !== 'api') { saveStored(GATE_TEMPLATES_STORAGE_KEY, next); return; }
      const res = await api.deleteGateTemplateItem(id);
      if (res.error) toast.error(res.error);
    },

    reloadProjectGates: async (projectId) => {
      const { mode } = get();
      if (mode !== 'api') {
        const stored = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        const filtered = stored.filter((g) => g.projectId === projectId);
        set((s) => ({
          gatesByProject: { ...s.gatesByProject, [projectId]: filtered },
          gatesLoadedForProject: { ...s.gatesLoadedForProject, [projectId]: true },
        }));
        return;
      }
      const res = await api.listProjectGates(projectId);
      set((s) => ({
        gatesByProject: { ...s.gatesByProject, [projectId]: res.data ?? [] },
        gatesLoadedForProject: { ...s.gatesLoadedForProject, [projectId]: true },
      }));
    },

    createProjectGate: async (projectId, data) => {
      const { mode, activeOrgId, gatesByProject } = get();
      if (mode !== 'api' || !activeOrgId) {
        const now = new Date().toISOString();
        const gateId = genLocalId();
        const items: ProjectGateItem[] = (data.itemsToCopy ?? []).map((it) => ({
          id: genLocalId(),
          gateId,
          organizationId: activeOrgId ?? 'local',
          label: it.label,
          description: it.description,
          isRequired: it.isRequired,
          sortOrder: it.sortOrder,
          isChecked: false,
          createdAt: now,
          updatedAt: now,
        }));
        const gate: ProjectGate = {
          id: gateId,
          projectId,
          organizationId: activeOrgId ?? 'local',
          workspacePhaseId: data.workspacePhaseId,
          templateId: data.templateId,
          name: data.name,
          requiresApproval: data.requiresApproval ?? false,
          status: 'open',
          createdAt: now,
          updatedAt: now,
          items,
        };
        const existing = gatesByProject[projectId] ?? [];
        const next = [...existing, gate];
        set((s) => ({ gatesByProject: { ...s.gatesByProject, [projectId]: next } }));
        const allGates = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        saveStored(PROJECT_GATES_STORAGE_KEY, [...allGates.filter((g) => g.projectId !== projectId), ...next]);
        return gate;
      }
      const res = await api.createProjectGate(activeOrgId, projectId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Create gate failed'); return null; }
      const existing = gatesByProject[projectId] ?? [];
      set((s) => ({ gatesByProject: { ...s.gatesByProject, [projectId]: [...existing, res.data!] } }));
      return res.data;
    },

    updateProjectGate: async (id, patch) => {
      const { mode, gatesByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, gs] of Object.entries(gatesByProject)) {
        if (gs.some((g) => g.id === id)) { owningProjectId = pid; break; }
      }
      if (!owningProjectId) return;
      const next = gatesByProject[owningProjectId].map((g) =>
        g.id === id
          ? {
              ...g,
              ...patch,
              notes: patch.notes === null ? undefined : patch.notes ?? g.notes,
              updatedAt: new Date().toISOString(),
            }
          : g,
      );
      set((s) => ({ gatesByProject: { ...s.gatesByProject, [owningProjectId]: next } }));
      if (mode !== 'api') {
        const allGates = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        saveStored(PROJECT_GATES_STORAGE_KEY, [...allGates.filter((g) => g.projectId !== owningProjectId), ...next]);
        return;
      }
      const res = await api.updateProjectGate(id, patch);
      if (res.error) toast.error(res.error);
    },

    deleteProjectGate: async (id) => {
      const { mode, gatesByProject } = get();
      let owningProjectId: string | null = null;
      for (const [pid, gs] of Object.entries(gatesByProject)) {
        if (gs.some((g) => g.id === id)) { owningProjectId = pid; break; }
      }
      if (!owningProjectId) return;
      const next = gatesByProject[owningProjectId].filter((g) => g.id !== id);
      set((s) => ({ gatesByProject: { ...s.gatesByProject, [owningProjectId]: next } }));
      if (mode !== 'api') {
        const allGates = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        saveStored(PROJECT_GATES_STORAGE_KEY, allGates.filter((g) => g.id !== id));
        return;
      }
      const res = await api.deleteProjectGate(id);
      if (res.error) toast.error(res.error);
    },

    toggleProjectGateItem: async (itemId, isChecked, actorDisplay) => {
      const { mode, gatesByProject } = get();
      let owningProjectId: string | null = null;
      let owningGateId: string | null = null;
      for (const [pid, gs] of Object.entries(gatesByProject)) {
        for (const g of gs) {
          if ((g.items ?? []).some((it) => it.id === itemId)) {
            owningProjectId = pid;
            owningGateId = g.id;
            break;
          }
        }
        if (owningProjectId) break;
      }
      if (!owningProjectId || !owningGateId) return;
      const now = new Date().toISOString();
      const next = gatesByProject[owningProjectId].map((g) =>
        g.id !== owningGateId
          ? g
          : {
              ...g,
              items: (g.items ?? []).map((it) =>
                it.id !== itemId
                  ? it
                  : {
                      ...it,
                      isChecked,
                      checkedAt: isChecked ? now : undefined,
                      checkedBy: isChecked ? actorDisplay : undefined,
                      updatedAt: now,
                    },
              ),
            },
      );
      set((s) => ({ gatesByProject: { ...s.gatesByProject, [owningProjectId]: next } }));
      if (mode !== 'api') {
        const allGates = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        saveStored(PROJECT_GATES_STORAGE_KEY, [...allGates.filter((g) => g.projectId !== owningProjectId), ...next]);
        return;
      }
      const res = await api.toggleProjectGateItem(itemId, isChecked, actorDisplay);
      if (res.error) toast.error(res.error);
    },

    transitionProjectGate: async (gateId, toStatus, comment, actorDisplay) => {
      const { mode, activeOrgId, gatesByProject } = get();
      let owningProjectId: string | null = null;
      let gate: ProjectGate | null = null;
      for (const [pid, gs] of Object.entries(gatesByProject)) {
        const found = gs.find((g) => g.id === gateId);
        if (found) { owningProjectId = pid; gate = found; break; }
      }
      if (!owningProjectId || !gate) { toast.error('Gate not found'); return false; }
      const fromStatus = gate.status;
      const now = new Date().toISOString();
      const patched: ProjectGate = { ...gate, status: toStatus, updatedAt: now };
      if (toStatus === 'passed') {
        patched.passedAt = now;
        patched.passedBy = actorDisplay;
      }
      const next = gatesByProject[owningProjectId].map((g) => (g.id === gateId ? patched : g));
      set((s) => ({ gatesByProject: { ...s.gatesByProject, [owningProjectId]: next } }));
      if (mode !== 'api' || !activeOrgId) {
        const allGates = loadStored<ProjectGate>(PROJECT_GATES_STORAGE_KEY);
        saveStored(PROJECT_GATES_STORAGE_KEY, [...allGates.filter((g) => g.projectId !== owningProjectId), ...next]);
        return true;
      }
      const res = await api.transitionProjectGate(activeOrgId, gateId, fromStatus, toStatus, comment, actorDisplay);
      if (res.error) { toast.error(res.error); return false; }
      return true;
    },

    updateWorkspacePolicy: async (patch) => {
      const { mode, activeOrgId } = get();
      set((s) => ({
        crApprovalRoles: patch.crApprovalRoles ?? s.crApprovalRoles,
        gatePolicy: patch.gatePolicy ?? s.gatePolicy,
        crApprovalEnabled: patch.crApprovalEnabled ?? s.crApprovalEnabled,
        crApprovalsRequired: patch.crApprovalsRequired ?? s.crApprovalsRequired,
      }));
      if (mode !== 'api' || !activeOrgId) return;
      const res = await api.updateOrgPolicy(activeOrgId, patch);
      if (res.error) toast.error(res.error);
    },

    reloadHealthThresholds: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ healthThresholds: DEFAULT_HEALTH_THRESHOLDS, healthThresholdsLoaded: true });
        return;
      }
      const res = await api.getOrgHealthThresholds(activeOrgId);
      set({
        healthThresholds: res.data ?? DEFAULT_HEALTH_THRESHOLDS,
        healthThresholdsLoaded: true,
      });
    },

    updateHealthThresholds: async (patch) => {
      const { mode, activeOrgId } = get();
      set((s) => ({ healthThresholds: { ...s.healthThresholds, ...patch } }));
      if (mode !== 'api' || !activeOrgId) return;
      const res = await api.updateOrgHealthThresholds(activeOrgId, patch);
      if (res.error) {
        toast.error(res.error);
      } else if (res.data) {
        // Reconcile with the normalized values the server actually stored.
        set({ healthThresholds: res.data });
      }
    },

    // ---------- R1: resource management mode (migration 0066) ----------
    reloadResourceMode: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        // Demo / localStorage mode: read the flip from localStorage. Default to
        // 'enterprise' there so the demo surfaces the enterprise UI (the Settings
        // toggle is backend-gated and unreachable in demo, so this is the only way
        // to see it). NOTE: the enterprise DATA tables (rate cards, departments,
        // role assignments) are all empty in demo, so cost and utilisation FIGURES
        // still match a Standard workspace — only the extra UI affordances appear.
        let next: ResourceMode = 'enterprise';
        try {
          const raw = localStorage.getItem(RESOURCE_MODE_STORAGE_KEY);
          if (raw != null) next = normalizeResourceMode(raw);
        } catch { /* ignore */ }
        set({ resourceMode: next, resourceModeLoaded: true });
        return;
      }
      const res = await api.getOrgResourceMode(activeOrgId);
      set({
        resourceMode: res.data ?? DEFAULT_RESOURCE_MODE,
        resourceModeLoaded: true,
      });
    },

    setResourceMode: async (mode) => {
      const next = normalizeResourceMode(mode);
      const { mode: storeMode, activeOrgId } = get();
      set({ resourceMode: next });
      if (storeMode !== 'api' || !activeOrgId) {
        try { localStorage.setItem(RESOURCE_MODE_STORAGE_KEY, next); } catch { /* ignore */ }
        return;
      }
      const res = await api.updateOrgResourceMode(activeOrgId, next);
      if (res.error) {
        toast.error(res.error);
      } else if (res.data) {
        set({ resourceMode: res.data });
      }
    },

    // ---------- Phase 34a: Notifications ----------

    reloadNotifications: async () => {
      const { mode } = get();
      if (mode !== 'api') {
        set({ notifications: [], notificationsLoaded: true });
        return;
      }
      const res = await api.listNotifications(100);
      set({ notifications: res.data ?? [], notificationsLoaded: true });
    },

    createNotification: async (data) => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) return;
      // Don't notify yourself of your own action
      if (supabase) {
        const { data: authUser } = await supabase.auth.getUser();
        if (authUser?.user?.id && authUser.user.id === data.recipientUserId) return;
      }
      const res = await api.createNotification({
        organizationId: activeOrgId,
        recipientUserId: data.recipientUserId,
        actorDisplay: data.actorDisplay,
        kind: data.kind,
        entityType: data.entityType,
        entityId: data.entityId,
        projectId: data.projectId,
        title: data.title,
        body: data.body,
      });
      if (res.error) {
        // Silent — notification failures shouldn't block workflow actions
        console.warn('Notification create failed:', res.error);
      }
    },

    markNotificationRead: async (id) => {
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n,
        ),
      }));
      const { mode } = get();
      if (mode !== 'api') return;
      const res = await api.markNotificationRead(id);
      if (res.error) toast.error(res.error);
    },

    markAllNotificationsRead: async () => {
      const now = new Date().toISOString();
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.isRead ? n : { ...n, isRead: true, readAt: now },
        ),
      }));
      const { mode } = get();
      if (mode !== 'api') return;
      const res = await api.markAllNotificationsRead();
      if (res.error) toast.error(res.error);
    },

    deleteNotification: async (id) => {
      set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) }));
      const { mode } = get();
      if (mode !== 'api') return;
      const res = await api.deleteNotification(id);
      if (res.error) toast.error(res.error);
    },

    // ---------- Phase 35a: Roadmap foundation ----------

    reloadResourceRoles: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ resourceRoles: [], resourceRolesLoaded: true });
        return;
      }
      const res = await api.listResourceRoles(activeOrgId);
      set({ resourceRoles: res.data ?? [], resourceRolesLoaded: true });
    },

    createResourceRole: async (data) => {
      const { mode, activeOrgId, resourceRoles } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('No workspace'); return null; }
      const res = await api.createResourceRole(activeOrgId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Create failed'); return null; }
      set({ resourceRoles: [...resourceRoles, res.data] });
      return res.data;
    },

    updateResourceRole: async (id, patch) => {
      const { mode, resourceRoles } = get();
      const now = new Date().toISOString();
      const next = resourceRoles.map((r) =>
        r.id === id
          ? {
              ...r,
              ...patch,
              description: patch.description === null ? undefined : patch.description ?? r.description,
              color: patch.color === null ? undefined : patch.color ?? r.color,
              updatedAt: now,
            }
          : r,
      );
      set({ resourceRoles: next });
      if (mode !== 'api') return;
      const res = await api.updateResourceRole(id, patch);
      if (res.error) toast.error(res.error);
    },

    deleteResourceRole: async (id) => {
      const { mode, resourceRoles, resourceRoleAssignments } = get();
      set({
        resourceRoles: resourceRoles.filter((r) => r.id !== id),
        // Cascade delete happens on the server too via FK, but reflect
        // it immediately in local state so the UI doesn't reference stale ids
        resourceRoleAssignments: resourceRoleAssignments.filter((a) => a.roleId !== id),
      });
      if (mode !== 'api') return;
      const res = await api.deleteResourceRole(id);
      if (res.error) toast.error(res.error);
    },

    reloadResourceRoleAssignments: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ resourceRoleAssignments: [], resourceRoleAssignmentsLoaded: true });
        return;
      }
      const res = await api.listResourceRoleAssignments(activeOrgId);
      set({
        resourceRoleAssignments: res.data ?? [],
        resourceRoleAssignmentsLoaded: true,
      });
    },

    // Standard-mode path: make this the resource's SOLE role (clear others).
    assignResourceRole: async (resourceName, roleId) => {
      const { mode, activeOrgId, resourceRoleAssignments } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('No workspace'); return; }
      const key = normalizeResourceName(resourceName);
      // Delete existing roles by row id (not by name string) so a differently
      // cased stored resource_name can't survive and duplicate the assignment.
      const existing = resourceRoleAssignments.filter((a) => normalizeResourceName(a.resourceName) === key);
      for (const a of existing) {
        const del = await api.deleteResourceRoleAssignment(a.id);
        if (del.error) { toast.error(del.error); return; }
      }
      const res = await api.upsertResourceRoleAssignment(activeOrgId, resourceName, roleId, true);
      if (res.error || !res.data) { toast.error(res.error ?? 'Assign failed'); return; }
      const others = resourceRoleAssignments.filter((a) => normalizeResourceName(a.resourceName) !== key);
      set({ resourceRoleAssignments: [...others, res.data] });
    },

    // Enterprise: add an additional role (primary only if it's the person's first).
    addResourceRole: async (resourceName, roleId) => {
      const { mode, activeOrgId, resourceRoleAssignments } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('No workspace'); return; }
      const key = normalizeResourceName(resourceName);
      const existing = resourceRoleAssignments.filter((a) => normalizeResourceName(a.resourceName) === key);
      if (existing.some((a) => a.roleId === roleId)) return; // already assigned
      const res = await api.upsertResourceRoleAssignment(activeOrgId, resourceName, roleId, existing.length === 0);
      if (res.error || !res.data) { toast.error(res.error ?? 'Add role failed'); return; }
      set({ resourceRoleAssignments: [...resourceRoleAssignments, res.data] });
    },

    setPrimaryResourceRole: async (resourceName, roleId) => {
      const { mode, activeOrgId, resourceRoleAssignments } = get();
      const key = normalizeResourceName(resourceName);
      set({
        resourceRoleAssignments: resourceRoleAssignments.map((a) =>
          normalizeResourceName(a.resourceName) === key ? { ...a, isPrimary: a.roleId === roleId } : a,
        ),
      });
      if (mode !== 'api' || !activeOrgId) return;
      const res = await api.setPrimaryResourceRole(activeOrgId, resourceName, roleId);
      if (res.error) toast.error(res.error);
    },

    unassignResourceRole: async (assignmentId) => {
      const { mode, activeOrgId, resourceRoleAssignments } = get();
      const removed = resourceRoleAssignments.find((a) => a.id === assignmentId);
      let next = resourceRoleAssignments.filter((a) => a.id !== assignmentId);
      // If the primary was removed and roles remain, promote a survivor.
      let promote: { resourceName: string; roleId: string } | null = null;
      if (removed?.isPrimary) {
        const key = normalizeResourceName(removed.resourceName);
        const survivors = next.filter((a) => normalizeResourceName(a.resourceName) === key);
        if (survivors.length && !survivors.some((a) => a.isPrimary)) {
          const first = survivors[0];
          next = next.map((a) => (a.id === first.id ? { ...a, isPrimary: true } : a));
          promote = { resourceName: first.resourceName, roleId: first.roleId };
        }
      }
      set({ resourceRoleAssignments: next });
      if (mode !== 'api') return;
      const res = await api.deleteResourceRoleAssignment(assignmentId);
      if (res.error) toast.error(res.error);
      if (promote && activeOrgId) {
        const p = await api.setPrimaryResourceRole(activeOrgId, promote.resourceName, promote.roleId);
        if (p.error) toast.error(p.error);
      }
    },

    // ---------- Enterprise model R2: rate cards (migration 0067) ----------
    reloadRateCards: async () => {
      const { mode, activeOrgId } = get();
      // Rate cards only exist in api (hosted) mode; demo has none → parity.
      if (mode !== 'api' || !activeOrgId) {
        set({ rateCards: [], rateCardsLoaded: true });
        return;
      }
      const res = await api.listRateCards(activeOrgId);
      set({ rateCards: res.data ?? [], rateCardsLoaded: true });
    },

    createRateCard: async (data) => {
      const { mode, activeOrgId, rateCards } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('Rate cards require a hosted workspace'); return null; }
      const res = await api.createRateCard(activeOrgId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Failed to add rate card'); return null; }
      set({ rateCards: [res.data, ...rateCards] });
      return res.data;
    },

    updateRateCard: async (id, patch) => {
      const { mode, rateCards } = get();
      if (mode !== 'api') return;
      const res = await api.updateRateCard(id, patch);
      if (res.error || !res.data) { toast.error(res.error ?? 'Update failed'); return; }
      set({ rateCards: rateCards.map((c) => (c.id === id ? res.data! : c)) });
    },

    deleteRateCard: async (id) => {
      const { mode, rateCards } = get();
      set({ rateCards: rateCards.filter((c) => c.id !== id) });
      if (mode !== 'api') return;
      const res = await api.deleteRateCard(id);
      if (res.error) toast.error(res.error);
    },

    // ---------- Enterprise model R3: skill proficiency (migration 0069) ----------
    reloadResourceSkillLevels: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ resourceSkillLevels: [], resourceSkillLevelsLoaded: true });
        return;
      }
      const res = await api.listResourceSkillLevels(activeOrgId);
      set({ resourceSkillLevels: res.data ?? [], resourceSkillLevelsLoaded: true });
    },

    setSkillLevel: async (nameNormalized, skill, level) => {
      const { mode, activeOrgId, resourceSkillLevels } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('Skill levels require a hosted workspace'); return; }
      const res = await api.upsertResourceSkillLevel(activeOrgId, nameNormalized, skill, level);
      if (res.error || !res.data) { toast.error(res.error ?? 'Failed to set level'); return; }
      const idx = resourceSkillLevels.findIndex(
        (l) => l.nameNormalized === nameNormalized && l.skill === skill,
      );
      if (idx >= 0) {
        const next = [...resourceSkillLevels];
        next[idx] = res.data;
        set({ resourceSkillLevels: next });
      } else {
        set({ resourceSkillLevels: [...resourceSkillLevels, res.data] });
      }
    },

    clearSkillLevel: async (id) => {
      const { mode, resourceSkillLevels } = get();
      set({ resourceSkillLevels: resourceSkillLevels.filter((l) => l.id !== id) });
      if (mode !== 'api') return;
      const res = await api.deleteResourceSkillLevel(id);
      if (res.error) toast.error(res.error);
    },

    // ---------- Enterprise model R4: departments (migration 0070) ----------
    reloadDepartments: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ departments: [], departmentsLoaded: true });
        return;
      }
      const res = await api.listDepartments(activeOrgId);
      set({ departments: res.data ?? [], departmentsLoaded: true });
    },

    createDepartment: async (data) => {
      const { mode, activeOrgId, departments } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('Departments require a hosted workspace'); return null; }
      const res = await api.createDepartment(activeOrgId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Failed to add department'); return null; }
      set({ departments: [...departments, res.data].sort((a, b) => a.name.localeCompare(b.name)) });
      return res.data;
    },

    updateDepartment: async (id, patch) => {
      const { mode, departments, resources } = get();
      const before = departments.find((d) => d.id === id);
      if (mode !== 'api') return;
      const res = await api.updateDepartment(id, patch);
      if (res.error || !res.data) { toast.error(res.error ?? 'Update failed'); return; }
      set({ departments: departments.map((d) => (d.id === id ? res.data! : d)).sort((a, b) => a.name.localeCompare(b.name)) });
      // If the name changed, keep registry rows pointing at the new name so the
      // picker stays consistent (team-member strings are historical metadata).
      if (before && patch.name && patch.name !== before.name) {
        for (const r of resources) {
          if (r.department === before.name) await get().updateResource(r.id, { department: patch.name });
        }
      }
    },

    deleteDepartment: async (id) => {
      const { mode, departments } = get();
      set({ departments: departments.filter((d) => d.id !== id) });
      if (mode !== 'api') return;
      const res = await api.deleteDepartment(id);
      if (res.error) toast.error(res.error);
    },

    reloadPipelineProjects: async (portfolioId) => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set((s) => ({
          pipelineProjectsByPortfolio: { ...s.pipelineProjectsByPortfolio, [portfolioId]: [] },
          pipelineProjectsLoadedForPortfolio: { ...s.pipelineProjectsLoadedForPortfolio, [portfolioId]: true },
        }));
        return;
      }
      const res = await api.listPipelineProjects(activeOrgId, portfolioId);
      set((s) => ({
        pipelineProjectsByPortfolio: { ...s.pipelineProjectsByPortfolio, [portfolioId]: res.data ?? [] },
        pipelineProjectsLoadedForPortfolio: { ...s.pipelineProjectsLoadedForPortfolio, [portfolioId]: true },
      }));
    },

    createPipelineProject: async (portfolioId, data) => {
      const { mode, activeOrgId, pipelineProjectsByPortfolio } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('No workspace'); return null; }
      const existing = pipelineProjectsByPortfolio[portfolioId] ?? [];
      // Default sort_order to end of current list so new items append
      const maxSortOrder = existing.reduce((max, p) => Math.max(max, p.sortOrder), -1);
      const res = await api.createPipelineProject(activeOrgId, {
        portfolioId,
        ...data,
        sortOrder: maxSortOrder + 1,
      });
      if (res.error || !res.data) { toast.error(res.error ?? 'Create failed'); return null; }
      set((s) => ({
        pipelineProjectsByPortfolio: {
          ...s.pipelineProjectsByPortfolio,
          [portfolioId]: [...existing, res.data!],
        },
      }));
      return res.data;
    },

    updatePipelineProject: async (id, patch) => {
      const { mode, pipelineProjectsByPortfolio } = get();
      let owningPortfolioId: string | null = null;
      for (const [pid, projects] of Object.entries(pipelineProjectsByPortfolio)) {
        if (projects.some((p) => p.id === id)) { owningPortfolioId = pid; break; }
      }
      if (!owningPortfolioId) return;
      const now = new Date().toISOString();
      const next = pipelineProjectsByPortfolio[owningPortfolioId].map((p) =>
        p.id === id ? { ...p, ...patch, updatedAt: now } : p,
      );
      set((s) => ({
        pipelineProjectsByPortfolio: { ...s.pipelineProjectsByPortfolio, [owningPortfolioId]: next },
      }));
      if (mode !== 'api') return;
      // Only send the fields that changed; the API layer knows how to serialize them
      const apiPatch: Parameters<typeof api.updatePipelineProject>[1] = {};
      if (patch.name !== undefined) apiPatch.name = patch.name;
      if (patch.description !== undefined) apiPatch.description = patch.description ?? null;
      if (patch.status !== undefined) apiPatch.status = patch.status;
      if (patch.priority !== undefined) apiPatch.priority = patch.priority;
      if (patch.businessValue !== undefined) apiPatch.businessValue = patch.businessValue ?? null;
      if (patch.effortWeeks !== undefined) apiPatch.effortWeeks = patch.effortWeeks ?? null;
      if (patch.targetStartDate !== undefined) apiPatch.targetStartDate = patch.targetStartDate ?? null;
      if (patch.targetEndDate !== undefined) apiPatch.targetEndDate = patch.targetEndDate ?? null;
      if (patch.sortOrder !== undefined) apiPatch.sortOrder = patch.sortOrder;
      if (patch.startedProjectId !== undefined) apiPatch.startedProjectId = patch.startedProjectId ?? null;
      const res = await api.updatePipelineProject(id, apiPatch);
      if (res.error) toast.error(res.error);
    },

    deletePipelineProject: async (id) => {
      const { mode, pipelineProjectsByPortfolio } = get();
      let owningPortfolioId: string | null = null;
      for (const [pid, projects] of Object.entries(pipelineProjectsByPortfolio)) {
        if (projects.some((p) => p.id === id)) { owningPortfolioId = pid; break; }
      }
      if (!owningPortfolioId) return;
      const next = pipelineProjectsByPortfolio[owningPortfolioId].filter((p) => p.id !== id);
      set((s) => ({
        pipelineProjectsByPortfolio: { ...s.pipelineProjectsByPortfolio, [owningPortfolioId]: next },
      }));
      if (mode !== 'api') return;
      const res = await api.deletePipelineProject(id);
      if (res.error) toast.error(res.error);
    },

    // ---------- Phase 35b: Pipeline resource needs ----------

    reloadPipelineResourceNeeds: async () => {
      const { mode, activeOrgId } = get();
      if (mode !== 'api' || !activeOrgId) {
        set({ pipelineResourceNeeds: [], pipelineResourceNeedsLoaded: true });
        return;
      }
      const res = await api.listPipelineResourceNeeds(activeOrgId);
      set({ pipelineResourceNeeds: res.data ?? [], pipelineResourceNeedsLoaded: true });
    },

    createPipelineResourceNeed: async (data) => {
      const { mode, activeOrgId, pipelineResourceNeeds } = get();
      if (mode !== 'api' || !activeOrgId) { toast.error('No workspace'); return null; }
      const res = await api.createPipelineResourceNeed(activeOrgId, data);
      if (res.error || !res.data) { toast.error(res.error ?? 'Create failed'); return null; }
      set({ pipelineResourceNeeds: [...pipelineResourceNeeds, res.data] });
      return res.data;
    },

    updatePipelineResourceNeed: async (id, patch) => {
      const { mode, pipelineResourceNeeds } = get();
      const now = new Date().toISOString();
      const next = pipelineResourceNeeds.map((n) =>
        n.id === id ? { ...n, ...patch, updatedAt: now } : n,
      );
      set({ pipelineResourceNeeds: next });
      if (mode !== 'api') return;
      const apiPatch: Parameters<typeof api.updatePipelineResourceNeed>[1] = {};
      if (patch.roleId !== undefined) apiPatch.roleId = patch.roleId;
      if (patch.count !== undefined) apiPatch.count = patch.count;
      if (patch.weeks !== undefined) apiPatch.weeks = patch.weeks ?? null;
      if (patch.startOffsetWeeks !== undefined) apiPatch.startOffsetWeeks = patch.startOffsetWeeks;
      if (patch.notes !== undefined) apiPatch.notes = patch.notes ?? null;
      const res = await api.updatePipelineResourceNeed(id, apiPatch);
      if (res.error) toast.error(res.error);
    },

    deletePipelineResourceNeed: async (id) => {
      const { mode, pipelineResourceNeeds } = get();
      set({ pipelineResourceNeeds: pipelineResourceNeeds.filter((n) => n.id !== id) });
      if (mode !== 'api') return;
      const res = await api.deletePipelineResourceNeed(id);
      if (res.error) toast.error(res.error);
    },

    // ---------- Project CRUD ----------

    setProjects: (projects) => {
      set({ projects });
      if (get().mode === 'localStorage') localStore().set(projects);
    },

    addProject: (project) => {
      updateLocalProjects((ps) => [...ps, project]);
      if (get().mode === 'api') {
        const { activeOrgId } = get();
        if (!activeOrgId) {
          toast.error('No active workspace.');
          return;
        }
        fireApi('Create project', api.createProject(activeOrgId, project).then((r) => ({ error: r.error })));
      }
    },

    importProjects: (newProjects) => {
      if (newProjects.length === 0) return;
      updateLocalProjects((ps) => [...ps, ...newProjects]);
      if (get().mode === 'api') {
        const { activeOrgId } = get();
        if (!activeOrgId) {
          toast.error('No active workspace.');
          return;
        }
        // Create each project row, then upsert its tasks (tasks FK the project,
        // so the row must exist first — hence await, not the debounced queue).
        void (async () => {
          let ok = 0;
          for (const project of newProjects) {
            const res = await api.createProject(activeOrgId, project);
            if (res.error) {
              toast.error(`Import: couldn't create "${project.name}"`);
              continue;
            }
            if (project.wbsTasks.length > 0) {
              const tr = await api.upsertTasks(project.id, project.wbsTasks);
              if (tr.error) toast.error(`Import: "${project.name}" saved, but some tasks failed`);
            }
            ok += 1;
          }
          if (ok > 0) toast.success(`Imported ${ok} project${ok !== 1 ? 's' : ''}`);
        })();
      } else {
        toast.success(`Imported ${newProjects.length} project${newProjects.length !== 1 ? 's' : ''}`);
      }
    },

    replaceProjectSchedule: (projectId, tasks) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const withSuccessors = recalculateSuccessors(tasks);
          const renumbered = renumberAllWbs(withSuccessors);
          const rolled = recomputeSummaryRollups(renumbered);
          return { ...p, wbsTasks: rolled, completion: rollUpCompletion(rolled) ?? p.completion };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) fireApi('Import schedule', api.replaceTasks(projectId, proj.wbsTasks));
      }
      persistRolledCompletion(projectId);
    },

    createEstimatedProject: (project, tasks, budgetLines) => {
      // Process the WBS skeleton the same way the schedule editor would.
      const processedTasks = recomputeSummaryRollups(
        renumberAllWbs(recalculateSuccessors(tasks)),
      );
      const seeded: Project = { ...project, wbsTasks: processedTasks };

      // Optimistic local insert (schedule visible immediately in both modes).
      updateLocalProjects((ps) => [...ps, seeded]);

      if (get().mode === 'api') {
        const { activeOrgId } = get();
        if (!activeOrgId) {
          toast.error('No active workspace.');
          return;
        }
        void (async () => {
          const res = await api.createProject(activeOrgId, seeded);
          if (res.error) {
            toast.error(`Couldn't create "${seeded.name}"`);
            return;
          }
          if (processedTasks.length > 0) {
            const tr = await api.upsertTasks(seeded.id, processedTasks);
            if (tr.error) toast.error(`"${seeded.name}" created, but some tasks failed to save`);
          }
          // Safe now that the project row exists — importBudgetLines ensures the
          // budget version FK before writing lines.
          if (budgetLines.length > 0) get().importBudgetLines(seeded.id, budgetLines, false);
          toast.success(`Created "${seeded.name}" from estimate`);
        })();
      } else {
        if (budgetLines.length > 0) get().importBudgetLines(seeded.id, budgetLines, false);
        toast.success(`Created "${seeded.name}" from estimate`);
      }
    },

    updateProject: (id, updates) => {
      updateLocalProjects((ps) =>
        ps.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      );
      if (get().mode === 'api') {
        fireApi('Update project', api.updateProject(id, updates));
      }
    },

    deleteProject: (id) => {
      updateLocalProjects((ps) => ps.filter((p) => p.id !== id));
      if (get().mode === 'api') {
        fireApi('Delete project', api.deleteProject(id));
      }
    },

    resetToSeed: () => {
      // Only meaningful in localStorage mode
      if (get().mode === 'localStorage') {
        // Phase 42: reset the grouping tiers alongside projects so the demo
        // returns to a fully-populated portfolios/programs state.
        set({ projects: seedProjects, portfolios: seedPortfolios, programs: seedPrograms });
        localStore().set(seedProjects);
        saveStored(PORTFOLIOS_STORAGE_KEY, seedPortfolios);
        saveStored(PROGRAMS_STORAGE_KEY, seedPrograms);
      } else {
        toast.warning('Reset is only available in localStorage mode. Logged-in users manage data via the database.');
      }
    },

    // ---------- Portfolio CRUD (Phase 18) ----------

    addPortfolio: (portfolio) => {
      // Optimistic local insert
      set((s) => ({ portfolios: [...s.portfolios, portfolio] }));
      if (get().mode === 'api') {
        const { activeOrgId } = get();
        if (!activeOrgId) {
          toast.error('No active workspace.');
          return;
        }
        fireApi(
          'Create portfolio',
          api.createPortfolio(activeOrgId, portfolio).then((r) => ({ error: r.error })),
        );
      } else {
        saveStored(PORTFOLIOS_STORAGE_KEY, get().portfolios);
      }
    },

    updatePortfolio: (id, updates) => {
      set((s) => ({
        portfolios: s.portfolios.map((p) =>
          p.id === id ? { ...p, ...updates } : p,
        ),
      }));
      if (get().mode === 'api') {
        fireApi('Update portfolio', api.updatePortfolio(id, updates));
      } else {
        saveStored(PORTFOLIOS_STORAGE_KEY, get().portfolios);
      }
    },

    deletePortfolio: (id) => {
      // Optimistic: remove locally, also clear portfolioId on member projects
      // (mirrors the server-side ON DELETE SET NULL behavior)
      set((s) => ({
        portfolios: s.portfolios.filter((p) => p.id !== id),
        projects: s.projects.map((p) =>
          p.portfolioId === id ? { ...p, portfolioId: null } : p,
        ),
      }));
      if (get().mode === 'api') {
        fireApi('Delete portfolio', api.deletePortfolio(id));
      } else {
        // Persist both the portfolios list and the projects (portfolioId cleared)
        saveStored(PORTFOLIOS_STORAGE_KEY, get().portfolios);
        localStore().set(get().projects);
      }
    },

    assignProjectsToPortfolio: (projectIds, portfolioId) => {
      // Optimistic: update portfolioId on local projects
      const idSet = new Set(projectIds);
      updateLocalProjects((ps) =>
        ps.map((p) => (idSet.has(p.id) ? { ...p, portfolioId } : p)),
      );
      if (get().mode === 'api') {
        fireApi(
          'Assign projects',
          api.assignProjectsToPortfolio(projectIds, portfolioId).then((r) => ({
            error: r.error,
          })),
        );
      }
    },

    // ---------- Program CRUD ----------

    addProgram: (program) => {
      set((s) => ({ programs: [...s.programs, program] }));
      if (get().mode === 'api') {
        const { activeOrgId } = get();
        if (!activeOrgId) {
          toast.error('No active workspace.');
          return;
        }
        fireApi('Create program', api.createProgram(activeOrgId, program).then((r) => ({ error: r.error })));
      } else {
        saveStored(PROGRAMS_STORAGE_KEY, get().programs);
      }
    },

    updateProgram: (id, updates) => {
      set((s) => ({
        programs: s.programs.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      }));
      if (get().mode === 'api') {
        fireApi('Update program', api.updateProgram(id, updates));
      } else {
        saveStored(PROGRAMS_STORAGE_KEY, get().programs);
      }
    },

    deleteProgram: (id) => {
      // Optimistic: remove locally + clear programId on member projects (mirrors ON DELETE SET NULL)
      set((s) => ({
        programs: s.programs.filter((p) => p.id !== id),
        projects: s.projects.map((p) => (p.programId === id ? { ...p, programId: null } : p)),
      }));
      if (get().mode === 'api') {
        fireApi('Delete program', api.deleteProgram(id));
      } else {
        // Persist both the programs list and the projects (programId cleared)
        saveStored(PROGRAMS_STORAGE_KEY, get().programs);
        localStore().set(get().projects);
      }
    },

    assignProjectsToProgram: (projectIds, programId) => {
      const idSet = new Set(projectIds);
      updateLocalProjects((ps) => ps.map((p) => (idSet.has(p.id) ? { ...p, programId } : p)));
      if (get().mode === 'api') {
        fireApi(
          'Assign projects to program',
          api.assignProjectsToProgram(projectIds, programId).then((r) => ({ error: r.error })),
        );
      }
    },

    // ---------- Task CRUD ----------

    addTask: (projectId, task) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const withSuccessors = recalculateSuccessors([...p.wbsTasks, task]);
          // Phase 30.36: renumber WBS to keep sequence clean. Safety-gated
          // inside renumberAllWbs — only runs when all WBS are numeric.
          const renumbered = renumberAllWbs(withSuccessors);
          const rolled = recomputeSummaryRollups(renumbered);
          return { ...p, wbsTasks: rolled, completion: rollUpCompletion(rolled) ?? p.completion };
        }),
      );
      if (get().mode === 'api') {
        // Upsert this task; also re-upsert any tasks whose successor field changed
        // For simplicity, upsert all tasks of this project
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) {
          scheduleTaskUpsert(projectId, proj.wbsTasks, 'Add task');
        }
      }
      persistRolledCompletion(projectId);
    },

    /**
     * Phase 30.32: insert at a specific position. Unlike addTask, this
     * preserves the surrounding task order rather than appending. Used by
     * the schedule's "insert row" flow so PMs can build a schedule from
     * middle-out, not only bottom-up.
     */
    insertTaskAt: (projectId, task, afterTaskId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          let inserted: Task[];
          if (afterTaskId === null) {
            // Prepend
            inserted = [task, ...p.wbsTasks];
          } else {
            const idx = p.wbsTasks.findIndex((t) => t.taskId === afterTaskId);
            if (idx < 0) {
              // Fallback: not found → append (matches addTask behavior)
              inserted = [...p.wbsTasks, task];
            } else {
              inserted = [
                ...p.wbsTasks.slice(0, idx + 1),
                task,
                ...p.wbsTasks.slice(idx + 1),
              ];
            }
          }
          const withSuccessors = recalculateSuccessors(inserted);
          // Phase 30.36: renumber all WBS after mid-schedule insert so the
          // sequence stays clean (1, 2, 3, 4, 5) instead of drifting into
          // (1, 2, 3, 6, 4, 5) when the sibling-of-above logic collided.
          // Safety-gated inside renumberAllWbs: only runs when all WBS are
          // numeric (custom "OPS-1" schemes are left alone).
          const renumbered = renumberAllWbs(withSuccessors);
          const rolled = recomputeSummaryRollups(renumbered);
          return { ...p, wbsTasks: rolled, completion: rollUpCompletion(rolled) ?? p.completion };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) {
          scheduleTaskUpsert(projectId, proj.wbsTasks, 'Insert task');
        }
      }
      persistRolledCompletion(projectId);
    },

    renumberProjectWbs: (projectId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const renumbered = renumberAllWbs(p.wbsTasks);
          if (renumbered === p.wbsTasks) return p;
          return { ...p, wbsTasks: recomputeSummaryRollups(renumbered) };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) {
          scheduleTaskUpsert(projectId, proj.wbsTasks, 'Renumber WBS');
        }
      }
    },

    updateTask: (projectId, taskId, updates) => {
      let updatedTasksForApi: Task[] = [];
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const updated = p.wbsTasks.map((t) =>
            t.taskId === taskId ? { ...t, ...updates } : t,
          );
          const needsRecalc = 'predecessors' in updates;
          const withSuccessors = needsRecalc ? recalculateSuccessors(updated) : updated;
          // Phase 19.2: any task date / duration / completion change can shift
          // a parent summary's rollup. Always recompute.
          const finalTasks = recomputeSummaryRollups(withSuccessors);
          updatedTasksForApi = finalTasks;
          return { ...p, wbsTasks: finalTasks, completion: rollUpCompletion(finalTasks) ?? p.completion };
        }),
      );
      if (get().mode === 'api') {
        if ('predecessors' in updates) {
          // Successor strings on other tasks may have changed too — upsert all
          scheduleTaskUpsert(projectId, updatedTasksForApi, 'Update task');
        } else {
          // Just upsert the one updated task PLUS any summaries whose rollup
          // values may have changed
          const justChanged = updatedTasksForApi.find((t) => t.taskId === taskId);
          const summariesToWrite = updatedTasksForApi.filter((t) => t.type === 'summary');
          const tasksToUpsert = justChanged
            ? [justChanged, ...summariesToWrite.filter((s) => s.taskId !== taskId)]
            : summariesToWrite;
          if (tasksToUpsert.length > 0) {
            scheduleTaskUpsert(projectId, tasksToUpsert, 'Update task');
          }
        }
      }
      persistRolledCompletion(projectId);
    },

    deleteTask: (projectId, taskId) => {
      // Snapshot the whole schedule so an accidental delete can be reverted
      // exactly (restoring the row alone wouldn't fix predecessor strings/WBS).
      const prevTasks = get().projects.find((p) => p.id === projectId)?.wbsTasks ?? null;
      let cleanedTasks: Task[] = [];
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const filtered = p.wbsTasks.filter((t) => t.taskId !== taskId);
          const cleaned = cleanupReferences(filtered, taskId);
          const withSuccessors = recalculateSuccessors(cleaned);
          cleanedTasks = recomputeSummaryRollups(withSuccessors);
          return { ...p, wbsTasks: cleanedTasks, completion: rollUpCompletion(cleanedTasks) ?? p.completion };
        }),
      );
      if (get().mode === 'api') {
        // Delete the row, then upsert the others to fix predecessor strings
        api.deleteTask(projectId, taskId).then((r) => {
          if (r.error) {
            toast.error(`Delete task: ${r.error}. Refresh to re-sync.`, 6000);
            return;
          }
          scheduleTaskUpsert(projectId, cleanedTasks, 'Update tasks');
        });
      }
      persistRolledCompletion(projectId);
      if (prevTasks) {
        toast.undo('Task deleted', () => get().replaceProjectSchedule(projectId, prevTasks));
      }
    },

    reorderTasks: (projectId, tasks) => {
      // Phase 33: run the full task-mutation pipeline so drag-to-reorder
      // yields a clean state — recalculate successors (some edges may
      // now be forward/backward), renumber WBS to reflect the new order,
      // and roll up summary tasks.
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const withSuccessors = recalculateSuccessors(tasks);
          const renumbered = renumberAllWbs(withSuccessors);
          return { ...p, wbsTasks: recomputeSummaryRollups(renumbered) };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (proj) scheduleTaskUpsert(projectId, proj.wbsTasks, 'Reorder tasks');
      }
    },

    // ---------- Phase 30.6: WBS hierarchy operations ----------

    indentTask: (projectId, taskId) => {
      const proj = get().projects.find((p) => p.id === projectId);
      if (!proj) return false;
      const idx = proj.wbsTasks.findIndex((t) => t.taskId === taskId);
      if (idx < 0) return false;

      const result = indentTaskHierarchy(proj.wbsTasks, idx);
      if (!result.ok) {
        toast.error(result.reason, 3500);
        return false;
      }

      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const withSuccessors = recalculateSuccessors(result.tasks);
          return { ...p, wbsTasks: recomputeSummaryRollups(withSuccessors) };
        }),
      );
      if (get().mode === 'api') {
        const updated = get().projects.find((p) => p.id === projectId);
        if (updated) scheduleTaskUpsert(projectId, updated.wbsTasks, 'Indent task');
      }
      return true;
    },

    outdentTask: (projectId, taskId) => {
      const proj = get().projects.find((p) => p.id === projectId);
      if (!proj) return false;
      const idx = proj.wbsTasks.findIndex((t) => t.taskId === taskId);
      if (idx < 0) return false;

      const result = outdentTaskHierarchy(proj.wbsTasks, idx);
      if (!result.ok) {
        toast.error(result.reason, 3500);
        return false;
      }

      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const withSuccessors = recalculateSuccessors(result.tasks);
          return { ...p, wbsTasks: recomputeSummaryRollups(withSuccessors) };
        }),
      );
      if (get().mode === 'api') {
        const updated = get().projects.find((p) => p.id === projectId);
        if (updated) scheduleTaskUpsert(projectId, updated.wbsTasks, 'Outdent task');
      }
      return true;
    },

    addSubtaskUnder: (projectId, parentTaskId) => {
      const proj = get().projects.find((p) => p.id === projectId);
      if (!proj) return;
      const parent = proj.wbsTasks.find((t) => t.taskId === parentTaskId);
      if (!parent) return;
      const parentWbs = (parent.wbs || '').trim();
      if (!parentWbs) {
        toast.error('Parent row has no WBS — set one first.', 3500);
        return;
      }

      // Allocate the next taskId and the next child WBS under the parent.
      const nextTaskId =
        proj.wbsTasks.reduce((m, t) => Math.max(m, t.taskId), 0) + 1;
      const newWbs = nextChildWbs(proj.wbsTasks, parentWbs);

      const today = new Date().toISOString().split('T')[0];
      const dur = 5;
      const endDate = (() => {
        const d = new Date(today + 'T00:00:00');
        d.setDate(d.getDate() + dur - 1);
        return d.toISOString().split('T')[0];
      })();

      const newTask: Task = {
        taskId: nextTaskId,
        wbs: newWbs,
        task: '',
        type: 'task',
        startDate: today,
        endDate,
        duration: dur,
        percentComplete: 0,
        predecessors: '',
        successors: '',
        resources: [],
        allocation: [],
      };

      // Promote the parent to Summary if it isn't already (matches indent
      // semantics — a row with children is a summary).
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const promoted = p.wbsTasks.map((t) =>
            t.taskId === parentTaskId && t.type !== 'summary' && t.type !== 'milestone'
              ? { ...t, type: 'summary' as const }
              : t,
          );
          const withSuccessors = recalculateSuccessors([...promoted, newTask]);
          return { ...p, wbsTasks: recomputeSummaryRollups(withSuccessors) };
        }),
      );
      if (get().mode === 'api') {
        const updated = get().projects.find((p) => p.id === projectId);
        if (updated) scheduleTaskUpsert(projectId, updated.wbsTasks, 'Add subtask');
      }
    },

    // ---------- Risk CRUD ----------

    addRisk: (projectId, risk) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, risks: [...p.risks, risk] } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add risk', api.upsertRisk(projectId, risk));
      }
    },

    updateRisk: (projectId, riskId, updates) => {
      let updatedRisk: Risk | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            risks: p.risks.map((r) => {
              if (r.id !== riskId) return r;
              const merged = { ...r, ...updates };
              updatedRisk = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updatedRisk) {
        fireApi('Update risk', api.upsertRisk(projectId, updatedRisk));
      }
    },

    deleteRisk: (projectId, riskId) => {
      const snapshot = get().projects.find((p) => p.id === projectId)?.risks.find((r) => r.id === riskId) ?? null;
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, risks: p.risks.filter((r) => r.id !== riskId) }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Delete risk', api.deleteRisk(riskId));
      }
      if (snapshot) toast.undo('Risk deleted', () => get().addRisk(projectId, snapshot));
    },

    // ---------- Phase 30.9 (Budget B2): Budget line CRUD ----------
    // Phase 30.12 (Budget B3): now version-scoped.

    addBudgetLine: (projectId, line) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, budgetLines: [...(p.budgetLines ?? []), line] }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add budget line', api.upsertBudgetLine(projectId, line));
      }
    },

    importBudgetLines: (projectId, imported, replace) => {
      if (imported.length === 0) return;
      void (async () => {
        const proj0 = get().projects.find((p) => p.id === projectId);
        if (!proj0) return;

        // Resolve the target budget version, creating a default one if none.
        let versionId =
          proj0.currentBudgetVersionId ||
          proj0.budgetVersions?.find((v) => v.isDefault)?.id ||
          proj0.budgetVersions?.[0]?.id ||
          null;
        if (!versionId) {
          versionId = `${projectIdPrefix(projectId)}-V-001`;
          // Awaits the version row (and project FK) before any line references it.
          await get().ensureCurrentBudgetVersion(projectId, {
            id: versionId,
            name: 'Current',
            isDefault: true,
          });
        }

        // Generate sequential project-scoped ids, continuing past existing lines.
        const prefix = `${projectIdPrefix(projectId)}-BUD`;
        const existing = get().projects.find((p) => p.id === projectId)?.budgetLines ?? [];
        let maxN = existing.reduce((m, e) => {
          if (!e.id.startsWith(`${prefix}-`)) return m;
          const n = parseInt(e.id.slice(prefix.length + 1), 10);
          return Number.isNaN(n) ? m : Math.max(m, n);
        }, 0);
        const newLines: BudgetLine[] = imported.map((l) => ({
          id: `${prefix}-${String((maxN += 1)).padStart(3, '0')}`,
          budgetVersionId: versionId as string,
          category: l.category,
          description: l.description,
          plannedAmount: l.plannedAmount,
          actualAmount: l.actualAmount,
          ...(l.costType ? { costType: l.costType } : {}),
        }));

        // Optimistic local update (replace = drop this version's existing lines).
        updateLocalProjects((ps) =>
          ps.map((p) => {
            if (p.id !== projectId) return p;
            const kept = replace
              ? (p.budgetLines ?? []).filter((l) => l.budgetVersionId !== versionId)
              : (p.budgetLines ?? []);
            return { ...p, budgetLines: [...kept, ...newLines] };
          }),
        );

        if (get().mode === 'api') {
          if (replace) {
            const dRes = await api.deleteBudgetLinesForVersion(projectId, versionId as string);
            if (dRes.error) toast.error('Import: could not clear existing budget lines');
          }
          const uRes = await api.upsertBudgetLines(projectId, newLines);
          if (uRes.error) toast.error('Import: some budget lines failed to save');
          else toast.success(`Imported ${newLines.length} budget line${newLines.length !== 1 ? 's' : ''}`);
        } else {
          toast.success(`Imported ${newLines.length} budget line${newLines.length !== 1 ? 's' : ''}`);
        }
      })();
    },

    updateBudgetLine: (projectId, versionId, lineId, updates) => {
      // Phase 30.12: the local filter must match on BOTH id and version,
      // because two versions can have lines with the same id (post-fork).
      let updatedLine: BudgetLine | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            budgetLines: (p.budgetLines ?? []).map((l) => {
              if (l.id !== lineId || l.budgetVersionId !== versionId) return l;
              const merged = { ...l, ...updates };
              updatedLine = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updatedLine) {
        fireApi('Update budget line', api.upsertBudgetLine(projectId, updatedLine));
      }
    },

    deleteBudgetLine: (projectId, versionId, lineId) => {
      const snapshot = get().projects.find((p) => p.id === projectId)?.budgetLines
        ?.find((l) => l.id === lineId && l.budgetVersionId === versionId) ?? null;
      // Phase 30.10: when a budget line is deleted, the DB had ON DELETE
      // SET NULL on change_requests.budget_line_id. Phase 30.12 dropped
      // that FK constraint, but the application-level cleanup is still
      // worth doing: clear matching CR linkages so the UI doesn't show
      // stale "linked CR" annotations until refresh.
      //
      // Phase 30.12: only clear CR linkage if it points at the CURRENT
      // version's line. CRs pointing at non-current versions' lines were
      // already in a "broken link" state (UI filters them out); deleting
      // such a line doesn't change anything user-visible.
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const isCurrent = p.currentBudgetVersionId === versionId;
          return {
            ...p,
            budgetLines: (p.budgetLines ?? []).filter(
              (l) => !(l.id === lineId && l.budgetVersionId === versionId),
            ),
            changes: isCurrent
              ? p.changes.map((c) =>
                  c.budgetLineId === lineId ? { ...c, budgetLineId: null } : c,
                )
              : p.changes,
          };
        }),
      );
      if (get().mode === 'api') {
        fireApi('Delete budget line', api.deleteBudgetLine(projectId, versionId, lineId));
      }
      if (snapshot) toast.undo('Budget line deleted', () => get().addBudgetLine(projectId, snapshot));
    },

    // ---------- Phase 30.12 (Budget B3): Budget version CRUD ----------

    addBudgetVersion: (projectId, version) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, budgetVersions: [...(p.budgetVersions ?? []), version] }
            : p,
        ),
      );
      if (get().mode === 'api') {
        const user = get().mode === 'api' ? undefined : undefined; // 30.13 will pass user.id
        fireApi('Add budget version', api.upsertBudgetVersion(projectId, version, user));
      }
    },

    updateBudgetVersion: (projectId, versionId, updates) => {
      let updatedVersion: BudgetVersion | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            budgetVersions: (p.budgetVersions ?? []).map((v) => {
              if (v.id !== versionId) return v;
              const merged = { ...v, ...updates };
              updatedVersion = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updatedVersion) {
        fireApi(
          'Update budget version',
          api.upsertBudgetVersion(projectId, updatedVersion),
        );
      }
    },

    deleteBudgetVersion: (projectId, versionId) => {
      // The DB will cascade-delete the version's lines (ON DELETE CASCADE
      // on budget_lines_version_fkey). Mirror locally.
      // Also: if the deleted version was current, clear the pointer locally
      // — the user will need to pick a new current. The DB has ON DELETE
      // SET NULL on projects.current_budget_version_id so it'll match.
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const wasCurrent = p.currentBudgetVersionId === versionId;
          return {
            ...p,
            budgetVersions: (p.budgetVersions ?? []).filter((v) => v.id !== versionId),
            budgetLines: (p.budgetLines ?? []).filter((l) => l.budgetVersionId !== versionId),
            currentBudgetVersionId: wasCurrent ? null : p.currentBudgetVersionId,
          };
        }),
      );
      if (get().mode === 'api') {
        fireApi(
          'Delete budget version',
          api.deleteBudgetVersion(projectId, versionId),
        );
      }
    },

    setCurrentBudgetVersion: (projectId, versionId) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, currentBudgetVersionId: versionId } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi(
          'Set current budget version',
          api.updateProject(projectId, { currentBudgetVersionId: versionId }),
        );
      }
    },

    /**
     * Phase 30.12.2: awaitable initial-version setup. See interface comment
     * for full rationale. Optimistic local update happens immediately so the
     * UI reacts; the function awaits the API writes so the caller can be
     * confident the server has both records committed before doing anything
     * that depends on them (like creating the first budget line, or
     * updating the project row in a way that would fire a realtime echo).
     */
    ensureCurrentBudgetVersion: async (projectId, version) => {
      // Optimistic local update — version added AND pointer set in one
      // state transition so the UI reflects the complete consistent state.
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? {
                ...p,
                budgetVersions: [...(p.budgetVersions ?? []), version],
                currentBudgetVersionId: version.id,
              }
            : p,
        ),
      );

      if (get().mode !== 'api') return; // localStorage mode: nothing more to do

      // Server commits, in order. We do these sequentially (not in parallel)
      // because the project row's FK on current_budget_version_id requires
      // the version to exist first.
      const vRes = await api.upsertBudgetVersion(projectId, version);
      if (vRes.error) {
        toast.error(`Failed to create budget version: ${vRes.error}`);
        return;
      }
      const pRes = await api.updateProject(projectId, {
        currentBudgetVersionId: version.id,
      });
      if (pRes.error) {
        toast.error(`Failed to set current version: ${pRes.error}`);
      }
    },

    /**
     * Phase 30.13: fork a version. Copies all lines from the source version
     * into the new version (line IDs preserved across versions — see
     * interface comment). Awaitable.
     *
     * Errors during line copy are logged but don't roll back already-created
     * lines. The user sees a toast with the failure count. This is a
     * deliberate tradeoff: rolling back partial copies would require us to
     * track which lines committed and which didn't, which is meaningfully
     * more code for a low-probability scenario. Acceptable for v1; the user
     * can inspect the scenario and re-fork if needed.
     */
    forkBudgetVersion: async (projectId, args) => {
      const project = get().projects.find((p) => p.id === projectId);
      if (!project) throw new Error('Project not found');

      const { sourceVersionId, newVersion } = args;
      const sourceLines = sourceVersionId
        ? (project.budgetLines ?? []).filter(
            (l) => l.budgetVersionId === sourceVersionId,
          )
        : [];

      // Build the copied lines — same id, same data, just new budgetVersionId.
      const copiedLines: BudgetLine[] = sourceLines.map((l) => ({
        ...l,
        budgetVersionId: newVersion.id,
        createdAt: undefined,
        updatedAt: undefined,
      }));

      // Optimistic local update — add the version row AND all copied lines.
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? {
                ...p,
                budgetVersions: [...(p.budgetVersions ?? []), newVersion],
                budgetLines: [...(p.budgetLines ?? []), ...copiedLines],
              }
            : p,
        ),
      );

      if (get().mode !== 'api') return newVersion;

      // Server commits: version first, then all lines.
      const vRes = await api.upsertBudgetVersion(projectId, newVersion);
      if (vRes.error) {
        // The FK from budget_lines to budget_versions means line inserts
        // would fail anyway; bail early with a clear error.
        throw new Error(`Failed to create version: ${vRes.error}`);
      }

      // Lines in parallel — they don't depend on each other.
      const lineResults = await Promise.all(
        copiedLines.map((l) => api.upsertBudgetLine(projectId, l)),
      );
      const failures = lineResults.filter((r) => r.error);
      if (failures.length > 0) {
        toast.error(
          `Created scenario but ${failures.length} of ${copiedLines.length} line copies failed. You may want to inspect the scenario.`,
        );
      }

      return newVersion;
    },

    /**
     * Phase 30.13: promote a scenario to current.
     *
     * Three server operations in sequence:
     *   1. Rename the old current version: append " (archived YYYY-MM-DD)"
     *   2. Update the project's current_budget_version_id pointer
     *   3. Clear any CR.budget_line_id that points at a line not in the new
     *      current's lines.
     *
     * All updated locally first (optimistic), then awaited server-side.
     */
    promoteBudgetVersion: async (projectId, toVersionId) => {
      const project = get().projects.find((p) => p.id === projectId);
      if (!project) throw new Error('Project not found');
      if (project.currentBudgetVersionId === toVersionId) {
        // Already current — nothing to do.
        return;
      }
      const oldCurrentId = project.currentBudgetVersionId;
      const oldCurrent = oldCurrentId
        ? (project.budgetVersions ?? []).find((v) => v.id === oldCurrentId)
        : null;

      // Compute archive name for the old current version (if any).
      const archiveSuffix = ` (archived ${new Date().toISOString().slice(0, 10)})`;
      const archivedName = oldCurrent
        ? oldCurrent.name + archiveSuffix
        : null;

      // Compute which CR linkages will be cleared. A CR's budget_line_id is
      // cleared if no line in the new current version has that id.
      const newCurrentLineIds = new Set(
        (project.budgetLines ?? [])
          .filter((l) => l.budgetVersionId === toVersionId)
          .map((l) => l.id),
      );
      const crsToClear = (project.changes ?? []).filter(
        (cr) =>
          cr.budgetLineId !== null &&
          cr.budgetLineId !== undefined &&
          !newCurrentLineIds.has(cr.budgetLineId),
      );

      // Optimistic local update — all three changes in one transition.
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            currentBudgetVersionId: toVersionId,
            budgetVersions: (p.budgetVersions ?? []).map((v) =>
              oldCurrent && v.id === oldCurrent.id && archivedName
                ? { ...v, name: archivedName, isDefault: false }
                : v,
            ),
            changes: p.changes.map((cr) => {
              const willClear = crsToClear.some((c) => c.id === cr.id);
              return willClear ? { ...cr, budgetLineId: null } : cr;
            }),
          };
        }),
      );

      if (get().mode !== 'api') return;

      // Server commits, in order.
      //   - Old current rename (if applicable)
      if (oldCurrent && archivedName) {
        const renameRes = await api.upsertBudgetVersion(projectId, {
          ...oldCurrent,
          name: archivedName,
          isDefault: false,
        });
        if (renameRes.error) {
          throw new Error(`Failed to archive old current: ${renameRes.error}`);
        }
      }
      //   - Update project pointer
      const pRes = await api.updateProject(projectId, {
        currentBudgetVersionId: toVersionId,
      });
      if (pRes.error) {
        throw new Error(`Failed to update current pointer: ${pRes.error}`);
      }
      //   - Clear stale CR linkages
      const clearResults = await Promise.all(
        crsToClear.map((cr) =>
          api.upsertChangeRequest(projectId, { ...cr, budgetLineId: null }),
        ),
      );
      const clearFails = clearResults.filter((r) => r.error);
      if (clearFails.length > 0) {
        toast.error(
          `Promoted, but ${clearFails.length} of ${crsToClear.length} CR linkage clears failed. The local state shows them cleared; refresh to verify.`,
        );
      }
    },

    // ---------- Phase 30.15 (Budget B4): BudgetLinePeriod CRUD ----------

    /**
     * Helper used by upsert / delete to keep the local line.planned_amount
     * and line.actual_amount in sync with the sum of its periods, mirroring
     * the server-side trigger. Without this, the UI would briefly show
     * stale line totals between the optimistic period update and the
     * server's echo.
     */
    // (defined inline below via a closure-style helper)

    upsertBudgetLinePeriod: (projectId, period) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const allPeriods = p.budgetLinePeriods ?? [];
          const matchIdx = allPeriods.findIndex(
            (x) =>
              x.budgetVersionId === period.budgetVersionId &&
              x.budgetLineId === period.budgetLineId &&
              x.periodStart === period.periodStart,
          );
          const newPeriods =
            matchIdx >= 0
              ? allPeriods.map((x, i) => (i === matchIdx ? period : x))
              : [...allPeriods, period];

          // Recompute the parent line's planned/actual as sum of remaining
          // periods (mirrors the server-side recompute trigger).
          const linePeriods = newPeriods.filter(
            (x) =>
              x.budgetVersionId === period.budgetVersionId &&
              x.budgetLineId === period.budgetLineId,
          );
          const newPlanned = linePeriods.reduce(
            (s, x) => s + (x.plannedAmount || 0),
            0,
          );
          const newActual = linePeriods.reduce(
            (s, x) => s + (x.actualAmount || 0),
            0,
          );
          const newLines = (p.budgetLines ?? []).map((l) =>
            l.id === period.budgetLineId &&
            l.budgetVersionId === period.budgetVersionId
              ? { ...l, plannedAmount: newPlanned, actualAmount: newActual }
              : l,
          );

          return {
            ...p,
            budgetLinePeriods: newPeriods,
            budgetLines: newLines,
          };
        }),
      );
      if (get().mode === 'api') {
        fireApi(
          'Upsert budget line period',
          api.upsertBudgetLinePeriod(projectId, period),
        );
      }
    },

    deleteBudgetLinePeriod: (projectId, versionId, lineId, periodStart) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const allPeriods = p.budgetLinePeriods ?? [];
          const newPeriods = allPeriods.filter(
            (x) =>
              !(
                x.budgetVersionId === versionId &&
                x.budgetLineId === lineId &&
                x.periodStart === periodStart
              ),
          );

          // Recompute parent line's planned/actual as sum of remaining periods.
          const linePeriods = newPeriods.filter(
            (x) =>
              x.budgetVersionId === versionId && x.budgetLineId === lineId,
          );
          const newPlanned = linePeriods.reduce(
            (s, x) => s + (x.plannedAmount || 0),
            0,
          );
          const newActual = linePeriods.reduce(
            (s, x) => s + (x.actualAmount || 0),
            0,
          );
          const newLines = (p.budgetLines ?? []).map((l) =>
            l.id === lineId && l.budgetVersionId === versionId
              ? { ...l, plannedAmount: newPlanned, actualAmount: newActual }
              : l,
          );

          return {
            ...p,
            budgetLinePeriods: newPeriods,
            budgetLines: newLines,
          };
        }),
      );
      if (get().mode === 'api') {
        fireApi(
          'Delete budget line period',
          api.deleteBudgetLinePeriod(projectId, versionId, lineId, periodStart),
        );
      }
    },

    /**
     * Distribute evenly. Replaces ANY existing periods for the line. The
     * implementation:
     *   1. Delete all existing periods for this line (locally + server)
     *   2. Insert new periods with even distribution
     *
     * Awaited so the caller can show feedback (the matrix UI uses this to
     * stop the user from editing periods mid-flight).
     */
    distributeBudgetLineEvenly: async (
      projectId,
      versionId,
      lineId,
      { totalPlanned, firstMonth, monthCount },
    ) => {
      if (monthCount <= 0) {
        toast.error('Month count must be positive');
        return;
      }
      // Compute per-month amount with cent-level remainder going to the last
      // month so the sum exactly equals totalPlanned.
      const perMonthCents = Math.floor((totalPlanned * 100) / monthCount);
      const perMonth = perMonthCents / 100;
      const lastMonth =
        totalPlanned - perMonth * (monthCount - 1); // absorbs rounding

      // Generate the period rows.
      const newPeriods: BudgetLinePeriod[] = [];
      const start = parseISODate(firstMonth);
      for (let i = 0; i < monthCount; i++) {
        const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + i, 1));
        const periodStart = formatISODate(d);
        newPeriods.push({
          budgetVersionId: versionId,
          budgetLineId: lineId,
          periodStart,
          plannedAmount: i === monthCount - 1 ? lastMonth : perMonth,
          actualAmount: 0,
        });
      }

      // 1. Clear existing periods for this line (local + server)
      const existing = (get().projects.find((p) => p.id === projectId)?.budgetLinePeriods ?? [])
        .filter(
          (x) => x.budgetVersionId === versionId && x.budgetLineId === lineId,
        );

      // Local: replace in one transition (delete + insert in same updateLocalProjects)
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          const filtered = (p.budgetLinePeriods ?? []).filter(
            (x) =>
              !(x.budgetVersionId === versionId && x.budgetLineId === lineId),
          );
          const merged = [...filtered, ...newPeriods];
          // Recompute parent line's planned/actual.
          const linePeriods = merged.filter(
            (x) =>
              x.budgetVersionId === versionId && x.budgetLineId === lineId,
          );
          const newPlanned = linePeriods.reduce(
            (s, x) => s + (x.plannedAmount || 0),
            0,
          );
          const newActual = linePeriods.reduce(
            (s, x) => s + (x.actualAmount || 0),
            0,
          );
          return {
            ...p,
            budgetLinePeriods: merged,
            budgetLines: (p.budgetLines ?? []).map((l) =>
              l.id === lineId && l.budgetVersionId === versionId
                ? { ...l, plannedAmount: newPlanned, actualAmount: newActual }
                : l,
            ),
          };
        }),
      );

      if (get().mode !== 'api') return;

      // Server: delete old periods, then upsert new ones. Done in parallel
      // because the trigger fires per-row, so order doesn't matter for the
      // final state (last write wins on the parent line).
      const deletions = existing.map((x) =>
        api.deleteBudgetLinePeriod(
          projectId,
          x.budgetVersionId,
          x.budgetLineId,
          x.periodStart,
        ),
      );
      const inserts = newPeriods.map((p) =>
        api.upsertBudgetLinePeriod(projectId, p),
      );
      const results = await Promise.all([...deletions, ...inserts]);
      const failures = results.filter((r) => r.error);
      if (failures.length > 0) {
        toast.error(
          `Distribute succeeded locally but ${failures.length} period writes failed. Refresh to verify state.`,
        );
      }
    },

    // ---------- Issue CRUD ----------

    addIssue: (projectId, issue) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, issues: [...p.issues, issue] } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add issue', api.upsertIssue(projectId, issue));
      }
    },

    updateIssue: (projectId, issueId, updates) => {
      let updatedIssue: Issue | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            issues: p.issues.map((i) => {
              if (i.id !== issueId) return i;
              const merged = { ...i, ...updates };
              updatedIssue = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updatedIssue) {
        fireApi('Update issue', api.upsertIssue(projectId, updatedIssue));
      }
    },

    deleteIssue: (projectId, issueId) => {
      const snapshot = get().projects.find((p) => p.id === projectId)?.issues.find((i) => i.id === issueId) ?? null;
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, issues: p.issues.filter((i) => i.id !== issueId) }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Delete issue', api.deleteIssue(issueId));
      }
      if (snapshot) toast.undo('Issue deleted', () => get().addIssue(projectId, snapshot));
    },

    /**
     * Phase 20.4: convert a risk to an issue.
     *
     * Performs soft conversion: the risk row stays (status='occurred'), a
     * new issue is created with audit metadata. Both records persist via
     * fireApi. The original risk's history is preserved.
     */
    convertRiskToIssue: (projectId, riskId, note) => {
      const { projects, mode } = get();
      const project = projects.find((p) => p.id === projectId);
      if (!project) {
        toast.error('Project not found');
        return null;
      }
      const risk = (project.risks ?? []).find((r) => r.id === riskId);
      if (!risk) {
        toast.error('Risk not found');
        return null;
      }
      if (risk.status === 'occurred') {
        toast.error('This risk has already been converted');
        return null;
      }

      // Build the conversion (pure)
      const userId = useAuthStore.getState().user?.id ?? '';
      const { updatedRisk, newIssue } = buildConversion(risk, note, userId);

      // Snapshot the original risk so we can revert local state if the API
      // sequence fails halfway through. Without this rollback, a failed
      // conversion leaves the local UI showing the risk as materialized
      // while the DB shows it as still open — retrying then creates a
      // duplicate issue (the bug reported in the field: two
      // "Materialized: M&A" issues stacking up on the same risk).
      const originalRisk = risk;

      // Local state update — both rows in a single set() pass for atomicity
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            risks: p.risks.map((r) => (r.id === riskId ? updatedRisk : r)),
            issues: [...p.issues, newIssue],
          };
        }),
      );

      /**
       * Background API persistence.
       *
       * Two DB writes are required:
       *   1) INSERT the new issue row into `issues`
       *   2) UPDATE the risk row, setting `materialized_into_issue_id`
       *      to the new issue's id
       *
       * Step 2 has a foreign key to `issues.id`, so step 1 MUST complete
       * before step 2 is sent. The previous implementation used two
       * `fireApi(...)` calls back-to-back — but each `api.upsertX(...)`
       * fires immediately on invocation, so both HTTP requests raced.
       * When the risk UPDATE reached the server before the issue INSERT
       * committed, the FK check failed and the risk stayed unmaterialized
       * in the DB, even though the local UI thought it was done.
       *
       * The fix: `await` step 1, only fire step 2 on success, and revert
       * the optimistic local state on any failure so a clean retry is
       * possible without duplicate issues.
       */
      if (mode === 'api') {
        void (async () => {
          const revertLocal = () => {
            updateLocalProjects((ps) =>
              ps.map((p) => {
                if (p.id !== projectId) return p;
                return {
                  ...p,
                  risks: p.risks.map((r) => (r.id === riskId ? originalRisk : r)),
                  issues: p.issues.filter((i) => i.id !== newIssue.id),
                };
              }),
            );
          };

          // Step 1: insert the issue
          const issueRes = await api.upsertIssue(projectId, newIssue);
          if (issueRes.error) {
            toast.error(
              `Convert risk to issue: ${issueRes.error}. Reverting.`,
              6000,
            );
            revertLocal();
            return;
          }

          // Step 2: update the risk to point at the new issue
          const riskRes = await api.upsertRisk(projectId, updatedRisk);
          if (riskRes.error) {
            toast.error(
              `Update risk after conversion: ${riskRes.error}. Reverting.`,
              6000,
            );
            // Rollback: the issue row is now orphaned in the DB — delete it
            // so the risk can be re-converted cleanly without a duplicate.
            // We fire-and-forget this because we're already in an error path.
            void api.deleteIssue(newIssue.id);
            revertLocal();
          }
        })();
      }

      return newIssue.id;
    },

    // ---------- Change Request CRUD ----------

    addChange: (projectId, change) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, changes: [...p.changes, change] } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add change request', api.upsertChangeRequest(projectId, change));
      }
    },

    updateChange: (projectId, changeId, updates) => {
      let updatedCr: ChangeRequest | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            changes: p.changes.map((c) => {
              if (c.id !== changeId) return c;
              const merged = { ...c, ...updates };
              updatedCr = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updatedCr) {
        fireApi(
          'Update change request',
          api.upsertChangeRequest(projectId, updatedCr),
        );
      }
    },

    deleteChange: (projectId, changeId) => {
      const snapshot = get().projects.find((p) => p.id === projectId)?.changes.find((c) => c.id === changeId) ?? null;
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, changes: p.changes.filter((c) => c.id !== changeId) }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Delete change request', api.deleteChangeRequest(changeId));
      }
      if (snapshot) toast.undo('Change request deleted', () => get().addChange(projectId, snapshot));
    },

    // ---------- Decision ops (Migration 0055 — no approval workflow) ----------

    addDecision: (projectId, decision) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, decisions: [...(p.decisions ?? []), decision] } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add decision', api.upsertDecision(projectId, decision));
      }
    },

    updateDecision: (projectId, decisionId, updates) => {
      let updated: Decision | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            decisions: (p.decisions ?? []).map((d) => {
              if (d.id !== decisionId) return d;
              const merged = { ...d, ...updates };
              updated = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updated) {
        fireApi('Update decision', api.upsertDecision(projectId, updated));
      }
    },

    deleteDecision: (projectId, decisionId) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, decisions: (p.decisions ?? []).filter((d) => d.id !== decisionId) }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Delete decision', api.deleteDecision(decisionId));
      }
    },

    // ---------- Action item ops (Migration 0056 — lightweight follow-ups) ----------

    addActionItem: (projectId, item) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId ? { ...p, actionItems: [...(p.actionItems ?? []), item] } : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Add action item', api.upsertActionItem(projectId, item));
      }
    },

    updateActionItem: (projectId, itemId, updates) => {
      let updated: ActionItem | null = null;
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            actionItems: (p.actionItems ?? []).map((a) => {
              if (a.id !== itemId) return a;
              const merged = { ...a, ...updates };
              updated = merged;
              return merged;
            }),
          };
        }),
      );
      if (get().mode === 'api' && updated) {
        fireApi('Update action item', api.upsertActionItem(projectId, updated));
      }
    },

    deleteActionItem: (projectId, itemId) => {
      updateLocalProjects((ps) =>
        ps.map((p) =>
          p.id === projectId
            ? { ...p, actionItems: (p.actionItems ?? []).filter((a) => a.id !== itemId) }
            : p,
        ),
      );
      if (get().mode === 'api') {
        fireApi('Delete action item', api.deleteActionItem(itemId));
      }
    },

    // ---------- Baseline ops ----------

    createBaseline: (projectId, baseline) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          // Mark prior active baselines as superseded if this new one is active
          const updatedExisting =
            baseline.status === 'active'
              ? p.baselines.map((b) =>
                  b.status === 'active' ? { ...b, status: 'superseded' as const } : b,
                )
              : p.baselines;
          const newBaselines = [...updatedExisting, baseline];
          return {
            ...p,
            baselines: newBaselines,
            activeBaselineId: baseline.status === 'active' ? baseline.id : p.activeBaselineId,
          };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (!proj) return;
        // Upsert all changed baselines and the project's active_baseline_id
        Promise.all([
          ...proj.baselines.map((b) => api.upsertBaseline(projectId, b)),
          api.updateProject(projectId, { activeBaselineId: proj.activeBaselineId }),
        ]).then((results) => {
          const firstErr = results.find((r) => r.error);
          if (firstErr) {
            toast.error(
              `Create baseline: ${firstErr.error}. Refresh to re-sync.`,
              6000,
            );
          }
        });
      }
    },

    setActiveBaseline: (projectId, baselineId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            activeBaselineId: baselineId,
            baselines: p.baselines.map((b) => ({
              ...b,
              status: b.id === baselineId ? ('active' as const) : ('superseded' as const),
            })),
          };
        }),
      );
      if (get().mode === 'api') {
        const proj = get().projects.find((p) => p.id === projectId);
        if (!proj) return;
        Promise.all([
          ...proj.baselines.map((b) => api.upsertBaseline(projectId, b)),
          api.updateProject(projectId, { activeBaselineId: baselineId }),
        ]).then((results) => {
          const firstErr = results.find((r) => r.error);
          if (firstErr) {
            toast.error(
              `Set active baseline: ${firstErr.error}. Refresh to re-sync.`,
              6000,
            );
          }
        });
      }
    },

    deleteBaseline: (projectId, baselineId) => {
      updateLocalProjects((ps) =>
        ps.map((p) => {
          if (p.id !== projectId) return p;
          return {
            ...p,
            baselines: p.baselines.filter((b) => b.id !== baselineId),
            activeBaselineId:
              p.activeBaselineId === baselineId ? null : p.activeBaselineId,
          };
        }),
      );
      if (get().mode === 'api') {
        api.deleteBaseline(baselineId).then((r) => {
          if (r.error) {
            toast.error(`Delete baseline: ${r.error}. Refresh to re-sync.`, 6000);
            return;
          }
          // Update project's active_baseline_id if needed
          const proj = get().projects.find((p) => p.id === projectId);
          if (proj && proj.activeBaselineId !== baselineId) return;
          fireApi(
            'Update active baseline',
            api.updateProject(projectId, { activeBaselineId: null }),
          );
        });
      }
    },
  };
});

// =============================================================================
// Selector hooks (preserved from Phase 10)
// =============================================================================

/** Get a single project by ID (or undefined). */
export const useProject = (id: string | undefined) =>
  useProjectStore((s) => (id ? s.projects.find((p) => p.id === id) : undefined));

/**
 * Status hook for components that want to render loading/error UI.
 *
 * Uses `useShallow` from Zustand 5 to provide reference-stable equality
 * across renders. Without this, the selector returns a brand-new object
 * literal every call, causing React to think the value changed and
 * triggering an infinite re-render loop ("Maximum update depth exceeded").
 */
export const useStoreStatus = () =>
  useProjectStore(
    useShallow((s) => ({
      mode: s.mode,
      status: s.status,
      errorMessage: s.errorMessage,
      isInitialized: s.isInitialized,
    })),
  );
